import "./queue_app.css";
import {
  buildAdminControlSummary,
  buildAdminUpdatePayload,
  buildOwnerCommandCenter,
  buildOwnerFirstRunSetupFlow,
  buildOwnerLaunchReadiness,
  buildOwnerTeamSetup,
} from "./admin_control_center.js";
import {
  buildConversationWorkspaceSummary,
  conversationDispositionOptions,
  resolveConversationDetailActions,
} from "./conversation_detail.js";
import {
  emptyStateClass,
  metricTileMarkup,
  sectionIntroMarkup,
  toneChipClass,
} from "./design_system.js";
import {
  buildDetailPlaceholder,
  buildInboxEmptyState,
  buildReportingEmptyState,
  buildSurfaceHelp,
} from "./embedded_help.js";
import { buildContactRecordWorkspace } from "./contact_record.js";
import { buildCrmActivityStream } from "./crm_activity_stream.js";
import { buildDispositionBoard } from "./disposition_board.js";
import { buildFollowUpTaskWorkspace } from "./follow_up_tasks.js";
import { buildInternalNotesWorkspace } from "./internal_notes.js";
import { buildRecordPipelineWorkspace, mergeLeadIntoPipelineBoard } from "./record_pipeline.js";
import { buildCustomerReleasePack } from "./customer_release_pack.js";
import { buildManagerInboxWorkspace, buildOperatorInboxWorkspace } from "./inbox_workspace.js";
import {
  buildManagerCommandWorkspace,
  buildManagerDeckSnapshot,
  summarizeBulkResult,
  summarizeBulkSelection,
} from "./manager_controls.js";
import { buildManagerConversationReview, buildManagerReviewCommandBrief, buildManagerReviewQueue, buildManagerVisibilityBrief } from "./manager_review.js";
import {
  buildSyncStatusSummary,
  captureConversationVersion,
  describeConversationDrift,
} from "./multi_user_awareness.js";
import {
  applyClientConversationFilters,
  buildOperationalReportWorkspace,
  buildAdminReportingQuery,
  buildInboxViewRequest,
  defaultAdminReportingFilters,
  defaultInboxFilters,
  deleteInboxViewPreset,
  loadAdminReportingFilters,
  loadInboxFilters,
  loadSavedInboxViews,
  persistAdminReportingFilters,
  persistInboxFilters,
  saveInboxViewPreset,
  summarizeOperationalReport,
} from "./search_reporting.js";
import { buildNavigationModel, decorateNavigationSection } from "./navigation_ia.js";
import { buildOperatorWorkflowGuide } from "./operator_workflow.js";
import {
  buildLeadQualificationWorkspace,
  buildOperatorCloseoutChecklist,
  buildOperatorDailyBrief,
  buildOperatorFocusPath,
  buildOperatorLeadCardView,
  buildOperatorMessagingGuard,
  buildOperatorMessagingSafety,
  buildOperatorNextLeadHandoff,
  buildOperatorQueueCommand,
  buildOperatorReplyAssist,
  buildQualificationNoteBody,
} from "./operator_workbench.js";
import {
  buildProductUiDiagnosticsSummary,
  buildGovernanceDashboardSummary,
  buildSupportConsoleSummary,
  governanceStatusBadge,
  supportStatusBadge,
} from "./support_governance.js";
import { drainTelemetryQueue, resolveClientBuild } from "./telemetry.js";
import { applyOnboardingRecommendation, buildOnboardingJourney } from "./onboarding_flow.js";
import {
  buildSurfaceHash,
  clearStoredSession,
  isSessionExpired,
  loadStoredSession,
  loadStoredSurface,
  persistSession,
  persistSurface,
  resolvePreferredSurface,
} from "./session_state.js";
import {
  beginPending,
  createRuntimeState,
  endPending,
  hasPending,
  isCurrentRenderVersion,
  nextRenderVersion,
} from "./runtime_state.js";

(function () {
  "use strict";

  const storedSession = loadStoredSession(window.localStorage);
  const state = {
    token: storedSession ? storedSession.accessToken : "",
    context: storedSession ? storedSession.context : null,
    expiresAt: storedSession ? storedSession.expiresAt : null,
    bootstrap: null,
    activeSurface: null,
    operatorData: null,
    managerData: null,
    managerBulkResult: null,
    managerSelection: [],
    inboxWorkspace: {
      manager: null,
      operator: null,
    },
    inboxFilters: {
      manager: loadInboxFilters(window.localStorage, "manager"),
      operator: loadInboxFilters(window.localStorage, "operator"),
    },
    inboxSavedViews: {
      manager: loadSavedInboxViews(window.localStorage, "manager"),
      operator: loadSavedInboxViews(window.localStorage, "operator"),
    },
    adminReporting: {
      filters: loadAdminReportingFilters(window.localStorage),
      report: null,
    },
    adminFormDirty: false,
    adminInviteResult: null,
    adminFocus: {
      targetId: null,
      clearTimer: null,
    },
    platformData: null,
    supportData: null,
    governanceData: null,
    productUiDiagnostics: null,
    supportWorkflow: {
      selectedDestinationId: null,
      deliveriesByDestination: {},
      actionResult: null,
    },
    telemetry: {
      queue: [],
      flushTimer: null,
      clientBuild: resolveClientBuild(import.meta.url),
    },
    sync: {
      timer: null,
      intervalMs: window.location.hostname === "127.0.0.1" ? 1500 : 45000,
      lastBootstrapAt: null,
      noticesByTarget: {},
      openDetails: {},
    },
    runtime: createRuntimeState(),
    conversationWorkspace: {
      snapshots: {},
      mutations: {},
      drafts: {},
      recordData: {},
    },
    selectedConversationId: null,
    selectedConversationTargetId: null,
    authChecked: false,
  };

  const loginForm = document.getElementById("login-form");
  const heroStatus = document.getElementById("hero-status");
  const sessionSummary = document.getElementById("session-summary");
  const sessionLifecycle = document.getElementById("session-lifecycle");
  const workspaceMain = document.getElementById("workspace-main");
  const skipLink = document.getElementById("skip-link");
  const navPills = document.getElementById("nav-pills");
  const navContext = document.getElementById("nav-context");
  const workspaceStartPanel = document.getElementById("workspace-start-panel");
  const routeSummary = document.getElementById("route-summary");
  const navDescription = document.getElementById("nav-description");
  const syncStatus = document.getElementById("sync-status");
  const flashStack = document.getElementById("flash-messages");
  const reducedMotionQuery = typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;
  const surfaceStatusIds = {
    manager: "manager-status",
    operator: "operator-status",
    admin: "admin-status",
    platform: "platform-status",
    support: "support-status",
    governance: "governance-status",
  };

  function parseJson(value) {
    if (!value) {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function saveSession() {
    if (!state.token || !state.context) {
      clearStoredSession(window.localStorage);
      return;
    }
    persistSession(window.localStorage, {
      accessToken: state.token,
      context: state.context,
      expiresAt: state.expiresAt,
    });
  }

  function clearSession() {
    state.token = "";
    state.context = null;
    state.expiresAt = null;
    state.bootstrap = null;
    state.activeSurface = null;
    state.operatorData = null;
    state.managerData = null;
    state.managerBulkResult = null;
    state.managerSelection = [];
    state.inboxWorkspace = {
      manager: null,
      operator: null,
    };
    state.inboxFilters = {
      manager: loadInboxFilters(window.localStorage, "manager"),
      operator: loadInboxFilters(window.localStorage, "operator"),
    };
    state.inboxSavedViews = {
      manager: loadSavedInboxViews(window.localStorage, "manager"),
      operator: loadSavedInboxViews(window.localStorage, "operator"),
    };
    state.adminReporting = {
      filters: loadAdminReportingFilters(window.localStorage),
      report: null,
    };
    state.platformData = null;
    state.adminFormDirty = false;
    state.adminInviteResult = null;
    if (state.adminFocus.clearTimer) {
      window.clearTimeout(state.adminFocus.clearTimer);
    }
    state.adminFocus = {
      targetId: null,
      clearTimer: null,
    };
    state.supportData = null;
    state.governanceData = null;
    state.productUiDiagnostics = null;
    state.supportWorkflow = {
      selectedDestinationId: null,
      deliveriesByDestination: {},
      actionResult: null,
    };
    if (state.telemetry.flushTimer) {
      window.clearTimeout(state.telemetry.flushTimer);
    }
    state.telemetry = {
      queue: [],
      flushTimer: null,
      clientBuild: resolveClientBuild(import.meta.url),
    };
    if (state.sync.timer) {
      window.clearTimeout(state.sync.timer);
    }
    state.sync = {
      timer: null,
      intervalMs: window.location.hostname === "127.0.0.1" ? 1500 : 45000,
      lastBootstrapAt: null,
      noticesByTarget: {},
      openDetails: {},
    };
    state.runtime = createRuntimeState();
    state.conversationWorkspace = {
      snapshots: {},
      mutations: {},
      drafts: {},
      recordData: {},
    };
    state.selectedConversationId = null;
    state.selectedConversationTargetId = null;
    clearStoredSession(window.localStorage);
    persistSurface(window.localStorage, null);
  }

  function e(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function flash(message, kind) {
    const node = document.createElement("div");
    node.className = `flash ${kind}`;
    node.setAttribute("role", kind === "error" ? "alert" : "status");
    node.textContent = message;
    flashStack.prepend(node);
    window.setTimeout(() => node.remove(), 6000);
  }

  function visibleSyncNoticeCount() {
    return Object.values(state.sync.noticesByTarget || {}).filter(Boolean).length;
  }

  function renderSyncStatus() {
    syncStatus.textContent = buildSyncStatusSummary({
      active: Boolean(state.token && state.context),
      intervalMs: state.sync.intervalMs,
      lastSyncedAt: state.sync.lastBootstrapAt,
      noticeCount: visibleSyncNoticeCount(),
      visible: !document.hidden,
    });
  }

  function conversationNoticeMarkup(targetId) {
    const notice = state.sync.noticesByTarget[targetId] || null;
    if (!notice) {
      return "";
    }
    const surfaceKey = targetId.startsWith("manager") ? "manager" : "operator";
    return `<div id="${surfaceKey}-sync-banner" class="${emptyStateClass(notice.tone, ["conversation-sync-banner"])}">${e(
      notice.message
    )} <button class="action ghost" type="button" data-refresh-sync-notice="true">Review latest state</button></div>`;
  }

  function renderConversationNotice(targetId) {
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    const existing = target.querySelector(".conversation-sync-banner");
    if (existing) {
      existing.remove();
    }
    const markup = conversationNoticeMarkup(targetId);
    if (!markup) {
      return;
    }
    const header = target.querySelector("header");
    if (!header) {
      return;
    }
    header.insertAdjacentHTML("afterend", markup);
  }

  function setLifecycleState(kind, message) {
    sessionLifecycle.className = emptyStateClass(kind);
    sessionLifecycle.textContent = message;
  }

  function setSurfaceStatus(surfaceKey, tone, message, busy) {
    const statusId = surfaceStatusIds[surfaceKey];
    const statusNode = statusId ? document.getElementById(statusId) : null;
    const surfaceNode = document.getElementById(`${surfaceKey}-surface`);
    if (statusNode) {
      statusNode.className = `surface-status${tone ? ` ${tone}` : ""}`;
      statusNode.textContent = message;
    }
    if (surfaceNode) {
      surfaceNode.setAttribute("aria-busy", busy ? "true" : "false");
      surfaceNode.classList.toggle("surface-loading", Boolean(busy));
    }
  }

  async function runPendingAction(actionKey, button, busyLabel, callback) {
    if (!beginPending(state.runtime, actionKey)) {
      return false;
    }
    const originalText = button ? button.textContent : "";
    const originalDisabled = button ? button.disabled : false;
    if (button) {
      button.disabled = true;
      button.dataset.busy = "true";
      if (busyLabel) {
        button.textContent = busyLabel;
      }
    }
    try {
      await callback();
      return true;
    } finally {
      endPending(state.runtime, actionKey);
      if (button) {
        button.disabled = originalDisabled;
        button.dataset.busy = "false";
        button.textContent = originalText;
      }
    }
  }

  function authHeaders(extra) {
    return Object.assign({ Authorization: `Bearer ${state.token}` }, extra || {});
  }

  async function apiFetch(path, options) {
    const response = await fetch(path, Object.assign({ cache: "no-store" }, options || {}));
    if (response.status === 401) {
      clearSession();
      state.authChecked = true;
      renderSession();
      renderNav();
      window.location.hash = "";
      throw new Error("Session expired or invalid. Sign in again.");
    }
    if (!response.ok) {
      throw new Error(`${path} returned ${response.status}: ${await response.text()}`);
    }
    if (response.status === 204) {
      return null;
    }
    return response.json();
  }

  function formatDate(value) {
    return value ? new Date(value).toLocaleString() : "n/a";
  }

  function badgeClass(value) {
    return toneChipClass(value);
  }

  function renderSectionIntro(options) {
    return sectionIntroMarkup(options, e);
  }

  function renderEmptyState(message, tone, extraClasses = []) {
    return `<div class="${emptyStateClass(tone, extraClasses)}">${e(message)}</div>`;
  }

  function renderRichEmptyState(title, detail, tone, extraClasses = []) {
    return `<div class="${emptyStateClass(tone, extraClasses)}"><strong>${e(title)}</strong><div class="meta">${e(detail)}</div></div>`;
  }

  function renderGuidedEmptyState(title, detail, tone, actions = [], extraClasses = []) {
    return `
      <div class="${emptyStateClass(tone, extraClasses)}">
        <strong>${e(title)}</strong>
        <div class="meta">${e(detail)}</div>
        ${
          actions.length
            ? `<div class="conversation-actions">
                ${actions
                  .map((item) => {
                    if (item.kind === "button") {
                      const datasets = Object.entries(item.dataset || {})
                        .map(([key, value]) => `data-${key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}="${e(String(value))}"`)
                        .join(" ");
                      return `<button class="action ghost" type="button" ${datasets}>${e(item.label)}</button>`;
                    }
                    return `<a class="action ghost" href="${e(item.href)}" target="_blank" rel="noreferrer">${e(item.label)}</a>`;
                  })
                  .join("")}
              </div>`
            : ""
        }
      </div>
    `;
  }

  function idempotencyKey(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function queueTelemetryEvent(event) {
    state.telemetry.queue.push(event);
    if (!state.token) {
      return;
    }
    if (state.telemetry.flushTimer) {
      window.clearTimeout(state.telemetry.flushTimer);
    }
    state.telemetry.flushTimer = window.setTimeout(() => {
      flushTelemetryQueue().catch(() => {});
    }, 1200);
  }

  async function flushTelemetryQueue() {
    if (!state.token) {
      return;
    }
    if (state.telemetry.flushTimer) {
      window.clearTimeout(state.telemetry.flushTimer);
      state.telemetry.flushTimer = null;
    }
    const events = drainTelemetryQueue(state.telemetry.queue, state.telemetry.clientBuild, new Date());
    if (!events.length) {
      return;
    }
    try {
      await apiFetch("/api/product-ui/telemetry", {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
        body: JSON.stringify({ events }),
      });
    } catch (error) {
      state.telemetry.queue.unshift(
        ...events.map((item) => ({
          eventName: item.event_name,
          surface: item.surface,
          route: item.route,
          outcome: item.outcome,
          durationMs: item.duration_ms,
          detail: item.detail,
          clientBuild: item.client_build,
          clientOccurredAt: item.client_occurred_at,
          metadata: item.metadata,
        }))
      );
    }
  }

  async function loadProductUiDiagnostics() {
    await flushTelemetryQueue();
    state.productUiDiagnostics = await apiFetch("/api/product-ui/diagnostics?window_hours=24", {
      headers: authHeaders(),
    });
    return state.productUiDiagnostics;
  }

  function rememberConversationSnapshot(conversationId, snapshot) {
    if (!conversationId || !snapshot) {
      return;
    }
    state.conversationWorkspace.snapshots[conversationId] = Object.assign(
      {},
      state.conversationWorkspace.snapshots[conversationId] || {},
      snapshot
    );
  }

  function mutateRememberedConversation(conversationId, updates) {
    if (!conversationId || !updates) {
      return;
    }
    rememberConversationSnapshot(conversationId, updates);
  }

  function mergeOptimisticMessages(messages, optimisticMessages) {
    const confirmed = Array.isArray(messages) ? messages : [];
    const pending = Array.isArray(optimisticMessages) ? optimisticMessages : [];
    if (!pending.length) {
      return {
        items: confirmed,
        unresolved: [],
      };
    }
    const items = [...confirmed];
    const unresolved = [];
    for (const candidate of pending) {
      const exists = items.some(
        (item) =>
          (candidate.id && item.id === candidate.id)
          || (
            item.direction === candidate.direction
            && item.body === candidate.body
            && item.created_at === candidate.created_at
          )
      );
      if (!exists) {
        items.push(candidate);
        unresolved.push(candidate);
      }
    }
    items.sort((left, right) => new Date(left.created_at || 0).getTime() - new Date(right.created_at || 0).getTime());
    return {
      items,
      unresolved,
    };
  }

  function rememberedConversationSnapshot(conversationId) {
    return state.conversationWorkspace.snapshots[conversationId] || null;
  }

  function rememberConversationMutation(conversationId, mutation) {
    if (!conversationId || !mutation) {
      return;
    }
    state.conversationWorkspace.mutations[conversationId] = mutation;
    mutateRememberedConversation(conversationId, { last_session_mutation: mutation });
  }

  function rememberedConversationMutation(conversationId) {
    return state.conversationWorkspace.mutations[conversationId]
      || ((rememberedConversationSnapshot(conversationId) || {}).last_session_mutation || null);
  }

  function rememberConversationDraft(conversationId, updates) {
    if (!conversationId || !updates) {
      return;
    }
    state.conversationWorkspace.drafts[conversationId] = Object.assign(
      {},
      state.conversationWorkspace.drafts[conversationId] || {},
      updates
    );
  }

  function clearConversationDraft(conversationId, keys = null) {
    if (!conversationId) {
      return;
    }
    if (!Array.isArray(keys) || !keys.length) {
      delete state.conversationWorkspace.drafts[conversationId];
      return;
    }
    const nextDraft = Object.assign({}, state.conversationWorkspace.drafts[conversationId] || {});
    for (const key of keys) {
      delete nextDraft[key];
    }
    if (Object.keys(nextDraft).length) {
      state.conversationWorkspace.drafts[conversationId] = nextDraft;
      return;
    }
    delete state.conversationWorkspace.drafts[conversationId];
  }

  function rememberedConversationDraft(conversationId) {
    return state.conversationWorkspace.drafts[conversationId] || null;
  }

  function rememberConversationRecordData(conversationId, updates) {
    if (!conversationId || !updates) {
      return;
    }
    state.conversationWorkspace.recordData[conversationId] = Object.assign(
      {},
      state.conversationWorkspace.recordData[conversationId] || {},
      updates
    );
  }

  function rememberedConversationRecordData(conversationId) {
    return state.conversationWorkspace.recordData[conversationId] || null;
  }

  function mergeWorkspaceCollection(fetchedItems, cachedItems, timestampKey) {
    const map = new Map();
    for (const item of Array.isArray(fetchedItems) ? fetchedItems : []) {
      if (item && item.id) {
        map.set(item.id, item);
      }
    }
    for (const item of Array.isArray(cachedItems) ? cachedItems : []) {
      if (item && item.id) {
        map.set(item.id, Object.assign({}, map.get(item.id) || {}, item));
      }
    }
    const values = Array.from(map.values());
    values.sort((left, right) => {
      const leftTime = new Date((left || {})[timestampKey] || 0).getTime();
      const rightTime = new Date((right || {})[timestampKey] || 0).getTime();
      return rightTime - leftTime;
    });
    return values;
  }

  function canViewConversationActivity() {
    return ["tenant_owner", "manager"].includes(((state.context || {}).role || "").toLowerCase());
  }

  async function loadConversationActivity(conversationId) {
    if (!canViewConversationActivity()) {
      return {
        recentActions: [],
        activityUnavailableReason: "Queue action history is only visible to managers and tenant owners.",
      };
    }
    const recentActions = await apiFetch(
      `/api/conversations/queue/actions?conversation_id=${encodeURIComponent(conversationId)}&limit=6`,
      { headers: authHeaders() }
    );
    return {
      recentActions: Array.isArray(recentActions) ? recentActions : [],
      activityUnavailableReason: null,
    };
  }

  async function loadContactRecordWorkspace(snapshot) {
    if (!snapshot || !snapshot.contact_id) {
      return {
        relatedConversations: snapshot ? [snapshot] : [],
        timelineEvents: [],
      };
    }
    try {
      const [relatedConversations, timelineEvents] = await Promise.all([
        apiFetch(`/api/conversations?contact_id=${encodeURIComponent(snapshot.contact_id)}&limit=6`, {
          headers: authHeaders(),
        }),
        apiFetch(`/api/timeline?contact_id=${encodeURIComponent(snapshot.contact_id)}`, {
          headers: authHeaders(),
        }),
      ]);
      return {
        relatedConversations: Array.isArray(relatedConversations) ? relatedConversations : [],
        timelineEvents: Array.isArray(timelineEvents) ? timelineEvents : [],
      };
    } catch (error) {
      return {
        relatedConversations: snapshot ? [snapshot] : [],
        timelineEvents: [
          {
            event_type: "contact_record_unavailable",
            summary: "Contact history could not be loaded for this record.",
            occurred_at: "",
          },
        ],
      };
    }
  }

  function workflowTaskMembers() {
    const fromAdmin = ((((state.bootstrap || {}).admin_control_center || {}).workspace_members) || [])
      .map((item) => ({
        user_id: item.user_id,
        full_name: item.full_name,
        role: item.role,
        email: item.email,
      }));
    if (fromAdmin.length) {
      return fromAdmin;
    }
    return [
      {
        user_id: (state.context || {}).user_id,
        full_name: (state.context || {}).full_name,
        role: (state.context || {}).role,
        email: (state.context || {}).email,
      },
    ].filter((item) => item.user_id);
  }

  async function loadContactTasks(contactId) {
    if (!contactId) {
      return [];
    }
    const tasks = await apiFetch(`/api/tasks?contact_id=${encodeURIComponent(contactId)}&limit=12`, {
      headers: authHeaders(),
    });
    return Array.isArray(tasks) ? tasks : [];
  }

  async function resolveLeadIdForConversation(snapshot, options = {}) {
    if (snapshot && snapshot.lead_id) {
      return snapshot.lead_id;
    }
    if (!snapshot || !snapshot.contact_id) {
      return null;
    }
    const leads = await apiFetch(`/api/leads?contact_id=${encodeURIComponent(snapshot.contact_id)}&limit=1`, {
      headers: authHeaders(),
    });
    if (Array.isArray(leads) && leads.length && leads[0].id) {
      mutateRememberedConversation(snapshot.id, { lead_id: leads[0].id });
      return leads[0].id;
    }
    if (!options.createIfMissing) {
      return null;
    }
    const createdLead = await apiFetch("/api/leads", {
      method: "POST",
      headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
      body: JSON.stringify({
        contact_id: snapshot.contact_id,
        source: "product_ui_internal_note",
        assigned_user_id: snapshot.assigned_user_id || (state.context || {}).user_id || null,
        priority: "normal",
        tags_json: [],
      }),
    });
    if (createdLead && createdLead.id) {
      mutateRememberedConversation(snapshot.id, { lead_id: createdLead.id });
      return createdLead.id;
    }
    return null;
  }

  async function loadLeadNotes(snapshot) {
    const resolvedLeadId = await resolveLeadIdForConversation(snapshot, { createIfMissing: false });
    if (!resolvedLeadId) {
      return {
        notes: [],
        available: Boolean(snapshot && snapshot.contact_id),
        leadId: null,
        unavailableReason: snapshot && snapshot.contact_id
          ? "The first internal note will create a lead record for this contact."
          : "Internal notes become available once this conversation is linked to a lead record.",
      };
    }
    try {
      const notes = await apiFetch(`/api/leads/${encodeURIComponent(resolvedLeadId)}/notes?limit=8`, {
        headers: authHeaders(),
      });
      return {
        notes: Array.isArray(notes) ? notes : [],
        available: true,
        leadId: resolvedLeadId,
        unavailableReason: null,
      };
    } catch (error) {
      return {
        notes: [],
        available: false,
        leadId: resolvedLeadId,
        unavailableReason: error.message || "Internal notes could not be loaded for this record.",
      };
    }
  }

  async function loadLeadRecordWorkspace(snapshot) {
    const resolvedLeadId = await resolveLeadIdForConversation(snapshot, { createIfMissing: false });
    if (!resolvedLeadId) {
      return {
        lead: null,
        leadId: null,
        pipelineBoard: null,
        unavailableReason: snapshot && snapshot.contact_id
          ? "Lead record has not been created for this contact yet."
          : "Lead context is unavailable for this record.",
      };
    }
    try {
      const [lead, pipelineBoard] = await Promise.all([
        apiFetch(`/api/leads/${encodeURIComponent(resolvedLeadId)}`, { headers: authHeaders() }),
        apiFetch("/api/pipeline-board?limit_per_stage=12", { headers: authHeaders() }),
      ]);
      return {
        lead: lead || null,
        leadId: resolvedLeadId,
        pipelineBoard: pipelineBoard || null,
        unavailableReason: null,
      };
    } catch (error) {
      return {
        lead: null,
        leadId: resolvedLeadId,
        pipelineBoard: null,
        unavailableReason: error.message || "Record pipeline context could not be loaded.",
      };
    }
  }

  function isoFromDateTimeLocal(value) {
    const text = String(value || "").trim();
    if (!text) {
      return null;
    }
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString();
  }

  function dateTimeLocalFromIso(value) {
    const text = String(value || "").trim();
    if (!text) {
      return "";
    }
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  function renderSession() {
    document.body.classList.toggle("is-authenticated", Boolean(state.context));
    if (!state.context) {
      heroStatus.innerHTML = '<span class="status-pill">Disconnected</span>';
      sessionSummary.innerHTML = "<p class='hint'>No active session.</p>";
      setLifecycleState("",
        state.authChecked
          ? "Sign in to establish a tenant-scoped CRM session."
          : "Checking for a saved CRM session."
      );
      renderSyncStatus();
      return;
    }
    const expired = isSessionExpired(
      { accessToken: state.token, context: state.context, expiresAt: state.expiresAt },
      new Date()
    );
    heroStatus.innerHTML = `<span class="status-pill">${e(state.context.role)}</span><span class="status-note">${e(
      state.context.tenant_slug
    )}</span>`;
    sessionSummary.innerHTML = `<div class="metric-card"><div class="eyebrow">Authenticated</div><strong>${e(
      state.context.full_name
    )}</strong><p class="meta">${e(state.context.email)}</p><p class="meta">${
      state.expiresAt ? `Token expires ${formatDate(state.expiresAt)}` : "Token expiry unavailable"
    }</p></div>`;
    setLifecycleState(
      expired ? "overdue" : "ready",
      expired
        ? "This CRM session is expired. Refresh or sign in again."
        : `Signed in as ${state.context.full_name} for ${state.context.tenant_slug}.`
    );
    renderSyncStatus();
    loginForm.email.value = state.context.email || "";
    loginForm.tenant_slug.value = state.context.tenant_slug || "";
    loginForm.password.value = "";
  }

  function activeSurfaceMeta(key) {
    const item = ((state.bootstrap || {}).sections || []).find((section) => section.key === key) || null;
    return item ? decorateNavigationSection(item) : null;
  }

  function hasBootstrapSection(key) {
    return ((state.bootstrap || {}).sections || []).some((section) => section.key === key);
  }

  function renderRouteContext(meta) {
    if (!navContext) {
      return;
    }
    if (!meta) {
      navContext.innerHTML = "<p class='hint'>Sign in to load the current route context.</p>";
      return;
    }
    navContext.innerHTML = `
      <div class="nav-context-card">
        <p class="section-kicker">${e(meta.groupLabel)}</p>
        <h3>${e(meta.navTitle)}</h3>
        <p>${e(meta.routeSummary)}</p>
        <p class="hint">${e(meta.description)}</p>
      </div>
    `;
  }

  function workspaceStartConfig(role) {
    if (["platform_owner", "platform_ops"].includes(role)) {
      return {
        title: "Platform review path",
        summary: "Start with platform intake, then confirm support and health before provisioning work.",
        actions: [
          { label: "Open Platform", surface: "platform", tone: "primary" },
          { label: "Review Health", surface: "governance", tone: "ghost" },
        ],
      };
    }
    if (role === "tenant_owner") {
      return {
        title: "Owner launch path",
        summary: "Start with team setup and readiness. Use Inbox only to validate the queue from the customer view.",
        actions: [
          { label: "Open Setup", surface: "admin", tone: "primary" },
          { label: "Team Setup", targetId: "admin-onboarding-roster", tone: "ghost" },
          { label: "Open Queue", surface: "operator", tone: "ghost" },
        ],
      };
    }
    if (role === "manager") {
      return {
        title: "Manager review path",
        summary: "Start with the queue review brief, then rebalance work only when the queue needs it.",
        actions: [
          { label: "Open Manager", surface: "manager", tone: "primary" },
          { label: "Open Queue", surface: "operator", tone: "ghost" },
        ],
      };
    }
    return {
      title: "Operator lead path",
      summary: "Start in Inbox, open one assigned lead, reply, disposition, and create the follow-up before moving on.",
      actions: [
        { label: "Open Inbox", surface: "operator", tone: "primary" },
        { label: "Review Manager", surface: "manager", tone: "ghost" },
      ],
    };
  }

  function renderWorkspaceStartPanel(meta) {
    if (!workspaceStartPanel) {
      return;
    }
    if (!state.context || !state.bootstrap) {
      workspaceStartPanel.className = "workspace-start-panel detail-empty";
      workspaceStartPanel.innerHTML = `
        <p class="section-kicker">Start here</p>
        <h3>Sign in</h3>
        <p class="hint">Your first screen will adjust to your role after login.</p>
      `;
      return;
    }
    const config = workspaceStartConfig((state.context.role || "operator").toLowerCase());
    const actions = config.actions
      .filter((item) => !item.surface || hasBootstrapSection(item.surface))
      .map((item) => {
        if (item.targetId) {
          return `<button class="action ${e(item.tone)}" type="button" data-onboarding-focus-target="${e(item.targetId)}">${e(item.label)}</button>`;
        }
        return `<button class="action ${e(item.tone)}" type="button" data-open-surface="${e(item.surface)}">${e(item.label)}</button>`;
      })
      .join("");
    workspaceStartPanel.className = "workspace-start-panel ready";
    workspaceStartPanel.innerHTML = `
      <div class="workspace-start-copy">
        <p class="section-kicker">Start here</p>
        <h3>${e(config.title)}</h3>
        <p>${e(config.summary)}</p>
        ${meta ? `<p class="meta">Current surface: ${e(meta.groupLabel)} / ${e(meta.navTitle)}</p>` : ""}
      </div>
      <div class="workspace-start-actions">${actions}</div>
    `;
  }

  function showSurface(key, options) {
    const settings = Object.assign({ updateHash: true, persist: true, track: true }, options || {});
    const meta = activeSurfaceMeta(key);
    if (!meta) {
      return;
    }
    const previousSurface = state.activeSurface;
    state.activeSurface = key;
    document.querySelectorAll(".nav-pill").forEach((node) => {
      const active = node.dataset.surface === key;
      node.classList.toggle("active", active);
      if (active) {
        node.setAttribute("aria-current", "page");
      } else {
        node.removeAttribute("aria-current");
      }
    });
    document.querySelectorAll(".surface").forEach((node) => node.classList.add("hidden"));
    const surface = document.querySelector(`.surface[data-surface="${key}"]`);
    if (surface) {
      surface.classList.remove("hidden");
    }
    routeSummary.textContent = `${meta.groupLabel} / ${meta.navTitle}`;
    renderRouteContext(meta);
    renderWorkspaceStartPanel(meta);
    if (settings.persist) {
      persistSurface(window.localStorage, key);
    }
    if (settings.updateHash && window.location.hash !== buildSurfaceHash(key)) {
      window.location.hash = buildSurfaceHash(key);
    }
    if (settings.track && previousSurface !== key) {
      queueTelemetryEvent({
        eventName: "surface_opened",
        surface: key,
        route: buildSurfaceHash(key),
        metadata: { role: (state.context || {}).role || "anonymous" },
      });
    }
  }

  function clearAdminFocusMarker() {
    document.querySelectorAll(".focus-target").forEach((node) => node.classList.remove("focus-target"));
  }

  function scheduleAdminFocusClear(targetId, delayMs) {
    if (state.adminFocus.clearTimer) {
      window.clearTimeout(state.adminFocus.clearTimer);
    }
    state.adminFocus.clearTimer = window.setTimeout(() => {
      if (state.adminFocus.targetId === targetId) {
        const target = document.getElementById(targetId);
        if (target) {
          target.classList.remove("focus-target");
        }
        state.adminFocus.targetId = null;
      }
      state.adminFocus.clearTimer = null;
    }, delayMs);
  }

  function applyAdminFocusMarker(targetId, options) {
    const settings = Object.assign({ scroll: true, reschedule: true, durationMs: 1800 }, options || {});
    if (!targetId) {
      return false;
    }
    const target = document.getElementById(targetId);
    if (!target) {
      return false;
    }
    clearAdminFocusMarker();
    target.classList.add("focus-target");
    state.adminFocus.targetId = targetId;
    if (settings.reschedule) {
      scheduleAdminFocusClear(targetId, settings.durationMs);
    }
    if (settings.scroll && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    }
    return true;
  }

  async function focusAdminTarget(targetId) {
    if (!targetId) {
      return false;
    }
    state.adminFocus.targetId = targetId;
    showSurface("admin", { updateHash: true, persist: true });
    await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
    let target = document.getElementById(targetId);
    let attempts = 0;
    while (!target && attempts < 20) {
      attempts += 1;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      target = document.getElementById(targetId);
    }
    if (!target) {
      state.adminFocus.targetId = null;
      return false;
    }
    return applyAdminFocusMarker(targetId, { scroll: true, reschedule: true, durationMs: 1800 });
  }

  function featureEnabled(snapshot, key) {
    const items = (((snapshot || {}).entitlements || {}).features || []);
    const match = items.find((item) => item.key === key);
    return Boolean(match && match.enabled);
  }

  function renderNav() {
    navPills.innerHTML = "";
    if (!state.bootstrap) {
      navDescription.textContent = "Log in to load work, control, and health surfaces.";
      routeSummary.textContent = "No active route.";
      renderRouteContext(null);
      renderWorkspaceStartPanel(null);
      renderSyncStatus();
      document.querySelectorAll(".surface").forEach((node) => node.classList.add("hidden"));
      return;
    }
    const navigation = buildNavigationModel(state.bootstrap);
    navDescription.textContent = navigation.overview;
    navigation.groups.forEach((group) => {
      const wrapper = document.createElement("section");
      wrapper.className = "nav-group";
      wrapper.innerHTML = `
        <div class="nav-group-head">
          <p class="section-kicker">${e(group.label)}</p>
          <p class="hint">${e(group.summary)}</p>
        </div>
      `;
      const row = document.createElement("div");
      row.className = "nav-pills nav-pill-row";
      group.items.forEach((section) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "nav-pill";
        button.dataset.surface = section.key;
        button.dataset.surfaceGroup = section.groupKey;
        button.setAttribute("aria-controls", `${section.key}-surface`);
        button.textContent = section.navTitle;
        button.title = section.routeSummary;
        button.addEventListener("click", () => showSurface(section.key));
        row.appendChild(button);
      });
      wrapper.appendChild(row);
      navPills.appendChild(wrapper);
    });
    const preferredSurface = resolvePreferredSurface({
      hash: window.location.hash,
      storedSurface: loadStoredSurface(window.localStorage),
      allowedSections: state.bootstrap.sections,
    });
    showSurface(preferredSurface || state.bootstrap.sections[0].key, {
      updateHash: true,
      persist: true,
    });
    renderSyncStatus();
  }

  async function login(event) {
    event.preventDefault();
    const submitButton = loginForm.querySelector('button[type="submit"]');
    await runPendingAction("login", submitButton, "Signing in...", async function () {
      try {
        const payload = await apiFetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: loginForm.email.value,
            password: loginForm.password.value,
            tenant_slug: loginForm.tenant_slug.value,
          }),
        });
        state.token = payload.access_token;
        state.context = payload.context;
        state.expiresAt = payload.expires_at || null;
        saveSession();
        state.authChecked = true;
        queueTelemetryEvent({
          eventName: "login_success",
          surface: "auth",
          route: "#login",
          metadata: { role: payload.context.role },
        });
        renderSession();
        await loadBootstrap();
        flash("Product UI session established.", "success");
      } catch (error) {
        queueTelemetryEvent({
          eventName: "login_failed",
          surface: "auth",
          route: "#login",
          outcome: "error",
          detail: error.message,
        });
        flash(error.message, "error");
      }
    });
  }

  function rememberOpenConversationDetail(targetId, conversationId, snapshot) {
    state.sync.openDetails[targetId] = {
      conversationId,
      contactId: snapshot ? snapshot.contact_id || null : null,
      baseline: captureConversationVersion(snapshot),
    };
  }

  function refreshOpenConversationBaseline(targetId, snapshot) {
    if (!targetId || !state.sync.openDetails[targetId]) {
      return;
    }
    state.sync.openDetails[targetId] = Object.assign({}, state.sync.openDetails[targetId], {
      contactId: snapshot ? snapshot.contact_id || state.sync.openDetails[targetId].contactId || null : state.sync.openDetails[targetId].contactId || null,
      baseline: captureConversationVersion(snapshot),
    });
  }

  function applyOptimisticReplyToWorkspace(targetId, message, snapshot) {
    if (!targetId || !message) {
      return;
    }
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    const thread = target.querySelector(".message-thread");
    if (thread && !thread.querySelector(`[data-message-id="${e(message.id)}"]`)) {
      const article = document.createElement("article");
      article.className = `message-item ${message.direction || "outbound"}`;
      if (message.id) {
        article.dataset.messageId = message.id;
      }
      article.innerHTML = `<div class="meta">${e(message.direction || "outbound")} - ${formatDate(message.created_at)}</div><div>${e(message.body)}</div>`;
      thread.appendChild(article);
    }
    const heroMeta = target.querySelector(".conversation-hero-copy .meta");
    if (heroMeta) {
      const count = Array.isArray((snapshot || {}).optimistic_messages)
        ? Math.max((snapshot.optimistic_messages || []).length, thread ? thread.querySelectorAll(".message-item").length : 0)
        : thread
          ? thread.querySelectorAll(".message-item").length
          : null;
      if (count) {
        heroMeta.textContent = `${(snapshot && snapshot.phone_e164) || "phone unavailable"} - ${count} message(s)`;
      }
    }
  }

  function applyWorkspaceHeroSummary(targetId, summary) {
    if (!targetId || !summary) {
      return;
    }
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    const labelNode = target.querySelector("[data-hero-summary-label]");
    const valueNode = target.querySelector("[data-hero-summary-value]");
    const metaNode = target.querySelector("[data-hero-summary-meta]");
    const toneNode = target.querySelector("[data-hero-summary-tone]");
    if (labelNode) {
      labelNode.textContent = summary.label || "";
    }
    if (valueNode) {
      valueNode.textContent = summary.value || "";
    }
    if (metaNode) {
      metaNode.textContent = summary.meta || "";
    }
    if (toneNode) {
      toneNode.className = badgeClass(summary.tone || "muted");
      toneNode.textContent = summary.tone || "muted";
    }
  }

  function finalizeConversationWorkspaceAction(targetId, conversationId, summary) {
    if (!targetId || !conversationId) {
      return;
    }
    if (summary) {
      applyWorkspaceHeroSummary(targetId, summary);
    }
    refreshOpenConversationBaseline(
      targetId,
      rememberedConversationSnapshot(conversationId) || conversationSnapshotById(conversationId)
    );
    clearConversationNotice(targetId);
  }

  function clearConversationNotice(targetId) {
    if (!targetId) {
      return;
    }
    delete state.sync.noticesByTarget[targetId];
    renderConversationNotice(targetId);
    renderSyncStatus();
  }

  function rememberConversationNotice(targetId, notice) {
    if (!targetId) {
      return;
    }
    state.sync.noticesByTarget[targetId] = notice;
    renderConversationNotice(targetId);
    renderSyncStatus();
  }

  async function fetchConversationSnapshotForSync(conversationId, targetId) {
    const detail = state.sync.openDetails[targetId] || {};
    if (detail.contactId) {
      const related = await apiFetch(`/api/conversations?contact_id=${encodeURIComponent(detail.contactId)}&limit=12`, {
        headers: authHeaders(),
      });
      if (Array.isArray(related)) {
        const match = related.find((item) => item.id === conversationId);
        if (match) {
          rememberConversationSnapshot(conversationId, match);
          return match;
        }
      }
    }
    return conversationSnapshotById(conversationId);
  }

  async function evaluateSelectedConversationSync() {
    if (!state.selectedConversationId || !state.selectedConversationTargetId) {
      return;
    }
    const targetId = state.selectedConversationTargetId;
    const detailMeta = state.sync.openDetails[targetId];
    if (!detailMeta || !detailMeta.baseline) {
      return;
    }
    const latest = await fetchConversationSnapshotForSync(state.selectedConversationId, targetId);
    const drift = latest ? describeConversationDrift(detailMeta.baseline, latest) : {
      tone: "stale",
      message: "This record may have moved out of the current workspace. Refresh to review the latest state.",
      changes: ["record no longer matched the current visible workspace"],
    };
    if (!drift) {
      clearConversationNotice(targetId);
      return;
    }
    rememberConversationNotice(targetId, drift);
    queueTelemetryEvent({
      eventName: "workspace_sync_detected",
      surface: targetId.startsWith("manager") ? "manager" : "operator",
      route: buildSurfaceHash(targetId.startsWith("manager") ? "manager" : "operator"),
      metadata: { conversation_id: state.selectedConversationId, changes: drift.changes },
    });
  }

  function scheduleBackgroundRefresh() {
    if (state.sync.timer) {
      window.clearTimeout(state.sync.timer);
      state.sync.timer = null;
    }
    if (!state.token || !state.context || document.hidden) {
      renderSyncStatus();
      return;
    }
    state.sync.timer = window.setTimeout(async function () {
      state.sync.timer = null;
      if (!state.token || document.hidden) {
        scheduleBackgroundRefresh();
        return;
      }
      if (hasPending(state.runtime)) {
        scheduleBackgroundRefresh();
        return;
      }
      if (!beginPending(state.runtime, "background-refresh")) {
        scheduleBackgroundRefresh();
        return;
      }
      try {
        await loadBootstrap({ syncDetail: false, telemetryEventName: "bootstrap_live_sync" });
        await evaluateSelectedConversationSync();
      } catch (_error) {
        // keep the UI stable; manual refresh + visible statuses already cover error states
      } finally {
        endPending(state.runtime, "background-refresh");
        scheduleBackgroundRefresh();
      }
    }, state.sync.intervalMs);
    renderSyncStatus();
  }

  async function loadBootstrap(options) {
    const settings = Object.assign({ syncDetail: true, telemetryEventName: "bootstrap_loaded" }, options || {});
    if (!state.token) {
      return;
    }
    const renderVersion = nextRenderVersion(state.runtime, "bootstrap");
    const startedAt = window.performance.now();
    const bootstrapPayload = await apiFetch("/api/product-ui/bootstrap", { headers: authHeaders() });
    if (!isCurrentRenderVersion(state.runtime, "bootstrap", renderVersion)) {
      return;
    }
    state.bootstrap = bootstrapPayload;
    state.sync.lastBootstrapAt = new Date().toISOString();
    queueTelemetryEvent({
      eventName: settings.telemetryEventName,
      surface: "shell",
      route: "#bootstrap",
      durationMs: window.performance.now() - startedAt,
      metadata: { sections: (state.bootstrap.sections || []).map((item) => item.key) },
    });
    renderNav();
    await renderSurfaces();
    if (settings.syncDetail) {
      await syncSelectedConversationDetail();
    }
    renderSyncStatus();
    scheduleBackgroundRefresh();
  }

  function renderMetrics(containerId, items) {
    document.getElementById(containerId).innerHTML = items.map((item) => metricTileMarkup(item, e)).join("");
  }

  function currentInboxFilters(surfaceKey) {
    return state.inboxFilters[surfaceKey] || defaultInboxFilters();
  }

  function setManagerSelection(ids) {
    state.managerSelection = Array.from(new Set((ids || []).filter(Boolean)));
  }

  function selectedManagerConversationIds() {
    return state.managerSelection.slice();
  }

  function updateInboxFilters(surfaceKey, filters) {
    state.inboxFilters[surfaceKey] = Object.assign({}, defaultInboxFilters(), filters || {});
    persistInboxFilters(window.localStorage, surfaceKey, state.inboxFilters[surfaceKey]);
  }

  async function loadInboxItems(surfaceKey, views, filters) {
    const entries = await Promise.all(
      views.map(async (item) => {
        const request = buildInboxViewRequest(item, filters || currentInboxFilters(surfaceKey));
        const payload = await apiFetch(request.path, { headers: authHeaders() });
        return [item.key, request.localFilter ? applyClientConversationFilters(payload, filters || currentInboxFilters(surfaceKey)) : payload];
      })
    );
    return Object.fromEntries(entries);
  }

  function activeInboxWorkspace(surfaceKey) {
    return state.inboxWorkspace[surfaceKey] || null;
  }

  function setActiveInboxView(surfaceKey, viewKey) {
    const workspace = activeInboxWorkspace(surfaceKey);
    if (!workspace) {
      return;
    }
    const valid = workspace.views.some((item) => item.key === viewKey);
    workspace.activeView = valid ? viewKey : workspace.defaultView;
    renderInboxWorkspace(surfaceKey, workspace);
    if (surfaceKey === "manager") {
      renderManagerBulkSummary();
    }
  }

  function inboxDetailTarget(surfaceKey) {
    return `${surfaceKey}-conversation-detail`;
  }

  function managerConversationCard(item) {
    return `
      <article class="conversation-card manager-card">
        <header>
          <div>
            <label class="checkbox-line">
              <input type="checkbox" class="manager-conversation-check" value="${item.id}" ${selectedManagerConversationIds().includes(item.id) ? "checked" : ""} />
              <strong>${e(item.contact_name)}</strong>
            </label>
            <div class="meta">${e(item.phone_e164)} - ${formatDate(item.last_message_at || item.created_at)}</div>
          </div>
          <div class="pill-row">
            <span class="${badgeClass(item.aging_bucket)}">${e(item.aging_bucket || "n/a")}</span>
            <span class="${badgeClass(item.assigned_user_id ? "ready" : "stale")}">${item.assigned_user_id ? "assigned" : "unassigned"}</span>
            <span class="${badgeClass(item.first_response_overdue ? "overdue" : "ready")}">${item.first_response_overdue ? "response overdue" : "response ok"}</span>
          </div>
        </header>
        <p class="meta">${e((item.latest_message && item.latest_message.body) || "No latest message.")}</p>
        <div class="conversation-actions">
          <button class="action ghost" type="button" data-detail-conversation="${item.id}" data-detail-target="${inboxDetailTarget("manager")}">Details</button>
        </div>
      </article>
    `;
  }

  function operatorConversationCard(item, queueKind, canClaim) {
    const card = buildOperatorLeadCardView(item, { queueKind, canClaim });
    return `
      <article class="conversation-card operator-lead-card tone-${e(card.tone)}">
        <header>
          <div>
            <div class="eyebrow">${e(card.queueLabel)}</div>
            <h4>${e(card.contactName)}</h4>
            <div class="meta">${e(card.phone)} - ${formatDate(item.last_message_at || item.created_at)}</div>
          </div>
          <div class="pill-row">
            ${card.badges.map((badge) => `<span class="${badgeClass(badge.tone)}">${e(badge.label)}</span>`).join("")}
          </div>
        </header>
        <div class="operator-card-priority">
          <span class="${badgeClass(card.priorityTone)}">${e(card.priorityLabel)}</span>
          <div>
            <strong>${e(card.focusTitle)}</strong>
            <p>${e(card.focusDetail)}</p>
          </div>
        </div>
        <p class="operator-message-preview">${e(card.latestMessagePreview)}</p>
        <div class="operator-card-facts">
          ${card.facts
            .map(
              (fact) => `
                <div>
                  <span>${e(fact.label)}</span>
                  <strong>${e(fact.value)}</strong>
                </div>
              `
            )
            .join("")}
        </div>
        <div class="conversation-actions operator-card-actions">
          <button class="action primary" type="button" data-detail-conversation="${item.id}" data-detail-target="${inboxDetailTarget("operator")}">${e(card.openLabel)}</button>
          ${card.canClaim ? `<button class="action ghost" type="button" data-claim-conversation="${item.id}">${e(card.claimLabel)}</button>` : ""}
          ${card.canUnassign ? `<button class="action ghost" type="button" data-unassign-conversation="${item.id}">Unassign</button>` : ""}
        </div>
      </article>
    `;
  }

  function inboxAssigneeOptions(includeAny = true) {
    const members = (((state.bootstrap || {}).admin_control_center || {}).workspace_members || []).filter((item) =>
      ["tenant_owner", "manager", "analyst", "operator"].includes(item.role) && item.status === "active"
    );
    return (includeAny ? ['<option value="">Any assignee</option>'] : [])
      .concat(members.map((item) => `<option value="${item.user_id}">${e(item.full_name)} - ${e(item.role)}</option>`))
      .join("");
  }

  function activeInboxFilterCount(surfaceKey) {
    const filters = currentInboxFilters(surfaceKey);
    return Object.entries(filters).filter(([key, value]) => {
      if (key === "status") {
        return value && value !== "open";
      }
      return Boolean(value);
    }).length;
  }

  function renderInboxFilters(surfaceKey, workspace) {
    const target = document.getElementById(`${surfaceKey}-inbox-filters`);
    if (!target) {
      return;
    }
    const filters = currentInboxFilters(surfaceKey);
    const savedViews = state.inboxSavedViews[surfaceKey] || [];
    const activeFilterTotal = activeInboxFilterCount(surfaceKey);
    const filterForm = `
      <form class="detail-card filter-card" data-inbox-filter-form="${surfaceKey}">
        <header>
          <div><h4>Search and filters</h4><div class="meta">Refine ${e(surfaceKey)} inbox results and save reusable views.</div></div>
          <div class="pill-row">
            <span class="${badgeClass(savedViews.length > 0 ? "ready" : "muted")}">${savedViews.length} saved</span>
            <span class="${badgeClass(workspace.activeView === "overdue" ? "overdue" : "ready")}">${e(workspace.activeView)} view</span>
          </div>
        </header>
        <div class="grid grid-3">
          <label><span>Search</span><input name="search" value="${e(filters.search)}" placeholder="Contact or phone" /></label>
          <label>
            <span>Status</span>
            <select name="status">
              <option value="open" ${filters.status === "open" ? "selected" : ""}>Open</option>
              <option value="closed" ${filters.status === "closed" ? "selected" : ""}>Closed</option>
            </select>
          </label>
          <label>
            <span>Aging bucket</span>
            <select name="aging_bucket">
              <option value="" ${filters.aging_bucket === "" ? "selected" : ""}>Any</option>
              <option value="new" ${filters.aging_bucket === "new" ? "selected" : ""}>New</option>
              <option value="stale" ${filters.aging_bucket === "stale" ? "selected" : ""}>Stale</option>
              <option value="overdue" ${filters.aging_bucket === "overdue" ? "selected" : ""}>Overdue</option>
            </select>
          </label>
          ${
            surfaceKey === "manager"
              ? `<label><span>Assigned user</span><select name="assigned_user_id">${inboxAssigneeOptions()}</select></label>`
              : ""
          }
          <label><span>Date from</span><input type="date" name="date_from" value="${e(filters.date_from)}" /></label>
          <label><span>Date to</span><input type="date" name="date_to" value="${e(filters.date_to)}" /></label>
        </div>
        <div class="grid grid-3">
          <label><span>Saved view name</span><input name="preset_name" placeholder="Overdue watch" /></label>
          <label>
            <span>Saved views</span>
            <select name="saved_view_id">
              <option value="">Select a saved view</option>
              ${savedViews.map((item) => `<option value="${item.id}">${e(item.name)}</option>`).join("")}
            </select>
          </label>
        </div>
        <div class="button-row">
          <div class="meta">Open and overdue views use server-side filters. Mine, focus, and unassigned apply local refinement on the current queue slice.</div>
          <div class="button-row">
            <button class="action primary" type="submit">Apply Filters</button>
            <button class="action ghost" type="button" data-reset-inbox-filters="${surfaceKey}">Reset</button>
            <button class="action ghost" type="button" data-save-inbox-view="${surfaceKey}">Save View</button>
            <button class="action ghost" type="button" data-apply-saved-view="${surfaceKey}">Apply Saved</button>
            <button class="action ghost" type="button" data-delete-saved-view="${surfaceKey}">Delete Saved</button>
          </div>
        </div>
      </form>
    `;
    target.innerHTML = surfaceKey === "operator"
      ? `
        <details class="operator-filter-drawer" ${activeFilterTotal ? "open" : ""}>
          <summary>
            <span>Advanced search and saved views</span>
            <span class="${badgeClass(activeFilterTotal ? "stale" : "muted")}">${activeFilterTotal ? `${activeFilterTotal} active` : "optional"}</span>
          </summary>
          ${filterForm}
        </details>
      `
      : filterForm;
    if (surfaceKey === "manager") {
      const assigneeSelect = target.querySelector('select[name="assigned_user_id"]');
      if (assigneeSelect) {
        assigneeSelect.value = filters.assigned_user_id || "";
      }
    }
  }

  function renderInboxWorkspace(surfaceKey, workspace) {
    const tabContainer = document.getElementById(`${surfaceKey}-inbox-tabs`);
    const filterContainer = document.getElementById(`${surfaceKey}-inbox-filters`);
    const summaryContainer = document.getElementById(`${surfaceKey}-inbox-summary`);
    const listContainer = document.getElementById(`${surfaceKey}-inbox-list`);
    const activeView = workspace.views.find((item) => item.key === workspace.activeView) || workspace.views[0];
    const items = workspace.items[activeView.key] || [];
    if (surfaceKey === "manager") {
      const visibleIds = new Set(items.map((item) => item.id));
      setManagerSelection(selectedManagerConversationIds().filter((item) => visibleIds.has(item)));
    }
    const summaryTitle = surfaceKey === "operator" ? `Today's Work: ${activeView.label}` : activeView.label;
    const summaryDescription = surfaceKey === "operator"
      ? `${activeView.description} Operator progress is tracked through reply, outcome, follow-up, and team visibility.`
      : activeView.description;
    const operatorQueueCommand = surfaceKey === "operator"
      ? buildOperatorQueueCommand({
          triage: (state.operatorData || {}).triage,
          activeView,
          activeItems: items,
          mine: (state.operatorData || {}).mine || [],
          unassigned: (state.operatorData || {}).unassigned || [],
        })
      : null;
    if (filterContainer) {
      renderInboxFilters(surfaceKey, workspace);
    }
    tabContainer.innerHTML = workspace.views
      .map((item) => {
        const count = (workspace.items[item.key] || []).length;
        return `
          <button class="nav-pill${item.key === activeView.key ? " active" : ""}" type="button" data-inbox-surface="${surfaceKey}" data-inbox-view="${item.key}">
            ${e(item.label)} <span class="nav-pill-count">${count}</span>
          </button>
        `;
      })
      .join("");
    summaryContainer.innerHTML = `
      ${operatorQueueCommand ? renderOperatorQueueCommand(operatorQueueCommand, items[0]?.id || "") : ""}
      <article class="detail-card inbox-summary-card">
        <header>
          <div><h4>${e(summaryTitle)}</h4><div class="meta">${e(summaryDescription)}</div></div>
          <div class="inbox-summary-actions">
            <div class="pill-row">
              <span class="${badgeClass(items.length > 0 ? "ready" : "muted")}">${items.length} visible</span>
              <span class="${badgeClass(surfaceKey === "manager" ? "manual" : "ready")}">${surfaceKey} workspace</span>
              <span class="${badgeClass((currentInboxFilters(surfaceKey).search || currentInboxFilters(surfaceKey).aging_bucket || currentInboxFilters(surfaceKey).date_from || currentInboxFilters(surfaceKey).date_to || currentInboxFilters(surfaceKey).assigned_user_id || currentInboxFilters(surfaceKey).status !== "open") ? "stale" : "muted")}">filters ${Object.values(currentInboxFilters(surfaceKey)).filter(Boolean).length ? "active" : "default"}</span>
            </div>
            ${
              surfaceKey === "operator" && items.length
                ? `<button class="action primary" type="button" data-detail-conversation="${items[0].id}" data-detail-target="${inboxDetailTarget("operator")}">Open next lead</button>`
                : ""
            }
          </div>
        </header>
      </article>
    `;
    if (!items.length) {
      const emptyState = buildInboxEmptyState({
        surfaceKey,
        activeView,
        filters: currentInboxFilters(surfaceKey),
      });
      listContainer.innerHTML = renderGuidedEmptyState(
        emptyState.title,
        emptyState.detail,
        emptyState.tone,
        emptyState.actions,
        ["inbox-empty-state"]
      );
      return;
    }
    listContainer.innerHTML = items
      .map((item) =>
        surfaceKey === "manager"
          ? managerConversationCard(item)
          : operatorConversationCard(
              item,
              activeView.key === "focus" ? (((state.operatorData || {}).triage || {}).focus_queue === "unassigned" ? "unassigned" : "mine") : activeView.key,
              Boolean((((state.operatorData || {}).triage || {}).summary || {}).can_self_assign_unassigned)
            )
      )
      .join("");
  }

  function conversationSnapshotById(conversationId) {
    for (const workspace of [state.inboxWorkspace.manager, state.inboxWorkspace.operator]) {
      if (!workspace) {
        continue;
      }
      for (const items of Object.values(workspace.items || {})) {
        const match = (items || []).find((item) => item.id === conversationId);
        if (match) {
          rememberConversationSnapshot(conversationId, match);
          return rememberedConversationSnapshot(conversationId);
        }
      }
    }
    return rememberedConversationSnapshot(conversationId);
  }

  function nextOperatorConversationId(currentConversationId) {
    const workspace = state.inboxWorkspace.operator;
    if (!workspace) {
      return "";
    }
    const activeItems = (workspace.items || {})[workspace.activeView] || [];
    const candidates = activeItems.length
      ? activeItems
      : Object.values(workspace.items || {}).flat();
    const next = candidates.find((item) => item && item.id && item.id !== currentConversationId);
    return next?.id || "";
  }

  function selectedInboxContext(targetId) {
    const surfaceKey = targetId.startsWith("manager") ? "manager" : "operator";
    const workspace = activeInboxWorkspace(surfaceKey);
    const triage = ((state.operatorData || {}).triage || {});
    return {
      surfaceKey,
      activeView: workspace ? workspace.activeView : null,
      focusQueue: triage.focus_queue || null,
      canSelfAssign: Boolean((triage.summary || {}).can_self_assign_unassigned),
    };
  }

  function renderDetailActions(detailActions, conversationId) {
    const actions = ['<button class="action ghost" type="button" data-refresh-detail="true">Refresh Workspace</button>'];
    if (detailActions.canClaim) {
      actions.push(`<button class="action primary" type="button" data-detail-claim="${conversationId}">Claim</button>`);
    }
    if (detailActions.canUnassign) {
      actions.push(`<button class="action ghost" type="button" data-detail-unassign="${conversationId}">Unassign</button>`);
    }
    return actions.join("");
  }

  function renderWorkspaceRows(rows) {
    return rows
      .map(
        (item) => `
          <div class="table-row">
            <div>
              <strong>${e(item.label)}</strong>
              <div class="meta">${e(item.meta || "")}</div>
            </div>
            <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
          </div>
        `
      )
      .join("");
  }

  function renderSummaryCards(cards) {
    return cards
      .map(
        (item) => `
          <article class="detail-stat-card">
            <div class="eyebrow">${e(item.label)}</div>
            <strong>${e(item.value)}</strong>
            <p class="meta">${e(item.detail)}</p>
            <span class="${badgeClass(item.tone)}">${e(item.tone || "muted")}</span>
          </article>
        `
      )
      .join("");
  }

  function renderOperatorQueueCommand(command, firstConversationId) {
    return `
      <article id="operator-queue-command" class="detail-card operator-queue-command">
        <header>
          <div>
            <div class="eyebrow">Operator command center</div>
            <h4>${e(command.title)}</h4>
            <div class="meta">${e(command.detail)}</div>
          </div>
          <div class="inbox-summary-actions">
            <span class="${badgeClass(command.tone)}">${e(command.statusLabel)}</span>
            ${
              firstConversationId
                ? `<button class="action primary" type="button" data-detail-conversation="${e(firstConversationId)}" data-detail-target="${inboxDetailTarget("operator")}">${e(command.primaryActionLabel)}</button>`
                : `<button class="action ghost" type="button" data-refresh-surface="operator">${e(command.primaryActionLabel)}</button>`
            }
          </div>
        </header>
        <div class="operator-command-grid">
          ${command.cards
            .map(
              (item) => `
                <div class="operator-command-step tone-${e(item.tone)}">
                  <span class="${badgeClass(item.tone)}">${e(item.label)}</span>
                  <strong>${e(item.value)}</strong>
                  <p class="meta">${e(item.detail)}</p>
                </div>
              `
            )
            .join("")}
        </div>
      </article>
    `;
  }

  function renderOperatorWorkflowPanel(guide) {
    return `
      <section id="operator-workflow-guide" class="workspace-panel operator-workflow-panel">
        <div class="section-head">
          <div>
            <h3>Operator run status</h3>
            <p>Assignment, thread, response, outcome, follow-up, and manager visibility for this record.</p>
          </div>
          <span class="${badgeClass(guide.status === "ready" ? "ready" : "stale")}">${e(guide.status.replaceAll("_", " "))}</span>
        </div>
        <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
          ${renderSummaryCards(guide.summaryCards)}
        </div>
        <div class="operator-workflow-steps">
          ${guide.steps
            .map(
              (item) => `
                <article class="operator-workflow-step ${e(item.status)}">
                  <div class="operator-step-index">${e(item.status === "complete" ? "Done" : item.status === "active" ? "Now" : "Next")}</div>
                  <div>
                    <strong>${e(item.label)}</strong>
                    <div class="meta">${e(item.meta)}</div>
                  </div>
                  <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                </article>
              `
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderOperatorFocusPath(path) {
    return `
      <section id="operator-focus-path" class="workspace-panel operator-focus-path">
        <div class="section-head">
          <div>
            <h3>${e(path.title)}</h3>
            <p>${e(path.detail)}</p>
          </div>
          <span class="${badgeClass(path.tone)}">${e(path.statusLabel)}</span>
        </div>
        <div class="operator-focus-grid">
          ${path.stages
            .map(
              (item) => `
                <article class="operator-focus-step ${e(item.status)}">
                  <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                  <strong>${e(item.label)}</strong>
                  <p class="meta">${e(item.detail)}</p>
                </article>
              `
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderOperatorDailyBrief(brief) {
    return `
      <section id="operator-daily-brief" class="workspace-panel operator-daily-brief">
        <div class="section-head">
          <div>
            <h3>${e(brief.title)}</h3>
            <p>${e(brief.detail)}</p>
          </div>
          <div class="inbox-summary-actions">
            <span class="${badgeClass(brief.tone)}">${e(brief.statusLabel)}</span>
            <button class="action primary" type="button" data-workspace-focus-target="${e(brief.primaryAction.targetId)}">${e(
              brief.primaryAction.label
            )}</button>
          </div>
        </div>
        <div class="grid grid-4 operator-daily-card-grid">
          ${brief.cards.map((item) => metricTileMarkup(item, e)).join("")}
        </div>
        <div class="table-like operator-daily-actions">
          ${brief.actionRows
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.label)}</strong><div class="meta">${e(item.detail)}</div></div>
                  <div class="operator-daily-action-cell">
                    <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                    <button class="action ghost" type="button" data-workspace-focus-target="${e(item.targetId)}">${e(item.actionLabel)}</button>
                  </div>
                </div>
              `
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderOperatorNextLeadHandoff(handoff, targetId) {
    return `
      <section id="operator-next-lead-handoff" class="workspace-panel operator-next-lead-handoff">
        <div class="section-head">
          <div>
            <h3>${e(handoff.title)}</h3>
            <p>${e(handoff.detail)}</p>
          </div>
          <span class="${badgeClass(handoff.tone)}">${e(handoff.statusLabel)}</span>
        </div>
        <div class="button-row">
          ${
            handoff.nextConversationId
              ? `<button class="action primary" type="button" data-detail-conversation="${e(handoff.nextConversationId)}" data-detail-target="${e(targetId)}">${e(handoff.primaryActionLabel)}</button>`
              : `<button class="action ghost" type="button" data-focus-inbox="operator">${e(handoff.primaryActionLabel)}</button>`
          }
          <button class="action ghost" type="button" data-focus-inbox="operator">${e(handoff.secondaryActionLabel)}</button>
        </div>
      </section>
    `;
  }

  function renderOperatorMessagingGuard(guard) {
    return `
      <section id="operator-messaging-guard" class="workspace-panel operator-messaging-guard">
        <div class="section-head">
          <div>
            <h3>${e(guard.title)}</h3>
            <p>${e(guard.detail)}</p>
          </div>
          <span class="${badgeClass(guard.tone)}">${e(guard.statusLabel)}</span>
        </div>
        <ul class="operator-guard-list">
          ${guard.checklist.map((item) => `<li>${e(item)}</li>`).join("")}
        </ul>
      </section>
    `;
  }

  function renderOperatorMessagingSafety(safety) {
    return `
      <section id="operator-messaging-safety" class="workspace-panel operator-messaging-safety">
        <div class="section-head">
          <div>
            <h3>${e(safety.title)}</h3>
            <p>Send eligibility, delivery status, DNC posture, and audit trail for this conversation.</p>
          </div>
          <span class="${badgeClass(safety.tone)}">${e(safety.statusLabel)}</span>
        </div>
        <div class="table-like">
          ${renderWorkspaceRows(safety.rows)}
        </div>
      </section>
    `;
  }

  function messageDeliveryMeta(message) {
    const parts = [message.direction, formatDate(message.created_at)];
    if (String(message.direction || "").toLowerCase() === "outbound") {
      parts.push(`status: ${message.status || "queued"}`);
      if (message.delivered_at) {
        parts.push(`delivered ${formatDate(message.delivered_at)}`);
      } else if (message.sent_at) {
        parts.push(`sent ${formatDate(message.sent_at)}`);
      }
      if (message.error_code) {
        parts.push(`error ${message.error_code}`);
      }
    }
    return parts.filter(Boolean).join(" - ");
  }

  function renderOperatorReplyAssist(assist) {
    return `
      <section id="operator-reply-assist" class="workspace-panel operator-reply-assist">
        <div class="section-head">
          <div>
            <h3>${e(assist.title)}</h3>
            <p>${e(assist.detail)}</p>
          </div>
          <span class="${badgeClass("ready")}">${assist.templates.length} snippets</span>
        </div>
        <div class="reply-template-grid">
          ${assist.templates
            .map(
              (item) => `
                <article class="reply-template-card">
                  <strong>${e(item.label)}</strong>
                  <p>${e(item.body)}</p>
                  <button class="action ghost" type="button" data-reply-template-body="${e(item.body)}">Insert</button>
                </article>
              `
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderLeadQualificationPanel(workspace, draftState = {}) {
    return `
      <section id="lead-qualification-panel" class="workspace-panel workspace-panel-emphasis">
        <div class="section-head">
          <div>
            <h3>Lead qualification</h3>
            <p>Capture seller intent, property context, price posture, and the next action for manager and owner visibility.</p>
          </div>
          <span class="${badgeClass(workspace.tone)}">${e(workspace.statusLabel)}</span>
        </div>
        <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
          ${renderSummaryCards(workspace.summaryCards)}
        </div>
        <div class="table-like qualification-readout">
          ${renderWorkspaceRows(workspace.rows)}
        </div>
        <form id="qualification-form-${state.selectedConversationTargetId}" class="stack qualification-form">
          <div class="grid grid-2">
            ${workspace.fields
              .map(
                (field) => `
                  <label>
                    <span>${e(field.label)}</span>
                    <textarea name="${e(field.key)}" placeholder="${e(field.placeholder)}" ${workspace.canSave ? "" : "disabled"}>${e(
                      draftState[`qualification_${field.key}`] || field.value || ""
                    )}</textarea>
                  </label>
                `
              )
              .join("")}
          </div>
          <div class="meta">${
            workspace.canSave
              ? "Saving qualification adds a structured lead note and timeline event visible to managers and owners."
              : e(workspace.unavailableReason)
          }</div>
          <button class="action primary" type="submit" ${workspace.canSave ? "" : "disabled"}>Save Qualification</button>
        </form>
      </section>
    `;
  }

  function renderOperatorCloseoutPanel(checklist) {
    return `
      <section id="operator-closeout-checklist" class="workspace-panel operator-closeout-panel">
        <div class="section-head">
          <div>
            <h3>Closeout checklist</h3>
            <p>Use this before moving to the next lead so reply, disposition, follow-up, and visibility stay aligned.</p>
          </div>
          <span class="${badgeClass(checklist.tone)}">${e(checklist.statusLabel)}</span>
        </div>
        <div class="operator-closeout-items">
          ${checklist.items
            .map(
              (item) => `
                <article>
                  <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                  <strong>${e(item.label)}</strong>
                  <p class="meta">${e(item.detail)}</p>
                </article>
              `
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderManagerReviewQueuePanel(reviewQueue) {
    return `
      ${renderSectionIntro({
        eyebrow: "Manager review",
        title: "Review Lane",
        description: "Operator replies, outcomes, and queue movement that should get a fast supervisory scan.",
        badges: [{ label: `${reviewQueue.items.length} item(s)`, tone: reviewQueue.items.length ? "stale" : "ready" }],
      })}
      <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
        ${renderSummaryCards(reviewQueue.summaryCards)}
      </div>
      <div class="table-like">
        ${
          reviewQueue.items.length
            ? reviewQueue.items
                .map(
                  (item) => `
                    <div class="table-row manager-review-row">
                      <div>
                        <strong>${e(item.label)}: ${e(item.contactName)}</strong>
                        <div class="meta">${e(item.meta)}</div>
                      </div>
                      <div class="button-row">
                        <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                        <button class="action ghost" type="button" data-manager-command-conversation="${e(item.conversationId)}">${e(item.actionLabel)}</button>
                      </div>
                    </div>
                  `
                )
                .join("")
            : renderRichEmptyState("No manager review items", "Recent operator work will appear here after replies, dispositions, or queue movement.", "ready")
        }
      </div>
    `;
  }

  function renderManagerVisibilityBrief(brief) {
    return `
      <section id="manager-visibility-brief" class="workspace-panel manager-visibility-brief">
        <div class="section-head">
          <div>
            <p class="section-kicker">Manager visibility</p>
            <h3>${e(brief.title)}</h3>
            <p>${e(brief.detail)}</p>
          </div>
          <span class="${badgeClass(brief.tone)}">${e(brief.statusLabel)}</span>
        </div>
        <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
          ${renderSummaryCards(brief.summaryCards)}
        </div>
        <div class="table-like">
          ${
            brief.rows.length
              ? brief.rows
                  .map(
                    (item) => `
                      <div class="table-row manager-visibility-row">
                        <div>
                          <strong>${e(item.label)}: ${e(item.contactName)}</strong>
                          <div class="meta">${e(item.summary)} | ${e(item.actor)}${item.occurredAt ? ` | ${formatDate(item.occurredAt)}` : ""}</div>
                        </div>
                        <div class="button-row">
                          <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                          <button class="action ghost" type="button" data-manager-command-conversation="${e(item.conversationId)}">${e(item.actionLabel)}</button>
                        </div>
                      </div>
                    `
                  )
                  .join("")
              : renderRichEmptyState("No operator changes waiting", "Reply, outcome, and handoff signals will appear here as operators complete lead work.", "ready")
          }
        </div>
      </section>
    `;
  }

  function renderManagerReviewCommandBrief(brief) {
    const primaryAction = brief.primaryAction || {};
    const primaryButton = primaryAction.conversationId
      ? `<button class="action primary" type="button" data-manager-command-conversation="${e(primaryAction.conversationId)}">${e(primaryAction.label)}</button>`
      : `<button class="action primary" type="button" data-manager-command-view="${e(primaryAction.viewKey || "open")}">${e(primaryAction.label || "Review queue")}</button>`;
    return `
      <section id="manager-review-command-brief" class="workspace-panel workspace-panel-emphasis manager-review-command-brief">
        <div class="section-head">
          <div>
            <p class="section-kicker">Manager command</p>
            <h3>${e(brief.title)}</h3>
            <p>${e(brief.detail)}</p>
          </div>
          <div class="operator-daily-action-cell">
            <span class="${badgeClass(brief.tone)}">${e(brief.statusLabel)}</span>
            ${primaryButton}
          </div>
        </div>
        <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis manager-review-command-grid">
          ${renderSummaryCards(brief.cards)}
        </div>
        <div class="table-like manager-review-command-table">
          ${brief.rows
            .map(
              (item) => `
                <div class="table-row manager-review-command-row">
                  <div>
                    <strong>${e(item.rank)}. ${e(item.label)}: ${e(item.contactName)}</strong>
                    <div class="meta">${e(item.summary)} | ${e(item.actor)}</div>
                  </div>
                  <div class="operator-daily-action-cell">
                    <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
                    ${
                      item.conversationId
                        ? `<button class="action ghost" type="button" data-manager-command-conversation="${e(item.conversationId)}">${e(item.actionLabel)}</button>`
                        : `<button class="action ghost" type="button" data-manager-command-view="${e(primaryAction.viewKey || "open")}">${e(item.actionLabel)}</button>`
                    }
                  </div>
                </div>
              `
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderManagerConversationReviewPanel(review) {
    return `
      <section id="manager-review-panel" class="workspace-panel workspace-panel-emphasis manager-review-panel">
        <div class="section-head">
          <div>
            <h3>Manager review</h3>
            <p>Approve the operator's next step, return guidance, or create a manager follow-up without leaving the record.</p>
          </div>
          <span class="${badgeClass(review.tone)}">${e(review.statusLabel)}</span>
        </div>
        <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
          ${renderSummaryCards(review.summaryCards)}
        </div>
        <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis manager-change-grid">
          ${renderSummaryCards(review.changeCards)}
        </div>
        <div class="table-like">
          ${renderWorkspaceRows(review.rows)}
        </div>
        <div class="conversation-actions manager-review-actions">
          ${review.actions
            .map(
              (item) => `<button class="action ${item.key === "mark_reviewed" ? "primary" : "ghost"}" type="button" data-manager-review-action="${e(item.key)}">${e(item.label)}</button>`
            )
            .join("")}
        </div>
      </section>
    `;
  }

  function renderSurfaceHelpPanel(surfaceKey, context = {}) {
    const target = document.getElementById(`${surfaceKey}-help`);
    if (!target) {
      return;
    }
    const help = buildSurfaceHelp({ surfaceKey, role: (state.context || {}).role, context });
    target.innerHTML = `
      ${renderSectionIntro({
        eyebrow: help.eyebrow,
        title: help.title,
        description: help.description,
        badges: [{ label: `${help.cards.length} guides`, tone: help.cards.length ? "ready" : "muted" }],
      })}
      <div class="help-card-grid">
        ${help.cards
          .map(
            (item) => `
              <article class="detail-card help-card">
                <header>
                  <div><h4>${e(item.label)}</h4><div class="meta">${e(item.detail)}</div></div>
                  <div class="${badgeClass(item.tone)}">${e(item.tone)}</div>
                </header>
                <div class="conversation-actions">
                  <a class="action ghost" href="${e(item.href)}" target="_blank" rel="noreferrer">${e(item.actionLabel)}</a>
                </div>
              </article>
            `
          )
          .join("")}
      </div>
      ${help.footer ? `<div class="detail-empty ready helper-footer">${e(help.footer)}</div>` : ""}
    `;
  }

  function taskImpact(details = {}) {
    const completedCount = Array.isArray(details.taskIdsCompleted) ? details.taskIdsCompleted.length : 0;
    const createdCount = details.taskIdCreated ? 1 : 0;
    const fragments = [];
    if (completedCount) {
      fragments.push(`${completedCount} follow-up task(s) completed.`);
    }
    if (createdCount) {
      fragments.push("1 follow-up task created.");
    }
    return fragments.join(" ").trim() || "No follow-up task changes were recorded.";
  }

  function currentWorkspaceActor() {
    return {
      user_id: (state.context || {}).user_id,
      full_name: (state.context || {}).full_name,
      role: (state.context || {}).role,
      email: (state.context || {}).email,
    };
  }

  function renderRecordNavigationRows(rows, targetId) {
    if (!Array.isArray(rows) || !rows.length) {
      return renderEmptyState("No record navigation rows are available for this conversation yet.", "muted");
    }
    return rows
      .map(
        (item) => `
          <div class="table-row table-row-action">
            <div>
              <strong>${e(item.label)}</strong>
              <div class="meta">${e(item.meta || "")}</div>
            </div>
            <div class="record-nav-actions">
              <span class="${badgeClass(item.tone)}">${e(item.value)}</span>
              ${
                item.conversationId && !item.current
                  ? `<button class="action ghost" type="button" data-detail-conversation="${e(item.conversationId)}" data-detail-target="${e(targetId)}">${e(
                      item.actionLabel || "Open"
                    )}</button>`
                  : `<span class="record-nav-viewing">${e(item.actionLabel || (item.current ? "Viewing" : "No action"))}</span>`
              }
            </div>
          </div>
        `
      )
      .join("");
  }

  function applyRecordPipelineWorkspace(targetId, options = {}) {
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    const workspace = buildRecordPipelineWorkspace({
      conversation: options.conversation || {},
      lead: options.lead || null,
      pipelineBoard: options.pipelineBoard || null,
      relatedConversations: options.relatedConversations || [],
      members: workflowTaskMembers(),
    });
    const summaryGrid = target.querySelector("#record-pipeline-summary");
    if (summaryGrid) {
      summaryGrid.innerHTML = renderSummaryCards(workspace.summaryCards);
    }
    const stateRows = target.querySelector("#record-pipeline-state");
    if (stateRows) {
      stateRows.innerHTML = renderWorkspaceRows(workspace.leadRows);
    }
    const progressRows = target.querySelector("#record-pipeline-progress");
    if (progressRows) {
      progressRows.innerHTML = renderWorkspaceRows(workspace.progressRows);
    }
    const navigationRows = target.querySelector("#record-navigation");
    if (navigationRows) {
      navigationRows.innerHTML = renderRecordNavigationRows(workspace.navigationRows, targetId);
    }
  }

  function applyInternalNotesWorkspace(targetId, options = {}) {
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    const noteSection = target.querySelector("#record-collaboration-panel .workspace-panel");
    if (!noteSection) {
      return;
    }
    const workspace = buildInternalNotesWorkspace({
      notes: options.notes || [],
      members: workflowTaskMembers(),
      currentUser: currentWorkspaceActor(),
      conversation: options.conversation || {},
      available: options.available !== false,
      unavailableReason: options.unavailableReason || null,
    });
    const summaryGrid = noteSection.querySelector(".detail-summary-grid");
    if (summaryGrid) {
      summaryGrid.innerHTML = renderSummaryCards(workspace.summaryCards);
    }
    const noteList = target.querySelector("#internal-note-list");
    if (noteList) {
      noteList.innerHTML = renderWorkspaceRows(workspace.noteRows);
    }
    const guidance = target.querySelector("#collaboration-guidance");
    if (guidance) {
      guidance.innerHTML = renderWorkspaceRows(workspace.guidanceRows);
    }
  }

  function applyFollowUpWorkspace(targetId, tasks) {
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }
    const panel = target.querySelector("#follow-up-workspace-panel");
    if (!panel) {
      return;
    }
    const workspace = buildFollowUpTaskWorkspace({
      tasks: tasks || [],
      members: workflowTaskMembers(),
      currentUser: currentWorkspaceActor(),
      now: new Date().toISOString(),
    });
    const summaryGrid = panel.querySelector(".detail-summary-grid");
    if (summaryGrid) {
      summaryGrid.innerHTML = renderSummaryCards(workspace.summaryCards);
    }
    const taskList = panel.querySelector("#follow-up-task-list");
    if (taskList) {
      taskList.innerHTML = renderWorkspaceRows(workspace.taskRows);
    }
    const actionList = target.querySelector(".task-action-list");
    if (actionList) {
      actionList.innerHTML = workspace.taskRows.length
        ? workspace.taskRows
            .filter((item) => item.status !== "completed")
            .map(
              (item) => `
                <div class="task-action-row">
                  <div>
                    <strong>${e(item.label)}</strong>
                    <div class="meta">${e(item.notes || item.meta || "")}</div>
                  </div>
                  <button class="action ghost" type="button" data-complete-task="${item.id}">Complete</button>
                </div>
              `
            )
            .join("")
            : renderRichEmptyState(
                "No follow-up tasks are open",
                "Create a reminder when a record needs another touch, callback, or manager follow-up.",
                "ready"
              );
    }
  }

  function preferredOutcomeSurface() {
    return hasBootstrapSection("manager") ? "manager" : "operator";
  }

  function renderDispositionBoard(board, options = {}) {
    const settings = Object.assign({ title: "Outcome board", description: "", showOpenButtons: true }, options);
    const cardMarkup = (board.cards || [])
      .map(
        (item) => `
          <article class="detail-card ${item.primaryConversationId ? "selected-card" : ""}">
            <header>
              <div>
                <h4>${e(item.label)}</h4>
                <div class="meta">${e(item.count)} record(s)${item.rate ? ` | ${e(item.rate)} rate` : ""}</div>
              </div>
              <span class="${badgeClass(item.tone)}">${e(item.rate || item.count)}</span>
            </header>
            ${
              settings.showOpenButtons && item.primaryConversationId
                ? `<div class="button-row"><button class="action ghost" type="button" data-outcome-open-conversation="${item.primaryConversationId}" data-outcome-surface="${e(preferredOutcomeSurface())}">Open latest ${e(item.label.toLowerCase())} record</button></div>`
                : `<div class="meta">${item.primaryConversationLabel ? `Latest record: ${e(item.primaryConversationLabel)}` : "No recent record available for direct review."}</div>`
            }
          </article>
        `
      )
      .join("");
    const recentMarkup = (board.recentRows || [])
      .map(
        (item) => `
          <div class="table-row">
            <div>
              <strong>${e(item.contactName)}</strong>
              <div class="meta">${e(item.summary)} - ${formatDate(item.occurredAt)}</div>
            </div>
            <div class="button-row">
              <span class="${badgeClass(item.tone)}">${e(item.label)}</span>
              ${
                settings.showOpenButtons && item.conversationId
                  ? `<button class="action ghost" type="button" data-outcome-open-conversation="${item.conversationId}" data-outcome-surface="${e(preferredOutcomeSurface())}">Inspect</button>`
                  : ""
              }
            </div>
          </div>
        `
      )
      .join("");
    return `
      ${renderSectionIntro({
        eyebrow: "Outcome management",
        title: settings.title,
        description: settings.description,
        badges: [
          { label: `${(board.cards || []).length} outcomes`, tone: (board.cards || []).length ? "ready" : "muted" },
          {
            label: `${(board.recentRows || []).length} recent changes`,
            tone: (board.recentRows || []).length ? "stale" : "muted",
          },
        ],
      })}
      <div class="detail-summary-grid">
        ${renderSummaryCards(board.summaryCards || [])}
      </div>
      <div class="grid grid-2">
        <div class="stack">
          ${cardMarkup || renderEmptyState("No disposition outcomes are available for this reporting window yet.", "muted")}
        </div>
        <div class="table-like">
          ${recentMarkup || renderEmptyState("Recent outcome changes will appear here after dispositions are saved.", "muted")}
        </div>
      </div>
    `;
  }

  async function openOutcomeConversation(conversationId, surfaceKey) {
    const targetSurface = surfaceKey === "manager" && !hasBootstrapSection("manager") ? "operator" : surfaceKey;
    if (targetSurface === "manager" && hasBootstrapSection("manager")) {
      showSurface("manager", { updateHash: true, persist: true });
      await applyManagerCommand({ conversationId });
      flash("Outcome record loaded into the manager workspace.", "success");
      return;
    }
    await loadConversationDetail(conversationId, inboxDetailTarget("operator"));
    showSurface("operator", { updateHash: true, persist: true });
    flash("Outcome record loaded into the operator workspace.", "success");
  }

  function renderManagerBulkSummary() {
    const workspace = activeInboxWorkspace("manager");
    const selectedCount = selectedManagerConversationIds().length;
    const summary = summarizeBulkSelection({
      selectedCount,
      activeView: workspace ? workspace.activeView : "open",
    });
    const targetSelect = document.getElementById("bulk-user-select");
    const targetLabel = targetSelect && targetSelect.selectedOptions.length
      ? targetSelect.selectedOptions[0].textContent
      : "No target user selected";
    document.getElementById("manager-bulk-summary").innerHTML = `
      <article class="detail-card">
        <header>
          <div><h4>${summary.selectedCount} selected</h4><div class="meta">${e(summary.recommendation)}</div></div>
          <div class="pill-row">
            <span class="${badgeClass(summary.selectedCount > 0 ? "ready" : "muted")}">${summary.activeView}</span>
            <span class="${badgeClass(summary.recommendedAction === "escalate" ? "overdue" : summary.recommendedAction === "release" ? "stale" : "ready")}">${summary.recommendedAction}</span>
          </div>
        </header>
        <div class="meta">Target user: ${e(targetLabel)}</div>
      </article>
    `;
    const result = state.managerBulkResult;
    document.getElementById("manager-bulk-result").innerHTML = result
      ? `
        <article class="detail-card">
          <header>
            <div><h4>Last bulk action</h4><div class="meta">${e(result.action)} completed in this session.</div></div>
            <div class="pill-row">
              <span class="${badgeClass(result.count > 0 ? "ready" : "muted")}">${result.count} ${e(result.label)}</span>
              <span class="${badgeClass(result.skipped > 0 ? "stale" : "ready")}">${result.skipped} skipped</span>
            </div>
          </header>
          <div class="meta">${e(result.firstSkipMessage || "No skipped records were reported.")}</div>
        </article>
      `
      : `<div class="detail-empty">Bulk action results will appear here after assign, release, or escalate.</div>`;
  }

  function renderManagerCommandWorkspace(commandWorkspace) {
    document.getElementById("manager-command-workspace").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Supervisory controls",
        title: "Command Workspace",
        description: "Jump directly into risk slices, user workload focus, and recent queue movement.",
        badges: [
          { label: `${commandWorkspace.commandCards.length} command cards`, tone: "ready" },
          { label: `${commandWorkspace.rebalanceRows.length} rebalance rows`, tone: "stale" },
        ],
      })}
      <div class="grid grid-2">
        ${commandWorkspace.commandCards
          .map(
            (item) => `
              <article class="detail-card">
                <header>
                  <div><h4>${e(item.label)}</h4><div class="meta">${e(item.detail)}</div></div>
                  <span class="${badgeClass(item.tone)}">${e(String(item.value))}</span>
                </header>
                ${
                  item.action
                    ? item.action.type === "view"
                      ? `<button class="action ghost" type="button" data-manager-command-view="${item.action.viewKey}">${e(item.actionLabel)}</button>`
                      : item.action.type === "user"
                        ? `<button class="action ghost" type="button" data-manager-command-user="${item.action.userId}">${e(item.actionLabel)}</button>`
                        : `<button class="action ghost" type="button" data-manager-command-conversation="${item.action.conversationId}">${e(item.actionLabel)}</button>`
                    : `<div class="meta">${e(item.actionLabel)}</div>`
                }
              </article>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("manager-rebalance").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Queue balancing",
        title: "Recommended Rebalance",
        description: "Use these guidance rows before running assign, escalate, or release.",
        badges: [{ label: `${commandWorkspace.rebalanceRows.length} guidance rows`, tone: commandWorkspace.rebalanceRows.length ? "ready" : "muted" }],
      })}
      <div class="table-like">
        ${renderWorkspaceRows(commandWorkspace.rebalanceRows)}
      </div>
      <div class="button-row">
        <button class="action ghost" type="button" data-manager-command-view="overdue">Open overdue slice</button>
        <button class="action ghost" type="button" data-manager-command-view="unassigned">Open unassigned slice</button>
        <button class="action ghost" type="button" data-manager-clear-focus="true">Clear manager focus</button>
      </div>
      <div id="manager-bulk-summary" class="stack"></div>
      <div id="manager-bulk-result" class="stack"></div>
    `;
  }

  async function ensureAdminControlCenter() {
    if (!hasBootstrapSection("admin")) {
      return null;
    }
    if (state.bootstrap.admin_control_center) {
      return state.bootstrap.admin_control_center;
    }
    state.bootstrap.admin_control_center = await apiFetch("/api/admin/control-center", { headers: authHeaders() });
    return state.bootstrap.admin_control_center;
  }

  async function applyManagerCommand(options = {}) {
    const settings = Object.assign({ viewKey: null, assignedUserId: null, conversationId: null }, options);
    if (settings.conversationId) {
      await loadConversationDetail(settings.conversationId, inboxDetailTarget("manager"));
      showSurface("manager", { updateHash: false, persist: false });
      return;
    }

    const nextView = settings.viewKey || "open";
    const nextFilters = Object.assign({}, currentInboxFilters("manager"));
    nextFilters.status = "open";
    nextFilters.search = "";
    nextFilters.date_from = "";
    nextFilters.date_to = "";
    if (nextView === "overdue") {
      nextFilters.aging_bucket = "overdue";
      nextFilters.assigned_user_id = "";
    } else if (nextView === "unassigned") {
      nextFilters.aging_bucket = "";
      nextFilters.assigned_user_id = "";
    } else {
      nextFilters.aging_bucket = "";
      if (settings.assignedUserId !== null) {
        nextFilters.assigned_user_id = settings.assignedUserId || "";
      }
    }

    if (settings.assignedUserId !== null && nextView !== "overdue" && nextView !== "unassigned") {
      nextFilters.assigned_user_id = settings.assignedUserId || "";
    }

    updateInboxFilters("manager", nextFilters);
    await renderManagerSurface();
    setActiveInboxView("manager", nextView);
  }

  async function renderManagerSurface() {
    const renderVersion = nextRenderVersion(state.runtime, "manager");
    setSurfaceStatus("manager", "stale", "Loading manager workspace...", true);
    try {
      const [dashboard, actions, controlCenter] = await Promise.all([
        apiFetch("/api/conversations/queue/dashboard?sample_limit=5&include_zero_workload=true", { headers: authHeaders() }),
        apiFetch("/api/conversations/queue/actions?limit=8", { headers: authHeaders() }),
        ensureAdminControlCenter(),
      ]);
      if (!isCurrentRenderVersion(state.runtime, "manager", renderVersion)) {
        return;
      }
      const inbox = buildManagerInboxWorkspace({
        previousView: activeInboxWorkspace("manager") ? activeInboxWorkspace("manager").activeView : null,
        dashboard,
      });
      const items = await loadInboxItems("manager", inbox.views, currentInboxFilters("manager"));
      if (!isCurrentRenderVersion(state.runtime, "manager", renderVersion)) {
        return;
      }
      state.managerData = { dashboard, actions };
      state.inboxWorkspace.manager = Object.assign({}, inbox, { items, activeView: inbox.defaultView });
      const deck = buildManagerDeckSnapshot(dashboard);
      const commandWorkspace = buildManagerCommandWorkspace(dashboard, actions, {
        activeView: state.inboxWorkspace.manager.activeView,
      });
      const managerReviewQueue = buildManagerReviewQueue(actions, { dashboard, limit: 5 });
      const managerVisibilityBrief = buildManagerVisibilityBrief(actions, { dashboard, reviewQueue: managerReviewQueue, limit: 4 });
      const managerReviewCommandBrief = buildManagerReviewCommandBrief({
        reviewQueue: managerReviewQueue,
        visibilityBrief: managerVisibilityBrief,
        dashboard,
        commandWorkspace,
        limit: 3,
      });
      const outcomeBoard = buildDispositionBoard(dashboard.reports.week, actions);
      const dispositionCounts = Array.isArray(dashboard.reports.week.totals.disposition_counts)
        ? dashboard.reports.week.totals.disposition_counts
        : [];
      renderSurfaceHelpPanel("manager", {
        outcomeCount: dispositionCounts.reduce((total, item) => total + Number(item.count || 0), 0),
      });
      renderMetrics("manager-dashboard", [
        { label: "Open Queue", value: dashboard.summary.open_total, detail: `${dashboard.summary.open_unassigned} unassigned` },
        { label: "Overdue", value: dashboard.sla.overdue_total, detail: `${dashboard.sla.first_response_overdue_total} first-response overdue` },
        { label: "Inbound Today", value: dashboard.reports.day.totals.inbound_volume, detail: `${dashboard.reports.week.totals.inbound_volume} this week` },
        { label: "Review Lane", value: managerReviewQueue.items.length, detail: "operator work to scan" },
      ]);
      document.getElementById("manager-spotlight").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Supervisory readout",
        title: "Manager Brief",
        description: "The fastest summary of risk, workload posture, and supervisory attention for this queue.",
        badges: [
          { label: `${commandWorkspace.reviewCards.length} review cards`, tone: "ready" },
          { label: deck.attentionRequired ? "attention required" : "stable", tone: deck.attentionRequired ? "overdue" : "ready" },
        ],
      })}
      <div class="detail-summary-grid detail-summary-grid-inline">
        ${renderSummaryCards(commandWorkspace.reviewCards)}
      </div>
      <div class="grid grid-2">
        <article class="detail-card">
          <header>
            <div><h4>${deck.attentionRequired ? "Attention required" : "Queue stable"}</h4><div class="meta">Overdue, unassigned, and workload risk in one summary.</div></div>
            <div class="pill-row">
              <span class="${badgeClass(deck.attentionRequired ? "overdue" : "ready")}">${deck.attentionRequired ? "at risk" : "stable"}</span>
              <span class="${badgeClass(deck.maintenanceRuns > 0 ? "ready" : "muted")}">${deck.maintenanceRuns} maintenance runs</span>
            </div>
          </header>
          <div class="meta">${deck.overdueSpotlight} overdue spotlight / ${deck.unassignedSpotlight} unassigned spotlight / ${deck.unassignedCount} total unassigned</div>
        </article>
        <article class="detail-card">
          <header>
            <div><h4>${e((deck.mostLoadedUser || {}).full_name || "No workload leader")}</h4><div class="meta">${e((deck.mostLoadedUser || {}).email || "No active assignee available")}</div></div>
            <div class="pill-row">
              <span class="${badgeClass(deck.mostLoadedUser ? "stale" : "muted")}">${deck.mostLoadedUser ? `${deck.mostLoadedUser.open_assigned_count} open` : "n/a"}</span>
              <span class="${badgeClass(deck.firstResponseOverdueCount > 0 ? "overdue" : "ready")}">${deck.firstResponseOverdueCount} first-response overdue</span>
            </div>
          </header>
          <div class="meta">${deck.mostLoadedUser ? `${deck.mostLoadedUser.role} currently holds the heaviest assignment load.` : "No active workload users were returned."}</div>
        </article>
      </div>
      <div class="grid grid-2">
        ${commandWorkspace.commandCards
          .slice(0, 2)
          .map(
            (item) => `
              <article class="detail-card">
                <header>
                  <div><h4>${e(item.label)}</h4><div class="meta">${e(item.detail)}</div></div>
                  <span class="${badgeClass(item.tone)}">${e(String(item.value))}</span>
                </header>
                ${
                  item.action
                    ? item.action.type === "view"
                      ? `<button class="action ghost" type="button" data-manager-command-view="${item.action.viewKey}">${e(item.actionLabel)}</button>`
                      : item.action.type === "user"
                        ? `<button class="action ghost" type="button" data-manager-command-user="${item.action.userId}">${e(item.actionLabel)}</button>`
                        : `<button class="action ghost" type="button" data-manager-command-conversation="${item.action.conversationId}">${e(item.actionLabel)}</button>`
                    : `<div class="meta">${e(item.actionLabel)}</div>`
                }
              </article>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("manager-workload").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Workload review",
        title: "Workload Radar",
        description: "Open assignments by user plus the strongest recent outcome mix from the reporting window.",
        badges: [{ label: `${commandWorkspace.workloadRows.length} active users`, tone: commandWorkspace.workloadRows.length ? "ready" : "muted" }],
      })}
      <div class="grid grid-2">
        <div class="table-like">
          ${commandWorkspace.workloadRows
            .map(
              (user) => `
                <div class="table-row">
                  <div><strong>${e(user.fullName)}</strong><div class="meta">${e(user.email)} - ${e(user.role)} - ${e(user.shareLabel)} of open queue</div></div>
                  <div class="button-row">
                    <div class="${badgeClass(user.tone)}">${user.openAssignedCount}</div>
                    <button class="action ghost" type="button" data-manager-command-user="${user.userId}">Inspect</button>
                  </div>
                </div>
              `
            )
            .join("")}
        </div>
        <div class="table-like" id="manager-outcome-focus">
          ${
            commandWorkspace.outcomeFocusRows.length
              ? renderWorkspaceRows(commandWorkspace.outcomeFocusRows)
              : renderEmptyState("Outcome mix will appear here once the reporting window has disposition activity.", "muted")
          }
        </div>
      </div>
    `;
    document.getElementById("manager-outcomes").innerHTML = renderDispositionBoard(outcomeBoard, {
      title: "Outcome board",
      description: "Disposition mix, recent outcome changes, and quick inspection into records that changed state.",
    });
    document.getElementById("manager-actions").innerHTML = `
      ${renderManagerReviewCommandBrief(managerReviewCommandBrief)}
      <div class="section-divider"></div>
      ${renderManagerVisibilityBrief(managerVisibilityBrief)}
      <div class="section-divider"></div>
      ${renderManagerReviewQueuePanel(managerReviewQueue)}
      <div class="section-divider"></div>
      ${renderSectionIntro({
        eyebrow: "Action review",
        title: "Recent Queue Actions",
        description: "Manual interventions and automation movement, with direct inspection into the affected thread.",
        badges: [{ label: `${commandWorkspace.actionRows.length} actions`, tone: commandWorkspace.actionRows.length ? "ready" : "muted" }],
      })}
      <div class="table-like">
        ${commandWorkspace.actionRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.contactName)}</strong><div class="meta">${e(item.summary)} - ${formatDate(item.occurredAt)} - ${e(item.emphasis)}</div></div>
                <div class="button-row">
                  <div class="${badgeClass(item.tone)}">${e(item.sourceKind)}</div>
                  <button class="action ghost" type="button" data-manager-command-conversation="${item.conversationId}">Inspect</button>
                </div>
              </div>
            `
          )
          .join("")}
      </div>
    `;
    renderManagerCommandWorkspace(commandWorkspace);

      const members = (controlCenter ? controlCenter.workspace_members : []).filter((item) =>
        ["tenant_owner", "manager", "analyst", "operator"].includes(item.role) && item.status === "active"
      );
      document.getElementById("bulk-user-select").innerHTML = members
        .map((item) => `<option value="${item.user_id}">${e(item.full_name)} - ${e(item.role)}</option>`)
        .join("");
      renderInboxWorkspace("manager", state.inboxWorkspace.manager);
      renderManagerBulkSummary();
      setSurfaceStatus("manager", "ready", "Manager workspace up to date.", false);
    } catch (error) {
      if (isCurrentRenderVersion(state.runtime, "manager", renderVersion)) {
        setSurfaceStatus("manager", "overdue", "Manager workspace failed to refresh.", false);
      }
      throw error;
    }
  }

  async function renderOperatorSurface() {
    const renderVersion = nextRenderVersion(state.runtime, "operator");
    setSurfaceStatus("operator", "stale", "Loading operator queue...", true);
    try {
      const triage = await apiFetch("/api/conversations/queue/triage?sample_limit=5", { headers: authHeaders() });
      if (!isCurrentRenderVersion(state.runtime, "operator", renderVersion)) {
        return;
      }
      const inbox = buildOperatorInboxWorkspace({
        previousView: activeInboxWorkspace("operator") ? activeInboxWorkspace("operator").activeView : null,
        triage,
      });
      const items = await loadInboxItems("operator", inbox.views, currentInboxFilters("operator"));
      if (!isCurrentRenderVersion(state.runtime, "operator", renderVersion)) {
        return;
      }
      const [mine, unassigned] = [items.mine || [], items.unassigned || []];
      state.operatorData = { triage, mine, unassigned };
      state.inboxWorkspace.operator = Object.assign({}, inbox, { items, activeView: inbox.defaultView });
      renderSurfaceHelpPanel("operator", {
        focusQueue: triage.focus_queue,
      });
      renderMetrics("operator-triage", [
        { label: "Focus Queue", value: triage.focus_queue, detail: triage.focus_reason },
        { label: "My Open", value: triage.summary.my_open_total, detail: `${triage.summary.my_awaiting_response_total} awaiting response` },
        { label: "Unassigned Open", value: triage.summary.unassigned_open_total, detail: `${triage.summary.unassigned_overdue_total} overdue` },
      ]);
      renderInboxWorkspace("operator", state.inboxWorkspace.operator);
      setSurfaceStatus("operator", "ready", "Operator queue is current.", false);
    } catch (error) {
      if (isCurrentRenderVersion(state.runtime, "operator", renderVersion)) {
        setSurfaceStatus("operator", "overdue", "Operator queue failed to refresh.", false);
      }
      throw error;
    }
  }

  function notificationDestinationsEnabled() {
    return featureEnabled(state.bootstrap, "notification_destinations");
  }

  function selectedSupportDestination(support) {
    const candidates = (support?.notification_destinations || []);
    if (!candidates.length) {
      return null;
    }
    return (
      candidates.find((item) => item.id === state.supportWorkflow.selectedDestinationId) ||
      candidates.find((item) =>
        ["quarantined", "recovery_failed"].includes(String((item.recovery || {}).recovery_status || "").toLowerCase())
      ) ||
      candidates.find((item) => String(item.status || "").toLowerCase() !== "active") ||
      candidates[0]
    );
  }

  function defaultDestinationDeliveryKind(destination) {
    if (!destination) {
      return "queue_alert";
    }
    if (destination.bound_to_queue_alerts) {
      return "queue_alert";
    }
    if (destination.bound_to_queue_digests) {
      return "queue_digest";
    }
    return "queue_alert";
  }

  async function loadSupportDestinationDeliveries(destinationId, options) {
    if (!destinationId || !notificationDestinationsEnabled()) {
      return [];
    }
    const settings = Object.assign({ forceRefresh: false }, options || {});
    if (!settings.forceRefresh && state.supportWorkflow.deliveriesByDestination[destinationId]) {
      return state.supportWorkflow.deliveriesByDestination[destinationId];
    }
    const deliveries = await apiFetch(`/api/notifications/destinations/${destinationId}/deliveries?limit=8`, {
      headers: authHeaders(),
    });
    state.supportWorkflow.deliveriesByDestination[destinationId] = Array.isArray(deliveries) ? deliveries : [];
    return state.supportWorkflow.deliveriesByDestination[destinationId];
  }

  async function refreshSupportAndGovernance() {
    await renderSupportSurface();
    if ((state.bootstrap.sections || []).some((item) => item.key === "governance")) {
      await renderGovernanceSurface();
    }
  }

  async function runSupportDestinationTest(destinationId, deliveryKind) {
    const destination = (state.supportData?.notification_destinations || []).find((item) => item.id === destinationId);
    if (!destination) {
      flash("Select a destination first.", "error");
      return;
    }
    const result = await apiFetch(`/api/notifications/destinations/${destinationId}/test`, {
      method: "POST",
      headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
      body: JSON.stringify({ delivery_kind: deliveryKind || defaultDestinationDeliveryKind(destination) }),
    });
    state.supportWorkflow.selectedDestinationId = destinationId;
    state.supportWorkflow.actionResult = {
      title: `Destination test ${result.delivery_status}`,
      detail:
        result.delivery_status === "delivered"
          ? `Test ${result.delivery_kind} delivery succeeded for ${destination.name}.`
          : `${result.delivery_kind} test failed for ${destination.name}: ${result.error_message || "unknown error"}`,
      tone: result.delivery_status === "delivered" ? "ready" : "overdue",
    };
    queueTelemetryEvent({
      eventName: "notification_test_completed",
      surface: "support",
      route: "#support",
      outcome: result.delivery_status === "delivered" ? "success" : "error",
      detail: result.error_message || null,
      metadata: { destination_id: destinationId, delivery_kind: result.delivery_kind },
    });
    await loadSupportDestinationDeliveries(destinationId, { forceRefresh: true });
    await refreshSupportAndGovernance();
  }

  async function runSupportDestinationRecovery(destinationId, deliveryKind, reason) {
    const destination = (state.supportData?.notification_destinations || []).find((item) => item.id === destinationId);
    if (!destination) {
      flash("Select a destination first.", "error");
      return;
    }
    const result = await apiFetch(`/api/notifications/destinations/${destinationId}/recover`, {
      method: "POST",
      headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
      body: JSON.stringify({
        delivery_kind: deliveryKind || defaultDestinationDeliveryKind(destination),
        reason: reason || "Recovered from CRM support console.",
      }),
    });
    state.supportWorkflow.selectedDestinationId = destinationId;
    state.supportWorkflow.actionResult = {
      title: result.verified ? "Recovery verified" : "Recovery test failed",
      detail: result.verified
        ? `${destination.name} passed recovery verification and is ready for traffic.`
        : `${destination.name} remains quarantined. ${result.destination.recovery.recommended_action}`,
      tone: result.verified ? "ready" : "overdue",
    };
    queueTelemetryEvent({
      eventName: "notification_recovery_completed",
      surface: "support",
      route: "#support",
      outcome: result.verified ? "success" : "error",
      detail: result.verified ? null : result.destination?.recovery?.recommended_action || null,
      metadata: { destination_id: destinationId, delivery_kind: result.delivery?.delivery_kind || deliveryKind || null },
    });
    await loadSupportDestinationDeliveries(destinationId, { forceRefresh: true });
    await refreshSupportAndGovernance();
  }

  async function renderSupportSurface() {
    const renderVersion = nextRenderVersion(state.runtime, "support");
    const startedAt = window.performance.now();
    setSurfaceStatus("support", "stale", "Loading support diagnostics...", true);
    try {
      const support = await apiFetch("/api/support/console", { headers: authHeaders() });
      if (!isCurrentRenderVersion(state.runtime, "support", renderVersion)) {
        return;
      }
      const diagnostics = await loadProductUiDiagnostics();
      if (!isCurrentRenderVersion(state.runtime, "support", renderVersion)) {
        return;
      }
      state.supportData = support;
      state.productUiDiagnostics = diagnostics;
      const summary = buildSupportConsoleSummary(support, {
        selectedDestinationId: state.supportWorkflow.selectedDestinationId,
      });
      const diagnosticsSummary = buildProductUiDiagnosticsSummary(diagnostics);
      if (summary.selectedDestinationId) {
        state.supportWorkflow.selectedDestinationId = summary.selectedDestinationId;
      }
      const destinationFeatureEnabled = notificationDestinationsEnabled();
      let selectedDestinationDeliveries = [];
      if (destinationFeatureEnabled && summary.selectedDestinationId) {
        selectedDestinationDeliveries = await loadSupportDestinationDeliveries(summary.selectedDestinationId);
        if (!isCurrentRenderVersion(state.runtime, "support", renderVersion)) {
          return;
        }
      }
      renderSurfaceHelpPanel("support", {
        destinationCount: summary.destinationRows.length,
      });
      renderMetrics("support-metrics", summary.metrics);

    document.getElementById("support-overview").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Support summary",
        title: "Tenant Overview",
        description: "Current queue pressure, compliance posture, and support priority.",
        badges: [{ label: support.priority || "stable", tone: supportStatusBadge(support) }],
      })}
      <div class="table-like">
        ${summary.overviewRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
      ${renderEmptyState(
        supportStatusBadge(support) === "overdue"
          ? "This tenant currently has support-visible pressure that warrants active monitoring."
          : "Support-facing checks are currently stable for this tenant.",
        supportStatusBadge(support)
      )}
    `;

    document.getElementById("support-checks").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Health checks",
        title: "Config Health Checks",
        description: "Support-focused tenant health and escalation signals.",
        badges: [{ label: `${support.config_health_checks.length} checks`, tone: support.config_health_checks.length ? "ready" : "muted" }],
      })}
      <div class="table-like">
        ${support.config_health_checks
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.key)}</strong><div class="meta">${e(item.summary)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.status)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("support-smoke").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Runtime watch",
        title: "Smoke and Worker Watch",
        description: "Canonical smoke posture plus background worker safety.",
        badges: [
          { label: `${summary.smokeRows.length} smoke signals`, tone: "ready" },
          { label: `${summary.workerRows.length} worker signals`, tone: "stale" },
        ],
      })}
      <div class="table-like">
        ${summary.smokeRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
        ${summary.workerRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("support-smoke-history").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Release validation",
        title: "Smoke History",
        description: "Recent smoke-account login activity for this tenant.",
        badges: [{ label: `${support.smoke_history.length} recent events`, tone: support.smoke_history.length ? "ready" : "muted" }],
      })}
      <div class="table-like">
        ${
          support.smoke_history.length
            ? support.smoke_history
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${e(item.email)}</strong><div class="meta">${e(item.summary || item.reason || "Smoke auth event")} - ${formatDate(item.occurred_at)}</div></div>
                      <div class="${badgeClass(item.status === "success" ? "ready" : "overdue")}">${e(item.status)}</div>
                    </div>
                  `
                )
                .join("")
            : renderEmptyState("No recent smoke-account auth history was found for this tenant.", "muted")
        }
      </div>
    `;

    document.getElementById("support-incidents").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Incident review",
        title: "Recent Incidents",
        description: "Latest queue and maintenance incidents visible to support.",
        badges: [{ label: `${support.recent_incidents.length} recent incidents`, tone: support.recent_incidents.length ? "overdue" : "ready" }],
      })}
      <div class="table-like">
        ${
          support.recent_incidents.length
            ? support.recent_incidents
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${e(item.operation_name)}</strong><div class="meta">${e(item.summary || item.event_type)} - ${formatDate(item.created_at)}</div></div>
                      <div class="${badgeClass(item.incident_kind || "overdue")}">${e(item.incident_kind || item.status)}</div>
                    </div>
                  `
                )
                .join("")
            : renderEmptyState("No recent incidents were detected.", "ready")
        }
      </div>
    `;

    document.getElementById("support-deliveries").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Delivery posture",
        title: summary.selectedDestination ? "Destination Delivery History" : "Delivery Issues",
        description: summary.selectedDestination
          ? `Recent delivery attempts for ${summary.selectedDestination.name}.`
          : "Recent failed alerts, digests, and destination deliveries.",
        badges: [
          {
            label: summary.selectedDestination ? summary.selectedDestination.status : `${support.recent_delivery_issues.length} issues`,
            tone: summary.selectedDestination ? summary.selectedDestination.status : support.recent_delivery_issues.length ? "overdue" : "ready",
          },
        ],
      })}
      <div class="table-like">
        ${
          summary.selectedDestination && destinationFeatureEnabled
            ? selectedDestinationDeliveries.length
              ? selectedDestinationDeliveries
                  .map(
                    (item) => `
                      <div class="table-row">
                        <div><strong>${e(item.delivery_kind)}</strong><div class="meta">${e(
                          item.error_message || "Delivery attempt"
                        )} - ${formatDate(item.delivered_at)}</div></div>
                        <div class="${badgeClass(item.delivery_status === "delivered" ? "ready" : "overdue")}">${e(
                          item.delivery_status
                        )}</div>
                      </div>
                    `
                  )
                  .join("")
              : renderEmptyState("No delivery history was returned for the selected destination.", "muted")
            : support.recent_delivery_issues.length
              ? support.recent_delivery_issues
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${e(item.source_kind)}</strong><div class="meta">${e(item.summary)} - ${formatDate(item.occurred_at)}</div></div>
                      <div class="${badgeClass(item.status === "failed" ? "overdue" : "stale")}">${e(item.destination_type)}</div>
                    </div>
                  `
                )
                .join("")
              : renderEmptyState("No recent delivery failures were detected.", "ready")
        }
      </div>
    `;

    document.getElementById("support-destinations").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Destination recovery",
        title: "Notification Destinations",
        description: "Current tenant destination state, recovery posture, and direct support actions.",
        badges: [{ label: destinationFeatureEnabled ? "available" : "plan locked", tone: destinationFeatureEnabled ? "ready" : "stale" }],
      })}
      ${
        !destinationFeatureEnabled
          ? renderEmptyState(
              "Shared notification destinations are locked on this tenant plan. Support can still review delivery health and incidents from this console.",
              "stale"
            )
          : summary.destinationRows.length
            ? `<div class="stack">
                ${summary.destinationRows
                  .map(
                    (item) => `
                      <article class="detail-card${item.id === summary.selectedDestinationId ? " selected-card" : ""}">
                        <header>
                          <div>
                            <h4>${e(item.name)}</h4>
                            <div class="meta">${e(item.destinationType)} | ${item.recentFailuresTotal} recent failures | ${item.consecutiveLiveFailures} consecutive live failures</div>
                            <div class="meta">${e(item.recommendedAction)}</div>
                          </div>
                          <div class="pill-row">
                            <span class="${badgeClass(item.status === "active" ? "ready" : "stale")}">${e(item.status)}</span>
                            <span class="${badgeClass(
                              item.recoveryStatus === "verified"
                                ? "ready"
                                : item.recoveryStatus === "healthy"
                                  ? "muted"
                                  : "overdue"
                            )}">${e(item.recoveryStatus)}</span>
                          </div>
                        </header>
                        <div class="conversation-actions">
                          <button class="action ghost" type="button" data-support-select-destination="${item.id}">Inspect history</button>
                          <button class="action ghost" type="button" data-support-run-test="${item.id}" data-support-delivery-kind="${item.defaultDeliveryKind}">Send test</button>
                          <button class="action primary" type="button" data-support-run-recovery="${item.id}" data-support-delivery-kind="${item.defaultDeliveryKind}" ${
                            item.canRecover ? "" : "disabled"
                          }>Recover</button>
                        </div>
                      </article>
                    `
                  )
                  .join("")}
              </div>`
            : renderEmptyState("No notification destinations are configured for this tenant.", "muted")
      }
    `;

    document.getElementById("support-telemetry").innerHTML = `
          ${renderSectionIntro({
            eyebrow: "Client diagnostics",
            title: "UI Telemetry",
            description: "Client-side workflow volume, errors, and slow renders observed in the CRM.",
            badges: [{ label: `${diagnosticsSummary.metricCards.length} metrics`, tone: diagnostics.summary.total_events ? "ready" : "muted" }],
          })}
          <div class="grid grid-4">
            ${diagnosticsSummary.metricCards.map((item) => metricTileMarkup(item, e)).join("")}
          </div>
          <div class="grid grid-2">
            <div class="table-like">
              ${diagnosticsSummary.eventRows.length
                ? diagnosticsSummary.eventRows
                    .map(
                      (item) => `
                        <div class="table-row">
                          <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                          <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                        </div>
                      `
                    )
                    .join("")
                : renderEmptyState("No client-side UI events have been recorded for this tenant yet.", "muted")}
            </div>
            <div class="table-like">
              ${diagnosticsSummary.errorRows.length
                ? diagnosticsSummary.errorRows
                    .map(
                      (item) => `
                        <div class="table-row">
                          <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                          <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                        </div>
                      `
                    )
                    .join("")
                : diagnosticsSummary.slowRows
                    .map(
                      (item) => `
                        <div class="table-row">
                          <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                          <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                        </div>
                      `
                    )
                    .join("") || renderEmptyState("No slow or failing UI renders were detected in the current diagnostics window.", "ready")}
            </div>
          </div>
    `;

    document.getElementById("support-actions").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Recovery workflow",
        title: "Recovery Console",
        description: "Run a delivery test or recovery verification for the selected destination.",
        badges: state.supportWorkflow.actionResult ? [{ label: state.supportWorkflow.actionResult.title, tone: state.supportWorkflow.actionResult.tone }] : [],
      })}
      ${
        !destinationFeatureEnabled
          ? renderEmptyState("Destination recovery actions are plan-locked on this tenant.", "stale")
          : summary.selectedDestination
            ? `
              <article class="detail-card">
                <header>
                  <div>
                    <h4>${e(summary.selectedDestination.name)}</h4>
                    <div class="meta">${e(summary.selectedDestination.destination_type)} | last delivery ${
                        e(summary.selectedDestination.last_delivery_status || "n/a")
                      }</div>
                    <div class="meta">${e(summary.selectedDestination.recovery?.recommended_action || "No action required.")}</div>
                  </div>
                  <div class="pill-row">
                    <span class="${badgeClass(
                      summary.selectedDestination.status === "active" ? "ready" : "stale"
                    )}">${e(summary.selectedDestination.status)}</span>
                    <span class="${badgeClass(
                      String(summary.selectedDestination.recovery?.recovery_status || "").toLowerCase() === "verified"
                        ? "ready"
                        : ["quarantined", "recovery_failed"].includes(
                              String(summary.selectedDestination.recovery?.recovery_status || "").toLowerCase()
                            )
                          ? "overdue"
                          : "muted"
                    )}">${e(summary.selectedDestination.recovery?.recovery_status || "healthy")}</span>
                  </div>
                </header>
                <div class="grid grid-2">
                  <label>
                    <span>Destination</span>
                    <select id="support-destination-selector">
                      ${summary.destinationRows
                        .map(
                          (item) =>
                            `<option value="${item.id}" ${item.id === summary.selectedDestinationId ? "selected" : ""}>${e(
                              item.name
                            )} - ${e(item.status)}</option>`
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    <span>Delivery kind</span>
                    <select id="support-delivery-kind">
                      <option value="queue_alert" ${
                        defaultDestinationDeliveryKind(summary.selectedDestination) === "queue_alert" ? "selected" : ""
                      }>Queue alert</option>
                      <option value="queue_digest" ${
                        defaultDestinationDeliveryKind(summary.selectedDestination) === "queue_digest" ? "selected" : ""
                      }>Queue digest</option>
                    </select>
                  </label>
                </div>
                <label>
                  <span>Recovery note</span>
                  <textarea id="support-recovery-reason" placeholder="Explain why this recovery attempt is being made.">Recovered from CRM support console.</textarea>
                </label>
                <div class="conversation-actions">
                  <button class="action ghost" type="button" data-support-run-test="${summary.selectedDestinationId}">Send test</button>
                  <button class="action primary" type="button" data-support-run-recovery="${summary.selectedDestinationId}" ${
                    ["quarantined", "recovery_failed"].includes(
                      String(summary.selectedDestination.recovery?.recovery_status || "").toLowerCase()
                    )
                      ? ""
                      : "disabled"
                  }>Run recovery</button>
                  <button class="action ghost" type="button" data-open-surface="governance">Open governance</button>
                </div>
              </article>
              ${
                state.supportWorkflow.actionResult
                  ? renderRichEmptyState(
                      state.supportWorkflow.actionResult.title,
                      state.supportWorkflow.actionResult.detail,
                      state.supportWorkflow.actionResult.tone
                    )
                  : ""
              }
            `
            : renderEmptyState("Select a notification destination to begin recovery workflow actions.", "muted")
      }
      ${renderSectionIntro({
        eyebrow: "Response guidance",
        title: "Recommended Actions",
        description: "Fastest next checks for support and operations.",
        badges: [{ label: `${summary.actionRows.length} actions`, tone: summary.actionRows.length ? "ready" : "muted" }],
        compact: true,
      })}
      <div class="stack">
        ${summary.actionRows
          .map(
            (item) => `
              <article class="detail-card">
                <header><div><h4>${e(item.label)}</h4><div class="meta">${e(item.detail)}</div></div><div class="${badgeClass(
                  item.status
                )}">${e(item.kind.replaceAll("_", " "))}</div></header>
                <div class="conversation-actions">
                  ${
                    item.destinationId
                      ? `<button class="action ghost" type="button" data-support-select-destination="${item.destinationId}">Focus</button>`
                      : ""
                  }
                  ${
                    item.kind === "recover_destination"
                      ? `<button class="action primary" type="button" data-support-run-recovery="${item.destinationId}">Recover</button>`
                      : item.kind === "inspect_deliveries"
                        ? `<button class="action ghost" type="button" data-support-select-destination="${item.destinationId}">Inspect deliveries</button>`
                        : item.kind === "review_incidents"
                          ? `<button class="action ghost" type="button" data-open-surface="governance">Open governance</button>`
                          : ""
                  }
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    `;
      queueTelemetryEvent({
        eventName: "support_surface_loaded",
        surface: "support",
        route: "#support",
        durationMs: window.performance.now() - startedAt,
        metadata: {
          diagnostics_events: diagnostics.summary.total_events,
          destination_total: support.notification_destinations.length,
        },
      });
      setSurfaceStatus("support", "ready", "Support diagnostics are current.", false);
    } catch (error) {
      queueTelemetryEvent({
        eventName: "support_surface_failed",
        surface: "support",
        route: "#support",
        outcome: "error",
        durationMs: window.performance.now() - startedAt,
        detail: error.message,
      });
      if (isCurrentRenderVersion(state.runtime, "support", renderVersion)) {
        setSurfaceStatus("support", "overdue", "Support diagnostics failed to refresh.", false);
      }
      throw error;
    }
  }

  async function renderGovernanceSurface() {
    const renderVersion = nextRenderVersion(state.runtime, "governance");
    const startedAt = window.performance.now();
    setSurfaceStatus("governance", "stale", "Loading governance posture...", true);
    try {
      const governance = await apiFetch("/api/admin/governance-dashboard", { headers: authHeaders() });
      if (!isCurrentRenderVersion(state.runtime, "governance", renderVersion)) {
        return;
      }
      const diagnostics = state.productUiDiagnostics || (await loadProductUiDiagnostics());
      if (!isCurrentRenderVersion(state.runtime, "governance", renderVersion)) {
        return;
      }
      state.governanceData = governance;
      state.productUiDiagnostics = diagnostics;
      const summary = buildGovernanceDashboardSummary(governance);
      const diagnosticsSummary = buildProductUiDiagnosticsSummary(diagnostics);
      renderSurfaceHelpPanel("governance", {
        incidentCount: governance.recent_incidents.length,
      });
      renderMetrics("governance-metrics", summary.metricCards);

    document.getElementById("governance-posture").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Owner posture",
        title: "Governance Posture",
        description: "Owner-facing status for smoke, worker safety, and tenant risk.",
        badges: [{ label: governanceStatusBadge(governance), tone: governanceStatusBadge(governance) }],
      })}
      <div class="table-like">
        ${summary.postureRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
      ${renderEmptyState(
        governanceStatusBadge(governance) === "overdue"
          ? "Governance checks currently show elevated tenant risk that should be reviewed by an owner."
          : "Governance posture is currently stable.",
        governanceStatusBadge(governance)
      )}
    `;

    document.getElementById("governance-worker").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Worker guardrails",
        title: "Worker Safety",
        description: "Backpressure, failure streaks, and dead-letter visibility.",
        badges: [{ label: `${summary.workerRows.length} worker signals`, tone: summary.workerRows.length ? "stale" : "muted" }],
      })}
      <div class="table-like">
        ${summary.workerRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("governance-checks").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Operational readiness",
        title: "Readiness Checks",
        description: "Signals sourced from support, smoke governance, and current tenant policy state.",
        badges: [{ label: `${(governance.readiness_checks || []).length} checks`, tone: (governance.readiness_checks || []).length ? "ready" : "muted" }],
      })}
      <div class="table-like">
        ${(governance.readiness_checks || [])
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.key)}</strong><div class="meta">${e(item.summary)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.status)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("governance-risk").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Risk controls",
        title: "Risk and Retention",
        description: "Config drift, compliance, and smoke policy signals.",
        badges: [{ label: `${summary.riskRows.length} signals`, tone: summary.riskRows.length ? "stale" : "muted" }],
      })}
      <div class="table-like">
        ${summary.riskRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;

    document.getElementById("governance-incidents").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Incident pressure",
        title: "Recent Incidents",
        description: "Latest queue and maintenance incidents with operational impact.",
        badges: [{ label: `${governance.recent_incidents.length} incidents`, tone: governance.recent_incidents.length ? "overdue" : "ready" }],
      })}
      <div class="table-like">
        ${
          governance.recent_incidents.length
            ? governance.recent_incidents
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${e(item.operation_name)}</strong><div class="meta">${e(item.summary || item.event_type)} - ${formatDate(item.created_at)}</div></div>
                      <div class="${badgeClass(item.incident_kind || "attention_required")}">${e(item.incident_kind || item.status)}</div>
                    </div>
                  `
                )
                .join("")
            : renderEmptyState("No recent incidents were detected.", "ready")
        }
      </div>
    `;

    document.getElementById("governance-config").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Change review",
        title: "Recent Config Changes",
        description: "Latest tenant configuration mutations available for review or rollback.",
        badges: [{ label: `${governance.recent_config_changes.length} changes`, tone: governance.recent_config_changes.length ? "stale" : "muted" }],
      })}
      <div class="table-like">
        ${
          governance.recent_config_changes.length
            ? governance.recent_config_changes
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${e(item.area_label)}</strong><div class="meta">${e(item.summary)} - ${formatDate(item.occurred_at)}</div></div>
                      <div class="${badgeClass(item.rollback_supported ? "ready" : "muted")}">${item.rollback_supported ? "rollback" : "audit only"}</div>
                    </div>
                  `
                )
                .join("")
            : renderEmptyState("No recent tenant configuration changes were detected.", "muted")
        }
      </div>
    `;

    document.getElementById("governance-release").innerHTML = `
          ${renderSectionIntro({
            eyebrow: "Release diagnostics",
            title: "Release and UX Diagnostics",
            description: "Observed build fingerprints, surface mix, and recent client-side failures.",
            badges: [{ label: `${diagnosticsSummary.releaseRows.length} release rows`, tone: diagnosticsSummary.releaseRows.length ? "ready" : "muted" }],
          })}
          <div class="table-like">
            ${diagnosticsSummary.releaseRows
              .map(
                (item) => `
                  <div class="table-row">
                    <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                    <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                  </div>
                `
              )
              .join("")}
            ${diagnosticsSummary.errorRows.length
              ? diagnosticsSummary.errorRows
                  .map(
                    (item) => `
                      <div class="table-row">
                        <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                        <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                      </div>
                    `
                  )
                  .join("")
              : ""}
          </div>
    `;

    document.getElementById("governance-actions").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Response plan",
        title: "Incident Response",
        description: "Fastest next steps to reduce operational risk for this tenant.",
        badges: [{ label: `${summary.actionRows.length} next steps`, tone: summary.actionRows.length ? "ready" : "muted" }],
      })}
      <div class="stack">
        ${summary.actionRows
          .map(
            (item) => `
              <article class="detail-card">
                <header>
                  <div>
                    <h4>${e(item.label)}</h4>
                    <div class="meta">${e(item.detail)}</div>
                  </div>
                  <div class="${badgeClass(item.status)}">${e(item.kind.replaceAll("_", " "))}</div>
                </header>
                <div class="conversation-actions">
                  ${
                    item.kind === "support_destination_recovery"
                      ? '<button class="action primary" type="button" data-open-surface="support">Open support console</button>'
                      : item.kind === "review_smoke_governance" || item.kind === "review_config_audit"
                        ? '<button class="action ghost" type="button" data-open-surface="admin">Open admin</button>'
                        : item.kind === "review_worker_health"
                          ? '<button class="action ghost" type="button" data-refresh-surface="governance">Refresh governance</button>'
                          : ""
                  }
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    `;
      queueTelemetryEvent({
        eventName: "governance_surface_loaded",
        surface: "governance",
        route: "#governance",
        durationMs: window.performance.now() - startedAt,
        metadata: {
          diagnostics_events: diagnostics.summary.total_events,
          incident_total: governance.recent_incidents.length,
        },
      });
      setSurfaceStatus("governance", "ready", "Governance posture is current.", false);
    } catch (error) {
      queueTelemetryEvent({
        eventName: "governance_surface_failed",
        surface: "governance",
        route: "#governance",
        outcome: "error",
        durationMs: window.performance.now() - startedAt,
        detail: error.message,
      });
      if (isCurrentRenderVersion(state.runtime, "governance", renderVersion)) {
        setSurfaceStatus("governance", "overdue", "Governance posture failed to refresh.", false);
      }
      throw error;
    }
  }

  function checkbox(name, label, checked, disabled) {
    return `<label><span>${e(label)}</span><input type="checkbox" name="${e(name)}" ${checked ? "checked" : ""} ${
      disabled ? "disabled" : ""
    } /></label>`;
  }

  function destinationLabel(settings) {
    if (!settings) {
      return "not configured";
    }
    if (settings.notification_destination_name) {
      return `${settings.notification_destination_name} (${settings.notification_destination_status || "unknown"})`;
    }
    if (settings.webhook_url) {
      return "direct webhook";
    }
    return settings.destination_type || "event_log";
  }

  function accessRequestTone(status) {
    if (status === "approved") {
      return "ready";
    }
    if (status === "deferred") {
      return "stale";
    }
    if (status === "rejected") {
      return "overdue";
    }
    return "attention_required";
  }

  function accessRequestLabel(item) {
    return [item.first_name, item.last_name].filter(Boolean).join(" ") || item.full_name || item.email || item.request_id;
  }

  function renderAccessRequestDetails(item) {
    const rows = [
      { label: "Team", value: item.team_size || "n/a", meta: item.company_name || "No company supplied" },
      { label: "Email", value: item.email || "n/a", meta: item.phone_number || "No phone supplied" },
      { label: "Request", value: item.inquiry_label || item.inquiry_type || "Access request", meta: item.source_page || item.request_path || "Public site" },
    ];
    return rows
      .map(
        (row) => `
          <div class="table-row">
            <div><strong>${e(row.label)}</strong><div class="meta">${e(row.meta)}</div></div>
            <div class="${badgeClass("muted")}">${e(row.value)}</div>
          </div>
        `
      )
      .join("");
  }

  function accessRequestActionConfig(surfaceKey) {
    if (surfaceKey === "platform") {
      return {
        refreshPath: "/api/platform/control-center",
        actionBasePath: "/api/platform/access-requests",
        telemetrySurface: "platform",
      };
    }
    return {
      refreshPath: "/api/admin/control-center",
      actionBasePath: "/api/admin/access-requests",
      telemetrySurface: "admin",
    };
  }

  function accessRequestActionLocked(surfaceKey) {
    const role = ((state.context || {}).role || "").toLowerCase();
    if (surfaceKey === "platform") {
      return !["platform_owner", "platform_ops"].includes(role);
    }
    return role !== "tenant_owner";
  }

  function renderAccessRequestActions(item, locked, surfaceKey) {
    if (item.status !== "pending") {
      const summary = item.review_note || item.next_step_summary || "Review complete.";
      const approvedMeta = item.invite_url
        ? `
            <div class="conversation-actions">
              <a class="action ghost" href="${e(item.invite_url)}" target="_blank" rel="noreferrer">Open invite link</a>
              <button class="action ghost" type="button" data-copy-access-invite="${e(item.invite_url)}">Copy invite link</button>
            </div>
          `
        : "";
      return `
        <div class="detail-empty ${accessRequestTone(item.status)}">
          ${e(summary)}
        </div>
        ${approvedMeta}
      `;
    }
      return `
      <form class="stack" data-access-request-form="${e(item.request_id)}" data-access-request-surface="${e(surfaceKey)}">
        <div class="grid grid-2">
          <label><span>Tenant name</span><input name="tenant_name" value="${e(item.suggested_tenant_name || "")}" ${locked ? "disabled" : ""} /></label>
          <label><span>Tenant slug</span><input name="tenant_slug" value="${e(item.suggested_tenant_slug || "")}" ${locked ? "disabled" : ""} /></label>
          <label><span>Plan</span><input name="subscription_plan" value="${e(item.suggested_subscription_plan || "")}" ${locked ? "disabled" : ""} /></label>
          <label><span>Timezone</span><input name="timezone" value="${e(item.suggested_timezone || "")}" ${locked ? "disabled" : ""} /></label>
          <label><span>Invite expires (hours)</span><input type="number" min="1" max="336" name="expires_in_hours" value="72" ${locked ? "disabled" : ""} /></label>
        </div>
        <label><span>Owner review note</span><textarea name="note" placeholder="Capture why this request is being approved, deferred, or rejected." ${locked ? "disabled" : ""}>${e(
          item.review_note || ""
        )}</textarea></label>
        <div class="conversation-actions">
          <button class="action primary" type="button" data-access-request-action="approve" data-access-request-id="${e(item.request_id)}" data-access-request-surface="${e(surfaceKey)}" ${locked ? "disabled" : ""}>Approve and provision</button>
          <button class="action ghost" type="button" data-access-request-action="defer" data-access-request-id="${e(item.request_id)}" data-access-request-surface="${e(surfaceKey)}" ${locked ? "disabled" : ""}>Defer</button>
          <button class="action ghost" type="button" data-access-request-action="reject" data-access-request-id="${e(item.request_id)}" data-access-request-surface="${e(surfaceKey)}" ${locked ? "disabled" : ""}>Reject</button>
        </div>
      </form>
    `;
  }

  function renderAdminAccessRequests(controlCenter, summary) {
    document.getElementById("admin-access-requests").innerHTML = `
      <div class="section-head"><h3>Public Intake</h3><p>Global request review now lives in the Platform workspace so tenant owners only see tenant-specific administration here.</p></div>
      ${renderRichEmptyState(
        "Platform review moved",
        "Sign in with a platform_owner or platform_ops membership to review demo, walkthrough, and free-trial requests from the dedicated Platform surface.",
        "ready"
      )}
    `;
  }

  function renderPlatformSurface(controlCenter) {
    const accessRequests = (controlCenter || {}).access_requests || {};
    const items = accessRequests.items || [];
    const notification = (controlCenter || {}).notifications || {};
    const workspaceSummary = (controlCenter || {}).workspace_summary || {};
    const locked = accessRequestActionLocked("platform");
    renderSurfaceHelpPanel("platform");
    document.getElementById("platform-metrics").innerHTML = renderSummaryCards([
      {
        label: "Pending",
        value: accessRequests.pending_total || 0,
        detail: "awaiting platform review",
        tone: (accessRequests.pending_total || 0) > 0 ? "attention_required" : "ready",
      },
      {
        label: "Approved",
        value: accessRequests.approved_total || 0,
        detail: "provisioned into new tenants",
        tone: "ready",
      },
      {
        label: "Reviewers",
        value: workspaceSummary.active_total || 0,
        detail: `${(workspaceSummary.active_role_counts || {}).platform_owner || 0} owner, ${(workspaceSummary.active_role_counts || {}).platform_ops || 0} ops`,
        tone: "muted",
      },
      {
        label: "Notifications",
        value: notification.configured ? "Live" : "Needs config",
        detail: notification.destination_label || "Slack",
        tone: notification.configured ? "ready" : "stale",
      },
    ]);
    document.getElementById("platform-overview").innerHTML = `
      <div class="section-head"><h3>Platform Workspace</h3><p>Central review for public Vessel demand before we provision customer tenants.</p></div>
      <div class="table-like">
        <div class="table-row">
          <div><strong>Workspace</strong><div class="meta">Dedicated internal ops environment</div></div>
          <div class="${badgeClass("ready")}">${e((controlCenter.platform_profile || {}).slug || "platform-ops")}</div>
        </div>
        <div class="table-row">
          <div><strong>Owner</strong><div class="meta">Configured sole platform owner</div></div>
          <div class="${badgeClass((controlCenter.platform_profile || {}).owner_password_set ? "ready" : "stale")}">${e((controlCenter.platform_profile || {}).owner_email || "not configured")}</div>
        </div>
        <div class="table-row">
          <div><strong>Password bootstrap</strong><div class="meta">Ready once bootstrap-password is completed</div></div>
          <div class="${badgeClass((controlCenter.platform_profile || {}).owner_password_set ? "ready" : "attention_required")}">${(controlCenter.platform_profile || {}).owner_password_set ? "set" : "pending"}</div>
        </div>
      </div>
    `;
    document.getElementById("platform-notifications").innerHTML = `
      <div class="section-head"><h3>Notification Delivery</h3><p>Every new platform request should alert Slack immediately.</p></div>
      <div class="detail-empty ${notification.configured ? "ready" : "stale"}">
        ${e(notification.destination_label || "Slack webhook")}
      </div>
      ${
        (controlCenter.recommended_actions || []).length
          ? `<div class="stack">
              ${(controlCenter.recommended_actions || [])
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>Next step</strong><div class="meta">${e(item)}</div></div>
                      <div class="${badgeClass("attention_required")}">next</div>
                    </div>
                  `
                )
                .join("")}
            </div>`
          : '<div class="detail-empty ready">Platform intake is configured and current.</div>'
      }
    `;
    document.getElementById("platform-requests").innerHTML = `
      <div class="section-head"><h3>Access Requests</h3><p>${e(
        locked
          ? "This membership can view the queue but cannot change request state."
          : "Approve, defer, or reject public requests from one global platform queue."
      )}</p></div>
      ${
        items.length
          ? `<div class="stack">
              ${items
                .map(
                  (item) => `
                    <article class="detail-card">
                      <header>
                        <div>
                          <h4>${e(accessRequestLabel(item))}</h4>
                          <div class="meta">${e(item.inquiry_label || "Access request")} submitted ${e(formatDate(item.submitted_at))}</div>
                        </div>
                        <div class="${badgeClass(accessRequestTone(item.status))}">${e(item.status)}</div>
                      </header>
                      <div class="table-like">
                        ${renderAccessRequestDetails(item)}
                      </div>
                      <div class="detail-empty muted">${e(item.goals || "No goals supplied.")}</div>
                      ${renderAccessRequestActions(item, locked, "platform")}
                    </article>
                  `
                )
                .join("")}
            </div>`
          : renderRichEmptyState(
              "No platform requests yet",
              "New demo, walkthrough, and free-trial requests will appear here for global review and provisioning.",
              "muted"
            )
      }
    `;
    setSurfaceStatus("platform", "ready", "Platform workspace is current.", false);
  }

  function renderOwnerTeamSetup(teamSetup, inviteResult = null) {
    const inviteDisabled = teamSetup.invite.disabled ? "disabled" : "";
    return `
      <section id="owner-team-setup" class="workspace-panel owner-team-setup-panel">
        ${renderSectionIntro({
          eyebrow: "Team setup",
          title: teamSetup.title,
          description: teamSetup.summary,
          badges: [{ label: teamSetup.statusLabel, tone: teamSetup.tone }],
        })}
        <div class="grid grid-3 owner-team-card-grid">
          ${teamSetup.cards.map((item) => metricTileMarkup(item, e)).join("")}
        </div>
        <div class="grid grid-2 owner-team-setup-grid">
          <form id="owner-team-invite-form" data-owner-team-invite-form class="stack owner-team-invite-form">
            <div class="section-head">
              <h4>Create invite link</h4>
              <p>${e(teamSetup.invite.helperText)}</p>
            </div>
            <label>
              <span>Full Name</span>
              <input type="text" name="full_name" placeholder="Josh Schultz" required ${inviteDisabled} />
            </label>
            <label>
              <span>Email</span>
              <input type="email" name="email" placeholder="teammate@example.com" required ${inviteDisabled} />
            </label>
            <div class="grid grid-2 owner-team-form-grid">
              <label>
                <span>Role</span>
                <select name="role" ${inviteDisabled}>
                  ${teamSetup.invite.roleOptions
                    .map(
                      (item) =>
                        `<option value="${e(item.value)}" ${item.value === teamSetup.invite.defaultRole ? "selected" : ""}>${e(item.label)}</option>`
                    )
                    .join("")}
                </select>
              </label>
              <label>
                <span>Expires In Hours</span>
                <input type="number" name="expires_in_hours" min="1" max="720" value="${e(teamSetup.invite.defaultExpiresInHours)}" ${inviteDisabled} />
              </label>
            </div>
            <button class="action primary" type="submit" ${inviteDisabled}>Create Invite</button>
          </form>
          <div class="stack">
            ${
              inviteResult && inviteResult.invite_url
                ? `<div id="owner-team-invite-result" class="detail-empty ready" role="status" aria-live="polite">
                    <strong>Invite ready for ${e(inviteResult.email)}</strong>
                    <div class="meta">${e(inviteResult.full_name)} | ${e(inviteResult.role)} | expires ${e(formatDate(inviteResult.expires_at))}</div>
                    <div class="conversation-actions">
                      <a class="action ghost" href="${e(inviteResult.invite_url)}" target="_blank" rel="noreferrer">Open invite link</a>
                      <button class="action ghost" type="button" data-copy-access-invite="${e(inviteResult.invite_url)}">Copy invite link</button>
                    </div>
                  </div>`
                : renderRichEmptyState(
                    "No invite link created in this session",
                    teamSetup.invite.disabled
                      ? "Tenant owners can create invite links from this panel."
                      : "Create an invite to activate the next teammate without leaving onboarding.",
                    teamSetup.invite.disabled ? "stale" : "muted"
                  )
            }
            <div class="table-like owner-team-roster">
              ${
                teamSetup.rosterRows.length
                  ? teamSetup.rosterRows
                      .map(
                        (item) => `
                          <div class="table-row owner-team-member-row" data-owner-team-member-row="${e(item.membershipId)}">
                            <div>
                              <strong>${e(item.label)}</strong>
                              <div class="meta">${e(item.email)} | ${e(item.value)}</div>
                              <div class="meta">${e(item.actionDetail)}</div>
                            </div>
                            <div class="owner-team-member-management">
                              <span class="${badgeClass(item.tone)}">${e(item.statusLabel)}</span>
                              <form class="owner-team-member-role-form" data-owner-team-member-role-form data-membership-id="${e(
                                item.membershipId
                              )}" data-member-label="${e(item.label)}">
                                <select name="role" ${item.canUpdateRole ? "" : "disabled"} aria-label="Role for ${e(item.label)}">
                                  ${item.roleOptions
                                    .map(
                                      (roleOption) =>
                                        `<option value="${e(roleOption.value)}" ${roleOption.value === item.role ? "selected" : ""} ${
                                          item.isLastActiveOwner && roleOption.value !== "tenant_owner" ? "disabled" : ""
                                        }>${e(roleOption.label)}</option>`
                                    )
                                    .join("")}
                                </select>
                                <button class="action ghost" type="submit" ${item.canUpdateRole ? "" : "disabled"}>Save Role</button>
                              </form>
                              <div class="conversation-actions owner-team-member-actions">
                                ${
                                  item.canDeactivate
                                    ? `<button class="action ghost" type="button" data-owner-team-member-action="deactivate" data-membership-id="${e(
                                        item.membershipId
                                      )}" data-member-label="${e(item.label)}">Deactivate</button>`
                                    : ""
                                }
                                ${
                                  item.canReactivate
                                    ? `<button class="action primary" type="button" data-owner-team-member-action="reactivate" data-membership-id="${e(
                                        item.membershipId
                                      )}" data-member-label="${e(item.label)}">Reactivate</button>`
                                    : ""
                                }
                              </div>
                            </div>
                          </div>
                        `
                      )
                      .join("")
                  : renderRichEmptyState("No workspace members", "Invite an owner and at least one frontline user before go-live.", "overdue")
              }
            </div>
          </div>
        </div>
      </section>
    `;
  }

  function renderAdminSurface(controlCenter) {
    state.adminFormDirty = false;
    const summary = buildAdminControlSummary(controlCenter, { role: (state.context || {}).role });
    const launchReadiness = buildOwnerLaunchReadiness(controlCenter, { role: (state.context || {}).role });
    const commandCenter = buildOwnerCommandCenter(controlCenter, { role: (state.context || {}).role });
    const firstRunFlow = buildOwnerFirstRunSetupFlow(controlCenter, { role: (state.context || {}).role });
    const onboarding = buildOnboardingJourney(controlCenter, { role: (state.context || {}).role });
    const teamSetup = buildOwnerTeamSetup(controlCenter, {
      role: (state.context || {}).role,
      membershipId: (state.context || {}).membership_id,
    });
    const releasePack = buildCustomerReleasePack(controlCenter, { role: (state.context || {}).role });
    renderSurfaceHelpPanel("admin", {
      onboardingStatus: (controlCenter.onboarding || {}).status,
    });
    const entitlementRows = (controlCenter.entitlements.features || [])
      .map(
        (item) => `
          <div class="table-row">
            <div><strong>${e(item.key)}</strong><div class="meta">${e(item.description)}</div></div>
            <div class="${badgeClass(item.enabled ? "ready" : "stale")}">${item.enabled ? "enabled" : "locked"}</div>
          </div>
        `
      )
      .join("");
    document.getElementById("admin-metrics").innerHTML = `
      <article class="detail-card owner-command-center ${e(commandCenter.tone)}">
        ${renderSectionIntro({
          eyebrow: "Owner command center",
          title: "Launch Command Center",
          description: commandCenter.detail,
          badges: [{ label: commandCenter.headline, tone: commandCenter.tone }],
          actionsMarkup: `<button class="action primary" type="button" data-onboarding-focus-target="${e(
            commandCenter.primaryAction.targetId
          )}">${e(commandCenter.primaryAction.actionLabel)}</button>`,
        })}
        <div class="grid grid-4 owner-command-card-grid">
          ${commandCenter.commandCards.map((item) => metricTileMarkup(item, e)).join("")}
        </div>
        <section class="owner-first-run-flow">
          <div class="section-head">
            <div>
              <h3>First-Run Setup</h3>
              <p>${e(firstRunFlow.summary)}</p>
            </div>
            <span class="${badgeClass(firstRunFlow.tone)}">${e(firstRunFlow.statusLabel)}</span>
          </div>
          <div class="owner-progress-track" aria-label="First-run setup progress">
            <span style="width: ${firstRunFlow.progressPercent}%"></span>
          </div>
          <div class="owner-first-run-steps">
            ${firstRunFlow.steps
              .map(
                (item, index) => `
                  <article class="owner-first-run-step ${e(item.status)}">
                    <div class="owner-step-index">${index + 1}</div>
                    <div>
                      <h4>${e(item.label)}</h4>
                      <p class="meta">${e(item.detail)}</p>
                      <button class="action ${item.status === "active" ? "primary" : "ghost"}" type="button" data-onboarding-focus-target="${e(
                        item.targetId
                      )}">${e(item.actionLabel)}</button>
                    </div>
                    <span class="${badgeClass(item.tone)}">${e(item.status)}</span>
                  </article>
                `
              )
              .join("")}
          </div>
        </section>
        <div class="grid grid-2 owner-command-layout">
          <div class="stack">
            ${commandCenter.actionCards
              .map(
                (item) => `
                  <article class="detail-card owner-command-action ${e(item.tone)}">
                    <header>
                      <div>
                        <h4>${e(item.label)}</h4>
                        <div class="meta">${e(item.detail)}</div>
                      </div>
                      <span class="${badgeClass(item.tone)}">${e(item.tone)}</span>
                    </header>
                    <div class="conversation-actions">
                      <button class="action ${item.tone === "ready" ? "ghost" : "primary"}" type="button" data-onboarding-focus-target="${e(
                        item.targetId
                      )}">${e(item.actionLabel)}</button>
                    </div>
                  </article>
                `
              )
              .join("")}
          </div>
          <div class="table-like owner-command-links">
            ${commandCenter.secondaryActions
              .map(
                (item) => `
                  <div class="table-row">
                    <div><strong>${e(item.label)}</strong><div class="meta">${e(item.detail)}</div></div>
                    <button class="action ghost" type="button" data-onboarding-focus-target="${e(item.targetId)}">${e(item.actionLabel)}</button>
                  </div>
                `
              )
              .join("")}
          </div>
        </div>
      </article>
      <div class="owner-metric-strip">
        ${summary.metrics
          .map(
            (item) => `
              <article class="metric-card owner-summary-metric">
                <div class="eyebrow">${e(item.label)}</div>
                <strong>${e(item.value)}</strong>
                <p class="meta">${e(item.detail)}</p>
              </article>
            `
          )
          .join("")}
      </div>
    `;
    document.getElementById("admin-profile").innerHTML = `
      <div class="section-head"><h3>Tenant Profile</h3><p>Plan, timezone, and workspace footprint.</p></div>
      <div class="table-like">
        ${summary.profileRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
        ${entitlementRows}
      </div>
    `;
    document.getElementById("admin-governance").innerHTML = `
      <div class="section-head"><h3>Smoke Governance</h3><p>Canonical smoke posture and current rotation readiness.</p></div>
      <div class="table-like">
        ${summary.smokeRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
      <div class="detail-empty ${summary.locked ? "stale" : "ready"}">
        ${summary.locked ? "Managers can review smoke posture here, but only tenant owners can rotate and save smoke governance notes." : "Tenant owners can use this surface with /api/admin/smoke-governance for canonical smoke-account maintenance."}
      </div>
    `;
    document.getElementById("admin-readiness").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Owner rollout",
        title: "Owner Launch Readiness",
        description: launchReadiness.summary,
        badges: [{ label: launchReadiness.statusLabel, tone: launchReadiness.tone }],
      })}
      <div class="grid grid-4 owner-launch-card-grid">
        ${launchReadiness.launchCards.map((item) => metricTileMarkup(item, e)).join("")}
      </div>
      <div class="grid grid-2 owner-launch-readiness-grid">
        <div class="table-like">
          ${launchReadiness.checklistRows
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                  <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                </div>
              `
            )
            .join("")}
        </div>
        <div class="stack">
          ${launchReadiness.nextActions
            .map(
              (item) => `
                <article class="detail-card owner-launch-action ${e(item.tone)}">
                  <header>
                    <div>
                      <h4>${e(item.label)}</h4>
                      <div class="meta">${e(item.detail)}</div>
                    </div>
                    <div class="${badgeClass(item.tone)}">${e(item.tone)}</div>
                  </header>
                  <div class="conversation-actions">
                    <button class="action ${item.tone === "ready" ? "ghost" : "primary"}" type="button" data-onboarding-focus-target="${e(
                      item.targetId
                    )}">${e(item.actionLabel)}</button>
                  </div>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
      ${
        launchReadiness.readinessIssues.length
          ? `<div class="table-like owner-readiness-issues">
              ${launchReadiness.readinessIssues
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                      <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                    </div>
                  `
                )
                .join("")}
            </div>`
          : renderEmptyState("No active owner readiness issues were detected.", "ready")
      }
      <details class="owner-detail-drawer">
        <summary>
          <span><strong>Messaging Go-Live Readiness</strong><span class="meta">${e(summary.messagingGoLive.summary)}</span></span>
          <span class="${badgeClass(summary.messagingGoLive.tone)}">${e(summary.messagingGoLive.statusLabel)}</span>
        </summary>
        <div class="table-like">
          ${summary.messagingGoLive.rows
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                  <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                </div>
              `
            )
            .join("")}
        </div>
      </details>
      <details class="owner-detail-drawer">
        <summary>
          <span><strong>Readiness Checks</strong><span class="meta">Current configuration and operating health.</span></span>
          <span class="${badgeClass(launchReadiness.readinessIssues.length ? "stale" : "ready")}">${
            launchReadiness.readinessIssues.length ? `${launchReadiness.readinessIssues.length} review` : "clear"
          }</span>
        </summary>
        <div class="table-like">
          ${controlCenter.readiness_checks
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.key)}</strong><div class="meta">${e(item.summary)}</div></div>
                  <div class="${badgeClass(item.status)}">${e(item.status)}</div>
                </div>
              `
            )
            .join("")}
        </div>
      </details>
    `;
    document.getElementById("admin-policy").innerHTML = `
      <div class="section-head"><h3>Queue Policy and Automation</h3><p>Ownership guardrails, pull capacity, and system actions.</p></div>
      <div class="grid grid-2">
        <div class="table-like">
          ${summary.queueRows
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                  <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                </div>
              `
            )
            .join("")}
        </div>
        <div class="table-like">
          ${summary.automationRows
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                  <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                </div>
              `
            )
            .join("")}
        </div>
      </div>
    `;
    document.getElementById("admin-delivery").innerHTML = `
      <div class="section-head"><h3>Alerts and Digests</h3><p>Delivery posture, thresholds, and cadence at a glance.</p></div>
      <div class="table-like">
        ${summary.deliveryRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
      <div class="detail-empty ${summary.notificationLocked ? "stale" : "ready"}">
        ${
          summary.notificationLocked
            ? "Shared notification destinations are plan-locked on this tenant. Alert and digest settings stay bound to event-log or direct webhook defaults."
            : "Shared notification destinations are available for premium alert and digest routing."
        }
      </div>
    `;
    document.getElementById("admin-maintenance").innerHTML = `
      <div class="section-head"><h3>Maintenance Watch</h3><p>Recent scheduled queue tasks and rollout-safe worker activity.</p></div>
      <div class="table-like">
        ${summary.maintenanceRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.value)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;
    renderAdminAccessRequests(controlCenter, summary);
    document.getElementById("admin-release-pack").innerHTML = `
      <div class="section-head"><h3>Customer Rollout Pack</h3><p>Final launch readiness, role handoff, and release materials for the CRM rollout.</p></div>
      <article class="detail-card ${releasePack.launchTone}">
        <header>
          <div>
            <h4>${e(releasePack.headline)}</h4>
            <div class="meta">${e(releasePack.headlineDetail)}</div>
          </div>
          <div class="${badgeClass(releasePack.launchTone)}">${e(releasePack.launchTone)}</div>
        </header>
      </article>
      <div class="grid grid-2">
        <div class="table-like">
          ${releasePack.checklistRows
            .map(
              (item) => `
                <div class="table-row">
                  <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                  <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                </div>
              `
            )
            .join("")}
        </div>
        <div class="stack">
          ${releasePack.docsRows
            .map(
              (item) => `
                <article class="detail-card">
                  <header>
                    <div><h4>${e(item.title)}</h4><div class="meta">${e(item.detail)}</div></div>
                    <div class="${badgeClass(item.status)}">${e(item.status)}</div>
                  </header>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
      <div class="grid grid-2">
        <section class="detail-card">
          <div class="section-head"><h4>Browser validation</h4><p>Customer rollout browser matrix confirmed for the CRM shell.</p></div>
          <div class="table-like">
            ${releasePack.browserRows
              .map(
                (item) => `
                  <div class="table-row">
                    <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                    <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                  </div>
                `
              )
              .join("")}
          </div>
        </section>
        <section class="detail-card">
          <div class="section-head"><h4>Deployment routine</h4><p>Required release steps before declaring the CRM rollout complete.</p></div>
          <div class="table-like">
            ${releasePack.deploymentRows
              .map(
                (item) => `
                  <div class="table-row">
                    <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                    <div class="${badgeClass(item.status)}">${e(item.value)}</div>
                  </div>
                `
              )
              .join("")}
          </div>
        </section>
      </div>
      <div class="grid grid-3">
        ${releasePack.roleCards
          .map(
            (item) => `
              <article class="detail-card${item.active ? " selected-card" : ""}">
                <header>
                  <div><h4>${e(item.title)}</h4><div class="meta">${e(item.summary)}</div></div>
                  <div class="${badgeClass(item.active ? "ready" : "muted")}">${item.active ? "current focus" : e(item.key)}</div>
                </header>
                <ul class="meta bullet-list">
                  ${item.bullets.map((bullet) => `<li>${e(bullet)}</li>`).join("")}
                </ul>
              </article>
            `
          )
          .join("")}
      </div>
    `;
    document.getElementById("admin-onboarding-journey").innerHTML = `
      <div class="section-head">
        <h3>Guided Onboarding</h3>
        <p>${e(controlCenter.onboarding.operating_style_label)} flow. ${e(controlCenter.onboarding.next_step_summary)}</p>
      </div>
      <article class="detail-card onboarding-hero ${onboarding.spotlight.tone}">
        <header>
          <div>
            <h4>${e(onboarding.spotlight.title)}</h4>
            <div class="meta">${e(onboarding.spotlight.summary)}</div>
          </div>
          <div class="${badgeClass(onboarding.spotlight.tone)}">${e(controlCenter.onboarding.status)}</div>
        </header>
        <div class="grid grid-3 onboarding-metrics">
          <article class="metric-card">
            <div class="eyebrow">Status</div>
            <strong>${e(controlCenter.onboarding.status)}</strong>
            <p class="meta">${controlCenter.onboarding.completed_steps}/${controlCenter.onboarding.total_steps} completed</p>
          </article>
          <article class="metric-card">
            <div class="eyebrow">Actionable</div>
            <strong>${e(controlCenter.onboarding.actionable_steps)}</strong>
            <p class="meta">${e(controlCenter.onboarding.blocked_steps)} blocked by current plan</p>
          </article>
          <article class="metric-card">
            <div class="eyebrow">Style</div>
            <strong>${e(controlCenter.onboarding.operating_style_label)}</strong>
            <p class="meta">${e(controlCenter.onboarding.tenant_slug)}</p>
          </article>
        </div>
        <div class="conversation-actions">
          <button
            class="action primary"
            type="button"
            data-onboarding-focus-target="${e(onboarding.spotlight.targetId)}"
          >${e(onboarding.spotlight.actionLabel)}</button>
          <a class="action ghost" href="${e(onboarding.helperCards.product_ui_url || "/app")}" target="_blank" rel="noreferrer">Open product UI</a>
          <a class="action ghost" href="${e(onboarding.helperCards.health_url || "/health")}" target="_blank" rel="noreferrer">Open /health</a>
        </div>
      </article>
      <div class="detail-empty ${onboarding.locked ? "stale" : "ready"}">
        ${
          onboarding.locked
            ? "Managers can review onboarding coverage and recommended defaults here, but only tenant owners can apply presets and save the owner settings."
            : "Apply a recommended operating style, follow the checklist actions, then save the owner settings before go-live."
        }
      </div>
    `;
    document.getElementById("admin-onboarding-steps").innerHTML = `
      <div class="section-head"><h3>Checklist</h3><p>Actionable setup steps with the fastest in-product next action.</p></div>
      <div class="stack">
        ${onboarding.stepCards
          .map(
            (item) => `
              <article class="detail-card onboarding-step-card">
                <header>
                  <div>
                    <h4>${e(item.title)}</h4>
                    <div class="meta">${e(item.summary)}</div>
                  </div>
                  <div class="${badgeClass(item.tone)}">${e(item.status)}</div>
                </header>
                <div class="conversation-actions">
                  <button
                    class="action ${item.actionable ? "primary" : "ghost"}"
                    type="button"
                    data-onboarding-focus-target="${e(item.action.targetId)}"
                  >${e(item.action.label)}</button>
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    `;
    document.getElementById("admin-onboarding-roster").innerHTML = `
      ${renderOwnerTeamSetup(teamSetup, state.adminInviteResult)}
      <div class="section-head"><h3>Workspace Coverage</h3><p>Owner and frontline coverage visible without leaving onboarding.</p></div>
      <div class="table-like owner-coverage-table">
        ${onboarding.rosterRows
          .map(
            (item) => `
              <div class="table-row">
                <div><strong>${e(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                <div class="${badgeClass(item.status)}">${e(item.status)}</div>
              </div>
            `
          )
          .join("")}
      </div>
    `;
    document.getElementById("admin-onboarding-presets").innerHTML = `
      <div class="section-head"><h3>Recommended Defaults</h3><p>Load an operating style directly into the owner settings form, then review and save.</p></div>
      <div class="stack">
        ${onboarding.recommendationCards
          .map(
            (item) => `
              <article class="detail-card onboarding-preset-card">
                <header>
                  <div>
                    <h4>${e(item.title)}</h4>
                    <div class="meta">${e(item.summary)}</div>
                  </div>
                  <div class="${badgeClass(item.isCurrent ? "ready" : "muted")}">${item.isCurrent ? "current" : e(item.key)}</div>
                </header>
                <div class="stack meta">
                  <div>${e(item.queueSummary)}</div>
                  <div>${e(item.automationSummary)}</div>
                  <div>${e(item.alertSummary)}</div>
                  <div>${e(item.digestSummary)}</div>
                </div>
                <div class="conversation-actions">
                  <button
                    class="action ${onboarding.locked ? "ghost" : "primary"}"
                    type="button"
                    data-onboarding-apply-default="${e(item.key)}"
                    ${onboarding.locked ? "disabled" : ""}
                  >Apply to owner settings</button>
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    `;
    document.getElementById("admin-onboarding-helpers").innerHTML = `
      <div class="section-head"><h3>Go-Live Helpers</h3><p>Health checks and smoke commands for final readiness validation.</p></div>
      <div class="stack">
        ${(onboarding.helperCards.commands || [])
          .map(
            (item, index) => `
              <article class="detail-card">
                <header><div><h4>${e(item.label)}</h4><div class="meta">${e(item.description)}</div></div></header>
                <pre class="code-block">${e(item.command)}</pre>
                <div class="conversation-actions">
                  <button class="action ghost" type="button" data-onboarding-copy-command="${index}">Copy command</button>
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    `;
    document.getElementById("admin-form").innerHTML = `
      <div class="grid grid-2 admin-settings-grid">
        <section class="detail-card form-section" id="admin-settings-tenant-section">
          <header><div><h4>Tenant and queue policy</h4><div class="meta">Plan, timezone, pull limits, and ownership rules.</div></div></header>
          <div class="grid grid-2">
            <label><span>Tenant Name</span><input name="tenant_name" value="${e(controlCenter.tenant_profile.name)}" ${summary.locked ? "disabled" : ""} /></label>
            <label><span>Subscription Plan</span><input name="subscription_plan" value="${e(controlCenter.tenant_profile.subscription_plan || "")}" ${summary.locked ? "disabled" : ""} /></label>
            <label><span>Timezone</span><input name="timezone" value="${e(controlCenter.tenant_profile.timezone)}" ${summary.locked ? "disabled" : ""} /></label>
            <label><span>Default Pull Limit</span><input type="number" name="default_pull_limit" min="1" max="500" value="${controlCenter.queue_settings.default_pull_limit}" ${summary.locked ? "disabled" : ""} /></label>
            <label><span>Max Pull Limit</span><input type="number" name="max_pull_limit" min="1" max="500" value="${controlCenter.queue_settings.max_pull_limit}" ${summary.locked ? "disabled" : ""} /></label>
            <label><span>Auto-close inactive hours</span><input type="number" name="auto_close_after_inactive_hours" min="1" max="720" value="${e(
              controlCenter.queue_settings.auto_close_after_inactive_hours || ""
            )}" ${summary.locked ? "disabled" : ""} /></label>
          </div>
          <div class="grid grid-2">
            ${checkbox("operator_pull_enabled", "Operator pull enabled", controlCenter.queue_settings.operator_pull_enabled, summary.locked)}
            ${checkbox("manager_dispatch_enabled", "Manager dispatch enabled", controlCenter.queue_settings.manager_dispatch_enabled, summary.locked)}
            ${checkbox("manager_reassign_enabled", "Manager reassign enabled", controlCenter.queue_settings.manager_reassign_enabled, summary.locked)}
            ${checkbox("manager_release_enabled", "Manager release enabled", controlCenter.queue_settings.manager_release_enabled, summary.locked)}
          </div>
        </section>
        <section class="detail-card form-section" id="admin-settings-automation-section">
          <header><div><h4>Automation controls</h4><div class="meta">Batch sizing and guardrails for queue automation.</div></div></header>
          <div class="grid grid-2">
            <label><span>Auto-assign batch size</span><input type="number" name="auto_assign_batch_size" min="1" max="500" value="${controlCenter.automation_settings.auto_assign_batch_size}" ${summary.automationLocked ? "disabled" : ""} /></label>
            <label><span>Max open per user</span><input type="number" name="auto_assign_max_open_per_user" min="1" max="500" value="${e(
              controlCenter.automation_settings.auto_assign_max_open_per_user || ""
            )}" ${summary.automationLocked ? "disabled" : ""} /></label>
            <label><span>Overdue escalation batch</span><input type="number" name="auto_escalate_overdue_batch_size" min="1" max="500" value="${controlCenter.automation_settings.auto_escalate_overdue_batch_size}" ${summary.automationLocked ? "disabled" : ""} /></label>
          </div>
          <div class="grid grid-2">
            ${checkbox("auto_assign_enabled", "Auto assign enabled", controlCenter.automation_settings.auto_assign_enabled, summary.automationLocked)}
            ${checkbox(
              "auto_escalate_overdue_enabled",
              "Auto escalate overdue",
              controlCenter.automation_settings.auto_escalate_overdue_enabled,
              summary.automationLocked
            )}
          </div>
          <div class="detail-empty ${summary.automationLocked ? "stale" : "ready"}">${
            summary.automationLocked ? "Automation is locked by role or plan." : "Automation will follow the tenant queue policy and capacity thresholds saved here."
          }</div>
        </section>
      </div>
      <div class="grid grid-2 admin-settings-grid">
        <section class="detail-card form-section" id="admin-settings-alerts-section">
          <header><div><h4>Alert policy</h4><div class="meta">Threshold-based interruption rules for overdue queue states.</div></div></header>
          <div class="grid grid-2">
            <label><span>Cooldown minutes</span><input type="number" name="cooldown_minutes" min="0" max="1440" value="${controlCenter.alert_settings.cooldown_minutes}" ${summary.alertsLocked ? "disabled" : ""} /></label>
            <label><span>Overdue total threshold</span><input type="number" name="overdue_total_threshold" min="1" max="100000" value="${e(
              controlCenter.alert_settings.overdue_total_threshold || ""
            )}" ${summary.alertsLocked ? "disabled" : ""} /></label>
            <label><span>Overdue unassigned threshold</span><input type="number" name="overdue_unassigned_threshold" min="1" max="100000" value="${e(
              controlCenter.alert_settings.overdue_unassigned_threshold || ""
            )}" ${summary.alertsLocked ? "disabled" : ""} /></label>
            <label><span>First-response overdue threshold</span><input type="number" name="first_response_overdue_threshold" min="1" max="100000" value="${e(
              controlCenter.alert_settings.first_response_overdue_threshold || ""
            )}" ${summary.alertsLocked ? "disabled" : ""} /></label>
            <label><span>Automation-skipped threshold</span><input type="number" name="automation_skipped_overdue_threshold" min="1" max="100000" value="${e(
              controlCenter.alert_settings.automation_skipped_overdue_threshold || ""
            )}" ${summary.alertsLocked ? "disabled" : ""} /></label>
          </div>
          <div class="stack">
            ${checkbox("alerts_enabled", "Queue alerts enabled", controlCenter.alert_settings.enabled, summary.alertsLocked)}
          </div>
          <div class="detail-empty ${summary.alertsLocked ? "stale" : "ready"}">Destination: ${e(destinationLabel(controlCenter.alert_settings))}</div>
        </section>
        <section class="detail-card form-section" id="admin-settings-digests-section">
          <header><div><h4>Digest policy</h4><div class="meta">Cadence and delivery window for operational summaries.</div></div></header>
          <div class="grid grid-2">
            <label><span>Delivery hour UTC</span><input type="number" name="delivery_hour_utc" min="0" max="23" value="${controlCenter.digest_settings.delivery_hour_utc}" ${summary.digestsLocked ? "disabled" : ""} /></label>
          </div>
          <div class="grid grid-2">
            ${checkbox("digest_enabled", "Digest delivery enabled", controlCenter.digest_settings.enabled, summary.digestsLocked)}
            ${checkbox("daily_enabled", "Daily digest", controlCenter.digest_settings.daily_enabled, summary.digestsLocked)}
            ${checkbox("weekly_enabled", "Weekly digest", controlCenter.digest_settings.weekly_enabled, summary.digestsLocked)}
          </div>
          <div class="detail-empty ${summary.digestsLocked ? "stale" : "ready"}">Destination: ${e(destinationLabel(controlCenter.digest_settings))}</div>
        </section>
      </div>
      <div class="button-row">
        <div class="meta">${summary.locked ? "Managers have read-only access to the admin control center." : "Saving writes the owner-facing tenant control center configuration."}</div>
        <button class="action primary" type="submit" ${summary.locked ? "disabled" : ""}>Save Control Center</button>
      </div>
    `;
    document.getElementById("admin-form").onsubmit = saveAdminForm;
    if (state.adminFocus.targetId) {
      applyAdminFocusMarker(state.adminFocus.targetId, { scroll: false, reschedule: false });
    }
  }

  async function renderAdminReporting() {
    const renderVersion = nextRenderVersion(state.runtime, "admin-reporting");
    setSurfaceStatus("admin", "stale", "Loading admin reporting...", true);
    if (!state.bootstrap || !state.bootstrap.admin_control_center) {
      return;
    }
    try {
      const filters = state.adminReporting.filters || defaultAdminReportingFilters();
      const [report, actions] = await Promise.all([
        apiFetch(buildAdminReportingQuery(filters), { headers: authHeaders() }),
        apiFetch("/api/conversations/queue/actions?limit=12", { headers: authHeaders() }),
      ]);
      if (!isCurrentRenderVersion(state.runtime, "admin-reporting", renderVersion)) {
        return;
      }
      state.adminReporting.report = report;
      state.adminReporting.actions = actions;
      const outcomeBoard = buildDispositionBoard(report, actions);
      const reportWorkspace = buildOperationalReportWorkspace(report);
      renderMetrics("admin-reporting-metrics", summarizeOperationalReport(report));
    document.getElementById("admin-reporting-controls").innerHTML = `
      <form class="detail-card filter-card" id="admin-reporting-form">
        <header>
          <div><h4>Operational reporting</h4><div class="meta">Daily and weekly queue performance without leaving the owner workspace.</div></div>
          <div class="pill-row">
            <span class="${badgeClass(report.interval === "week" ? "ready" : "stale")}">${e(report.interval)}</span>
            <span class="${badgeClass((report.series || []).length > 0 ? "ready" : "muted")}">${(report.series || []).length} buckets</span>
          </div>
        </header>
        <div class="grid grid-3">
          <label>
            <span>Interval</span>
            <select name="interval">
              <option value="day" ${filters.interval === "day" ? "selected" : ""}>Day</option>
              <option value="week" ${filters.interval === "week" ? "selected" : ""}>Week</option>
            </select>
          </label>
          <label>
            <span>Status</span>
            <select name="status">
              <option value="" ${filters.status === "" ? "selected" : ""}>Any</option>
              <option value="open" ${filters.status === "open" ? "selected" : ""}>Open</option>
              <option value="closed" ${filters.status === "closed" ? "selected" : ""}>Closed</option>
            </select>
          </label>
          <label>
            <span>Assigned user</span>
            <select name="assigned_user_id">
              <option value="">Any assignee</option>
              ${inboxAssigneeOptions(false)}
            </select>
          </label>
          <label><span>Date from</span><input type="date" name="date_from" value="${e(filters.date_from)}" /></label>
          <label><span>Date to</span><input type="date" name="date_to" value="${e(filters.date_to)}" /></label>
        </div>
        <div class="button-row">
          <div class="meta">Reports use the same operational-report API as the production smoke and respect retention windows.</div>
          <div class="button-row">
            <button class="action primary" type="submit">Run Report</button>
            <button class="action ghost" type="button" data-reset-admin-reporting="true">Reset Report</button>
          </div>
        </div>
      </form>
      <div class="detail-summary-grid detail-summary-grid-inline" id="admin-reporting-summary">
        ${renderSummaryCards(reportWorkspace.summaryCards)}
      </div>
    `;
    const assigneeSelect = document.querySelector('#admin-reporting-form select[name="assigned_user_id"]');
    if (assigneeSelect) {
      assigneeSelect.value = filters.assigned_user_id || "";
    }
    document.getElementById("admin-reporting-series").innerHTML = `
      ${renderSectionIntro({
        eyebrow: "Performance readout",
        title: "Reporting Readout",
        description: `Inbound volume, latency, and outcome mix by ${e(report.interval)} bucket.`,
        badges: [{ label: `${reportWorkspace.seriesRows.length} buckets`, tone: reportWorkspace.seriesRows.length ? "ready" : "muted" }],
      })}
      <div class="grid grid-2">
        <div class="table-like">
          ${renderWorkspaceRows(reportWorkspace.insightRows)}
        </div>
        <div class="table-like">
          ${reportWorkspace.seriesRows.length
            ? reportWorkspace.seriesRows
                .map(
                  (item) => `
                    <div class="table-row">
                      <div><strong>${formatDate(item.label)}</strong><div class="meta">${e(item.meta)}</div></div>
                      <div class="${badgeClass(item.tone)}">${e(item.value)}</div>
                    </div>
                  `
                )
                .join("")
                : renderGuidedEmptyState(
                    buildReportingEmptyState().title,
                    buildReportingEmptyState().detail,
                    buildReportingEmptyState().tone,
                    buildReportingEmptyState().actions
                  )}
        </div>
      </div>
    `;
      document.getElementById("admin-outcomes").innerHTML = renderDispositionBoard(outcomeBoard, {
        title: "Disposition board",
        description: "Outcome distribution for the current reporting window with recent disposition saves from the live queue feed.",
      });
      setSurfaceStatus("admin", "ready", "Admin surfaces are current.", false);
    } catch (error) {
      if (isCurrentRenderVersion(state.runtime, "admin-reporting", renderVersion)) {
        setSurfaceStatus("admin", "overdue", "Admin reporting failed to refresh.", false);
      }
      throw error;
    }
  }

  function inboxFilterValues(form) {
    const data = new FormData(form);
    return {
      search: data.get("search") || "",
      status: data.get("status") || "open",
      aging_bucket: data.get("aging_bucket") || "",
      assigned_user_id: data.get("assigned_user_id") || "",
      date_from: data.get("date_from") || "",
      date_to: data.get("date_to") || "",
    };
  }

  async function rerenderInboxSurface(surfaceKey) {
    if (surfaceKey === "manager" && hasBootstrapSection("manager")) {
      await renderManagerSurface();
      return;
    }
    if (surfaceKey === "operator" && hasBootstrapSection("operator")) {
      await renderOperatorSurface();
    }
  }

  async function renderSurfaces() {
    if (!state.bootstrap) {
      return;
    }
    const tasks = [];
    if (hasBootstrapSection("platform")) {
      setSurfaceStatus("platform", "stale", "Loading platform workspace...", true);
      tasks.push(
        apiFetch("/api/platform/control-center", { headers: authHeaders() }).then((payload) => {
          state.bootstrap.platform_control_center = payload;
          state.platformData = payload;
          renderPlatformSurface(payload);
        })
      );
    }
    if (hasBootstrapSection("manager")) {
      tasks.push(renderManagerSurface());
    }
    if (hasBootstrapSection("operator")) {
      tasks.push(renderOperatorSurface());
    }
    if (hasBootstrapSection("admin")) {
      if (state.adminFormDirty) {
        setSurfaceStatus("admin", "stale", "Live sync paused while owner edits are unsaved.", false);
      } else {
        setSurfaceStatus("admin", "stale", "Loading admin surfaces...", true);
        tasks.push(
          ensureAdminControlCenter().then(async (payload) => {
            if (!payload) {
              return;
            }
            renderAdminSurface(payload);
            await renderAdminReporting();
          })
        );
      }
    }
    if (hasBootstrapSection("support")) {
      tasks.push(renderSupportSurface());
    }
    if (hasBootstrapSection("governance")) {
      tasks.push(renderGovernanceSurface());
    }
    await Promise.all(tasks);
  }

  async function saveAdminForm(event) {
    event.preventDefault();
    const data = new FormData(event.target);
    const controlCenter = state.bootstrap.admin_control_center;
    const body = buildAdminUpdatePayload(controlCenter, data);
    const submitButton = event.target.querySelector('button[type="submit"]');
    await runPendingAction("save-admin-form", submitButton, "Saving...", async function () {
      try {
        state.bootstrap.admin_control_center = await apiFetch("/api/admin/control-center", {
          method: "PUT",
          headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
          body: JSON.stringify(body),
        });
        state.adminFormDirty = false;
        renderAdminSurface(state.bootstrap.admin_control_center);
        queueTelemetryEvent({
          eventName: "admin_control_center_saved",
          surface: "admin",
          route: "#admin",
          metadata: { sections: Object.keys(body) },
        });
        flash("Control center saved.", "success");
        await loadBootstrap();
      } catch (error) {
        queueTelemetryEvent({
          eventName: "admin_control_center_save_failed",
          surface: "admin",
          route: "#admin",
          outcome: "error",
          detail: error.message,
        });
        flash(error.message, "error");
      }
    });
  }

  async function runAccessRequestAction(action, requestId, form, button, surfaceKey) {
    const config = accessRequestActionConfig(surfaceKey);
    const actionPath = {
      approve: `${config.actionBasePath}/${requestId}/approve`,
      defer: `${config.actionBasePath}/${requestId}/defer`,
      reject: `${config.actionBasePath}/${requestId}/reject`,
    }[action];
    if (!actionPath || !form) {
      flash("Access request form was unavailable.", "error");
      return;
    }
    const data = new FormData(form);
    const body =
      action === "approve"
        ? {
            tenant_name: data.get("tenant_name") || "",
            tenant_slug: data.get("tenant_slug") || "",
            subscription_plan: data.get("subscription_plan") || "",
            timezone: data.get("timezone") || "",
            expires_in_hours: data.get("expires_in_hours") ? Number(data.get("expires_in_hours")) : null,
            note: data.get("note") || "",
          }
        : {
            note: data.get("note") || "",
          };
    await runPendingAction(`access-request-${action}-${requestId}`, button || null, "Working...", async function () {
      try {
        const result = await apiFetch(actionPath, {
          method: "POST",
          headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
          body: JSON.stringify(body),
        });
        if (surfaceKey === "platform") {
          state.bootstrap.platform_control_center = await apiFetch(config.refreshPath, { headers: authHeaders() });
          state.platformData = state.bootstrap.platform_control_center;
          renderPlatformSurface(state.bootstrap.platform_control_center);
        } else {
          state.bootstrap.admin_control_center = await apiFetch(config.refreshPath, { headers: authHeaders() });
          renderAdminSurface(state.bootstrap.admin_control_center);
        }
        queueTelemetryEvent({
          eventName: `access_request_${action}`,
          surface: config.telemetrySurface,
          route: `#${config.telemetrySurface}`,
          metadata: {
            request_id: requestId,
            status: result.status,
            inquiry_type: result.inquiry_type,
          },
        });
        const successMessage =
          action === "approve"
            ? `Access request approved. Invite ready for ${result.email}.`
            : `Access request ${action === "defer" ? "deferred" : "rejected"}.`;
        flash(successMessage, "success");
      } catch (error) {
        queueTelemetryEvent({
          eventName: `access_request_${action}_failed`,
          surface: config.telemetrySurface,
          route: `#${config.telemetrySurface}`,
          outcome: "error",
          detail: error.message,
          metadata: { request_id: requestId },
        });
        flash(error.message, "error");
      }
    });
  }

  async function loadConversationDetail(conversationId, targetId) {
    try {
      const renderKey = `detail-${targetId}`;
      const renderVersion = nextRenderVersion(state.runtime, renderKey);
      const initialSnapshot = conversationSnapshotById(conversationId);
      const draftState = rememberedConversationDraft(conversationId) || {};
      const [fetchedMessages, messagingSafetyPayload, activity] = await Promise.all([
        apiFetch(`/api/conversations/${conversationId}/messages`, { headers: authHeaders() }),
        apiFetch(`/api/conversations/${conversationId}/messaging-safety`, { headers: authHeaders() }),
        loadConversationActivity(conversationId),
      ]);
      if (!isCurrentRenderVersion(state.runtime, renderKey, renderVersion)) {
        return;
      }
      const optimisticState = mergeOptimisticMessages(fetchedMessages, (initialSnapshot || {}).optimistic_messages || []);
      const messages = optimisticState.items;
      if (Array.isArray((initialSnapshot || {}).optimistic_messages)) {
        mutateRememberedConversation(conversationId, { optimistic_messages: optimisticState.unresolved });
      }
      state.selectedConversationId = conversationId;
      state.selectedConversationTargetId = targetId;
      const target = document.getElementById(targetId);
      const formId = `reply-form-${targetId}`;
      const dispositionFormId = `disposition-form-${targetId}`;
      const context = selectedInboxContext(targetId);
      const visibleInWorkspace = Boolean(
        activeInboxWorkspace(context.surfaceKey)
          && Object.values((activeInboxWorkspace(context.surfaceKey) || {}).items || {}).some((items) =>
            (items || []).some((item) => item.id === conversationId)
          )
      );
      const snapshot = Object.assign({}, initialSnapshot || {}, {
        latest_message: messages.length ? { body: messages[messages.length - 1].body } : (initialSnapshot || {}).latest_message,
        last_message_at: messages.length ? messages[messages.length - 1].created_at : (initialSnapshot || {}).last_message_at,
      });
      rememberConversationSnapshot(conversationId, snapshot);
      const [contactWorkspaceData, contactTasks, leadNoteData, leadRecordData] = await Promise.all([
        loadContactRecordWorkspace(snapshot),
        loadContactTasks(snapshot.contact_id),
        loadLeadNotes(snapshot),
        loadLeadRecordWorkspace(snapshot),
      ]);
      if (!isCurrentRenderVersion(state.runtime, renderKey, renderVersion)) {
        return;
      }
      const cachedRecordData = rememberedConversationRecordData(conversationId) || {};
      const mergedTasks = mergeWorkspaceCollection(contactTasks, cachedRecordData.tasks, "due_at");
      const mergedNotes = mergeWorkspaceCollection(leadNoteData.notes, cachedRecordData.notes, "created_at");
      const mergedTimelineEvents = mergeWorkspaceCollection(
        contactWorkspaceData.timelineEvents,
        cachedRecordData.timelineEvents,
        "occurred_at"
      );
      const mergedLead = leadRecordData.lead || cachedRecordData.lead || null;
      const mergedPipelineBoard = leadRecordData.pipelineBoard || cachedRecordData.pipelineBoard || null;
      rememberConversationRecordData(conversationId, {
        tasks: mergedTasks,
        notes: mergedNotes,
        timelineEvents: mergedTimelineEvents,
        lead: mergedLead,
        pipelineBoard: mergedPipelineBoard,
      });
      const contactRecord = buildContactRecordWorkspace({
        conversation: snapshot,
        recentMessages: messages,
        relatedConversations: contactWorkspaceData.relatedConversations,
        timelineEvents: mergedTimelineEvents,
      });
      const recordPipeline = buildRecordPipelineWorkspace({
        conversation: snapshot,
        lead: mergedLead,
        pipelineBoard: mergedPipelineBoard,
        relatedConversations: contactWorkspaceData.relatedConversations,
        members: workflowTaskMembers(),
      });
      const crmActivityStream = buildCrmActivityStream({
        messages,
        queueActions: activity.recentActions,
        timelineEvents: mergedTimelineEvents,
        tasks: mergedTasks,
        notes: mergedNotes,
      });
      const internalNotesWorkspace = buildInternalNotesWorkspace({
        notes: mergedNotes,
        members: workflowTaskMembers(),
        currentUser: {
          user_id: (state.context || {}).user_id,
          full_name: (state.context || {}).full_name,
          role: (state.context || {}).role,
          email: (state.context || {}).email,
        },
        conversation: snapshot,
        available: leadNoteData.available,
        unavailableReason: leadNoteData.unavailableReason,
      });
      const qualificationWorkspace = buildLeadQualificationWorkspace({
        notes: mergedNotes,
        available: leadNoteData.available,
        unavailableReason: leadNoteData.unavailableReason,
      });
      const followUpWorkspace = buildFollowUpTaskWorkspace({
        tasks: mergedTasks,
        members: workflowTaskMembers(),
        currentUser: {
          user_id: (state.context || {}).user_id,
          full_name: (state.context || {}).full_name,
          role: (state.context || {}).role,
          email: (state.context || {}).email,
        },
        now: new Date().toISOString(),
      });
      const detailActions = resolveConversationDetailActions({
        role: (state.context || {}).role,
        currentUserId: (state.context || {}).user_id,
        conversation: snapshot,
        activeView: context.activeView,
        focusQueue: context.focusQueue,
        canSelfAssign: context.canSelfAssign,
      });
      const latestMutation = rememberedConversationMutation(conversationId);
      const operatorWorkflow = buildOperatorWorkflowGuide({
        conversation: snapshot,
        messages,
        tasks: mergedTasks,
        latestMutation,
        recentActions: activity.recentActions,
        detailActions,
      });
      const messagingGuard = buildOperatorMessagingGuard({ canReply: detailActions.canReply });
      const messagingSafety = buildOperatorMessagingSafety({
        canReply: detailActions.canReply,
        conversation: snapshot,
        messages,
        timelineEvents: mergedTimelineEvents,
        latestMutation,
        safety: messagingSafetyPayload,
      });
      const replyAssist = buildOperatorReplyAssist({
        conversation: snapshot,
        qualification: Object.fromEntries(qualificationWorkspace.fields.map((field) => [field.key, field.value])),
      });
      const closeoutChecklist = buildOperatorCloseoutChecklist({
        qualification: qualificationWorkspace,
        messages,
        tasks: mergedTasks,
        latestMutation,
        recentActions: activity.recentActions,
      });
      const operatorDailyBrief = buildOperatorDailyBrief({
        conversation: snapshot,
        workflowGuide: operatorWorkflow,
        messagingSafety,
        closeoutChecklist,
        targetId,
      });
      const operatorFocusPath = buildOperatorFocusPath({
        workflowGuide: operatorWorkflow,
        messagingSafety,
        closeoutChecklist,
      });
      const operatorNextLeadHandoff = buildOperatorNextLeadHandoff({
        focusPath: operatorFocusPath,
        closeoutChecklist,
        nextConversationId: nextOperatorConversationId(conversationId),
      });
      const managerConversationReview = buildManagerConversationReview({
        conversation: snapshot,
        qualification: qualificationWorkspace,
        messages,
        tasks: mergedTasks,
        notes: mergedNotes,
        recentActions: activity.recentActions,
        currentUser: currentWorkspaceActor(),
      });
      const replyDisabled = detailActions.canReply && !messagingSafety.replyLocked ? "" : "disabled";
      const dispositionDisabled = detailActions.canDisposition ? "" : "disabled";
      const syncNotice = state.sync.noticesByTarget[targetId] || null;
      const summaryRows = [
        snapshot ? `<span class="${badgeClass(snapshot.aging_bucket)}">${e(snapshot.aging_bucket || "n/a")}</span>` : "",
        snapshot ? `<span class="${badgeClass(snapshot.status === "closed" ? "stale" : "ready")}">${e(snapshot.status || "open")}</span>` : "",
        snapshot
          ? `<span class="${badgeClass(snapshot.assigned_user_id ? "ready" : "stale")}">${snapshot.assigned_user_id ? "assigned" : "unassigned"}</span>`
          : "",
        snapshot
          ? `<span class="${badgeClass(snapshot.first_response_overdue ? "overdue" : "ready")}">${snapshot.first_response_overdue ? "response overdue" : "response ok"}</span>`
          : "",
      ].filter(Boolean);
      const workspaceSummary = buildConversationWorkspaceSummary({
        surfaceKey: context.surfaceKey,
        effectiveView: detailActions.effectiveView,
        conversation: snapshot,
        messages,
        recentActions: activity.recentActions,
        latestMutation,
        activityUnavailableReason: activity.activityUnavailableReason,
        visibleInWorkspace,
      });
      const heroSummary = latestMutation
        ? {
            label: latestMutation.kind === "disposition" ? "Latest outcome" : "Latest action",
            value: latestMutation.summary || "Conversation updated in this session.",
            meta: taskImpact(latestMutation),
            tone: latestMutation.tone || "ready",
          }
        : {
            label: "Next best action",
            value: detailActions.canReply ? "Reply or save a disposition from the action dock." : "Review the thread and recent activity.",
            meta: snapshot.first_response_overdue
              ? "First-response SLA is overdue, so the reply lane should take priority."
              : "Use the action dock for the fastest record updates.",
            tone: snapshot.first_response_overdue ? "overdue" : "muted",
          };
      target.className = "detail-card conversation-workspace-card";
      target.innerHTML = `
        <header id="conversation-workspace-hero" class="conversation-workspace-hero">
          <div class="conversation-hero-copy">
            <div class="eyebrow">Conversation workspace</div>
            <h4>${e((snapshot && snapshot.contact_name) || `Conversation ${conversationId}`)}</h4>
            <div class="meta">${e((snapshot && snapshot.phone_e164) || "phone unavailable")} - ${messages.length} message(s)</div>
            <div class="meta">Current queue context: ${e(detailActions.effectiveView || context.surfaceKey)}</div>
            <div class="conversation-status-strip">${summaryRows.join("")}</div>
          </div>
          <div class="conversation-hero-summary">
            <div class="eyebrow" data-hero-summary-label>${e(heroSummary.label)}</div>
            <strong data-hero-summary-value>${e(heroSummary.value)}</strong>
            <p class="meta" data-hero-summary-meta>${e(heroSummary.meta)}</p>
            <span class="${badgeClass(heroSummary.tone)}" data-hero-summary-tone>${e(heroSummary.tone || "muted")}</span>
          </div>
        </header>
        ${
          syncNotice
            ? `<div id="${context.surfaceKey}-sync-banner" class="detail-empty conversation-sync-banner ${e(syncNotice.tone)}">${e(syncNotice.message)} <button class="action ghost" type="button" data-refresh-sync-notice="true">Review latest state</button></div>`
            : ""
        }
        ${context.surfaceKey === "operator" ? renderOperatorDailyBrief(operatorDailyBrief) : ""}
        ${context.surfaceKey === "operator" ? renderOperatorFocusPath(operatorFocusPath) : ""}
        ${context.surfaceKey === "operator" ? renderOperatorWorkflowPanel(operatorWorkflow) : ""}
        <div class="detail-summary-grid detail-summary-grid-inline workspace-kpi-strip">
          ${renderSummaryCards(workspaceSummary.summaryCards)}
        </div>
        <div class="workspace-main-grid">
          <div class="workspace-main-column">
            <section id="conversation-action-dock" class="workspace-panel workspace-panel-emphasis">
              <div class="section-head">
                <h3>Action dock</h3>
                <p>Keep reply and outcome controls close to the live record so operators can move quickly.</p>
              </div>
              <div class="conversation-actions">
                ${renderDetailActions(detailActions, conversationId)}
              </div>
              ${context.surfaceKey === "operator" ? renderOperatorMessagingGuard(messagingGuard) : ""}
              ${context.surfaceKey === "operator" ? renderOperatorMessagingSafety(messagingSafety) : ""}
              ${context.surfaceKey === "operator" ? renderOperatorReplyAssist(replyAssist) : ""}
              ${context.surfaceKey === "operator" ? renderOperatorCloseoutPanel(closeoutChecklist) : ""}
              ${context.surfaceKey === "operator" ? renderOperatorNextLeadHandoff(operatorNextLeadHandoff, targetId) : ""}
              ${context.surfaceKey === "manager" ? renderManagerConversationReviewPanel(managerConversationReview) : ""}
              <div class="detail-layout detail-layout-2 action-dock-grid">
                <form id="${formId}" class="workspace-panel stack workspace-panel-subtle">
                  <div class="section-head">
                    <h3>Reply workflow</h3>
                    <p>Queue a sandbox reply and refresh the thread immediately after submit.</p>
                  </div>
                  <label><span>Reply Body</span><textarea name="body" placeholder="Queue a sandbox reply for this test workspace.">${e(draftState.replyBody || "")}</textarea></label>
                  <div class="meta">${e(
                    messagingSafety.replyLocked
                      ? messagingSafety.replyLockReason || "Replying is blocked by messaging safety checks."
                      : "Replying may auto-complete inbound follow-up tasks and refresh this workspace. Messaging remains test/sandbox only."
                  )}</div>
                  <button class="action warm" type="submit" ${replyDisabled}>Queue Reply</button>
                </form>
                <form id="${dispositionFormId}" class="workspace-panel stack workspace-panel-subtle">
                  <div class="section-head">
                    <h3>Disposition workflow</h3>
                    <p>Set the conversation outcome once the lead intent is clear.</p>
                  </div>
                  <label>
                    <span>Disposition</span>
                    <select name="disposition" ${dispositionDisabled}>
                      ${conversationDispositionOptions()
                        .map(
                          (item) =>
                            `<option value="${item.value}" ${draftState.disposition === item.value ? "selected" : ""}>${e(item.label)} - ${e(item.helper)}</option>`
                        )
                        .join("")}
                    </select>
                  </label>
                  <label><span>Note</span><textarea name="note" placeholder="Optional operator note for the lead timeline.">${e(draftState.dispositionNote || "")}</textarea></label>
                  <div class="meta">Interested keeps the conversation open. Other dispositions close it and update lead status.</div>
                  <button class="action primary" type="submit" ${dispositionDisabled}>Save Disposition</button>
                </form>
              </div>
            </section>
            ${renderLeadQualificationPanel(qualificationWorkspace, draftState)}
            <section id="conversation-thread-panel" class="workspace-panel workspace-panel-emphasis thread-panel">
              <div class="section-head">
                <h3>Message thread</h3>
                <p>Inbound and outbound messages for the current conversation.</p>
              </div>
              <div class="message-thread">
                ${
                  messages.length
                    ? messages
                        .map(
                          (message) => `
                            <article class="message-item ${e(message.direction)}" ${message.id ? `data-message-id="${e(message.id)}"` : ""}>
                              <div class="meta">${e(messageDeliveryMeta(message))}</div>
                              <div>${e(message.body)}</div>
                              ${
                                String(message.direction || "").toLowerCase() === "outbound"
                                  ? `<div class="message-status-row"><span class="${badgeClass(message.status || "queued")}">${e(message.status || "queued")}</span></div>`
                                  : ""
                              }
                            </article>
                          `
                        )
                        .join("")
                    : renderRichEmptyState(
                        "No messages returned yet",
                        "The record is available, but the message thread is empty. Use the workspace context to review ownership, notes, and follow-up tasks.",
                        "muted"
                      )
                }
              </div>
            </section>
          </div>
          <aside id="conversation-record-rail" class="workspace-rail">
            <section class="workspace-panel">
              <div class="section-head">
                <h3>Contact record</h3>
                <p>The CRM record for this contact, not just the current thread.</p>
              </div>
              <div class="table-like" id="contact-record-facts">${renderWorkspaceRows(contactRecord.factRows)}</div>
            </section>
            <section class="workspace-panel">
              <div class="section-head">
                <h3>Record progression</h3>
                <p>Lead posture, ownership, and current stage context for this record.</p>
              </div>
              <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis" id="record-pipeline-summary">
                ${renderSummaryCards(recordPipeline.summaryCards)}
              </div>
              <div class="table-like" id="record-pipeline-state">${renderWorkspaceRows(recordPipeline.leadRows)}</div>
            </section>
            <section id="record-position-panel" class="workspace-panel">
              <div class="section-head">
                <h3>Pipeline position</h3>
                <p>Where this record sits now and what the next progression step looks like.</p>
              </div>
              <div class="table-like" id="record-pipeline-progress">${renderWorkspaceRows(recordPipeline.progressRows)}</div>
            </section>
            <section id="record-navigation-panel" class="workspace-panel">
              <div class="section-head">
                <h3>Record navigation</h3>
                <p>Open related conversation threads for this contact without leaving the workspace.</p>
              </div>
              <div class="table-like" id="record-navigation">${renderRecordNavigationRows(recordPipeline.navigationRows, targetId)}</div>
            </section>
            <section class="workspace-panel">
              <div class="section-head">
                <h3>Workspace context</h3>
                <p>Queue position, ownership, and SLA posture for the active record.</p>
              </div>
              <div class="table-like">${renderWorkspaceRows(workspaceSummary.contextRows)}</div>
            </section>
            <section class="workspace-panel">
              <div class="section-head">
                <h3>Follow-up impact</h3>
                <p>How replies and dispositions affect downstream lead and task state.</p>
              </div>
              <div class="table-like">${renderWorkspaceRows(workspaceSummary.followUpRows)}</div>
            </section>
            <section class="workspace-panel">
              <div class="section-head">
                <h3>Queue activity focus</h3>
                <p>Conversation-scoped queue action context when your role can view it.</p>
              </div>
              <div class="table-like">${renderWorkspaceRows(workspaceSummary.activityRows)}</div>
            </section>
          </aside>
        </div>
        <div class="detail-layout detail-layout-2 collaboration-grid">
          <section class="workspace-panel" id="follow-up-workspace-panel">
            <div class="section-head">
              <h3>Follow-up tasks</h3>
              <p>Open reminders for this contact, including due-soon and overdue follow-ups.</p>
            </div>
            <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
              ${renderSummaryCards(followUpWorkspace.summaryCards)}
            </div>
            <div class="table-like" id="follow-up-task-list">
              ${renderWorkspaceRows(followUpWorkspace.taskRows)}
            </div>
            <div class="stack task-action-list">
              ${
                followUpWorkspace.taskRows.length
                  ? followUpWorkspace.taskRows
                      .filter((item) => item.status !== "completed")
                      .map(
                        (item) => `
                          <div class="task-action-row">
                            <div>
                              <strong>${e(item.label)}</strong>
                              <div class="meta">${e(item.notes || item.meta || "")}</div>
                            </div>
                            <button class="action ghost" type="button" data-complete-task="${item.id}">Complete</button>
                          </div>
                        `
                      )
                      .join("")
                  : renderRichEmptyState(
                      "No follow-up tasks are open",
                      "Create a reminder below if this record needs another touch, callback, or manager review.",
                      "ready"
                    )
              }
            </div>
          </section>
          <form id="task-form-${targetId}" class="workspace-panel stack workspace-panel-subtle">
            <div class="section-head">
              <h3>Create reminder</h3>
              <p>Assign a follow-up task to yourself or a teammate without leaving the conversation.</p>
            </div>
            <label><span>Title</span><input name="title" placeholder="Call back tomorrow about pricing" value="${e(draftState.taskTitle || "")}" required /></label>
            <label><span>Task type</span><input name="task_type" value="${e(draftState.taskType || "follow_up")}" /></label>
            <label>
              <span>Assignee</span>
              <select name="assigned_user_id">
                ${followUpWorkspace.assigneeOptions
                  .map(
                    (item) =>
                      `<option value="${e(item.user_id)}" ${String(draftState.taskAssignedUserId || "") === String(item.user_id) ? "selected" : ""}>${e(item.label)}</option>`
                  )
                  .join("")}
              </select>
            </label>
            <label>
              <span>Priority</span>
              <select name="priority">
                <option value="normal" ${(draftState.taskPriority || "normal") === "normal" ? "selected" : ""}>Normal</option>
                <option value="high" ${(draftState.taskPriority || "") === "high" ? "selected" : ""}>High</option>
                <option value="low" ${(draftState.taskPriority || "") === "low" ? "selected" : ""}>Low</option>
              </select>
            </label>
            <label><span>Due at</span><input name="due_at" type="datetime-local" value="${e(dateTimeLocalFromIso(draftState.taskDueAt || ""))}" /></label>
            <label><span>Notes</span><textarea name="notes" placeholder="Reminder context for the next follow-up.">${e(draftState.taskNotes || "")}</textarea></label>
            <button class="action primary" type="submit">Create Reminder</button>
          </form>
        </div>
        <div id="record-collaboration-panel" class="detail-layout detail-layout-2 collaboration-grid">
          <section class="workspace-panel">
            <div class="section-head">
              <h3>Internal notes</h3>
              <p>Private team context for handoffs, manager guidance, and record collaboration.</p>
            </div>
            <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
              ${renderSummaryCards(internalNotesWorkspace.summaryCards)}
            </div>
            <div class="table-like" id="internal-note-list">${renderWorkspaceRows(internalNotesWorkspace.noteRows)}</div>
            <div class="section-divider"></div>
            <div class="section-head section-head-compact">
              <h3>Collaboration guidance</h3>
              <p>Use these prompts before reassigning, replying, or handing the record to another teammate.</p>
            </div>
            <div class="table-like" id="collaboration-guidance">${renderWorkspaceRows(internalNotesWorkspace.guidanceRows)}</div>
          </section>
          <form id="note-form-${targetId}" class="workspace-panel stack workspace-panel-subtle">
            <div class="section-head">
              <h3>Add internal note</h3>
              <p>Keep teammate guidance inside LandSaaS instead of external chat or memory.</p>
            </div>
            <label>
              <span>Note type</span>
              <select name="note_type" ${internalNotesWorkspace.canCreate ? "" : "disabled"}>
                <option value="internal" ${(draftState.noteType || "internal") === "internal" ? "selected" : ""}>Internal</option>
                <option value="handoff" ${(draftState.noteType || "") === "handoff" ? "selected" : ""}>Handoff</option>
                <option value="manager_guidance" ${(draftState.noteType || "") === "manager_guidance" ? "selected" : ""}>Manager guidance</option>
              </select>
            </label>
            <label>
              <span>Note body</span>
              <textarea
                name="body"
                placeholder="Capture the callback window, negotiation context, or handoff guidance."
                ${internalNotesWorkspace.canCreate ? "" : "disabled"}
              >${e(draftState.noteBody || "")}</textarea>
            </label>
            <div class="meta">${
              internalNotesWorkspace.canCreate
                ? "Internal notes append to the record timeline and stay private to the team."
                : e(internalNotesWorkspace.unavailableReason)
            }</div>
            <button class="action primary" type="submit" ${internalNotesWorkspace.canCreate ? "" : "disabled"}>Save Internal Note</button>
          </form>
        </div>
        <section id="crm-activity-stream-panel" class="workspace-panel">
          <div class="section-head">
            <h3>CRM activity stream</h3>
            <p>Unified record history across messages, queue actions, tasks, and timeline events.</p>
          </div>
          <div class="detail-summary-grid detail-summary-grid-inline workspace-inline-kpis">
            ${renderSummaryCards(crmActivityStream.summaryCards)}
          </div>
          <div class="table-like" id="crm-activity-stream">${renderWorkspaceRows(crmActivityStream.rows)}</div>
        </section>
      `;
      rememberOpenConversationDetail(targetId, conversationId, snapshot);
      clearConversationNotice(targetId);
      const taskFormId = `task-form-${targetId}`;
      const noteFormId = `note-form-${targetId}`;
      const qualificationFormId = `qualification-form-${targetId}`;
      const replyForm = document.getElementById(formId);
      const dispositionForm = document.getElementById(dispositionFormId);
      const taskForm = document.getElementById(taskFormId);
      const noteForm = document.getElementById(noteFormId);
      const qualificationForm = document.getElementById(qualificationFormId);

      const replyBodyField = replyForm.querySelector('textarea[name="body"]');
      replyBodyField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { replyBody: this.value });
      });
      target.querySelectorAll("[data-reply-template-body]").forEach((button) => {
        button.addEventListener("click", function () {
          const body = this.dataset.replyTemplateBody || "";
          replyBodyField.value = body;
          rememberConversationDraft(conversationId, { replyBody: body });
          replyBodyField.focus();
          flash("Reply template inserted. Review before queueing.", "success");
        });
      });

      const dispositionField = dispositionForm.querySelector('select[name="disposition"]');
      const dispositionNoteField = dispositionForm.querySelector('textarea[name="note"]');
      dispositionField.addEventListener("change", function () {
        rememberConversationDraft(conversationId, { disposition: this.value });
      });
      dispositionNoteField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { dispositionNote: this.value });
      });

      const taskTitleField = taskForm.querySelector('input[name="title"]');
      const taskTypeField = taskForm.querySelector('input[name="task_type"]');
      const taskAssignedUserField = taskForm.querySelector('select[name="assigned_user_id"]');
      const taskPriorityField = taskForm.querySelector('select[name="priority"]');
      const taskDueAtField = taskForm.querySelector('input[name="due_at"]');
      const taskNotesField = taskForm.querySelector('textarea[name="notes"]');
      taskTitleField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { taskTitle: this.value });
      });
      taskTypeField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { taskType: this.value });
      });
      taskAssignedUserField.addEventListener("change", function () {
        rememberConversationDraft(conversationId, { taskAssignedUserId: this.value });
      });
      taskPriorityField.addEventListener("change", function () {
        rememberConversationDraft(conversationId, { taskPriority: this.value });
      });
      taskDueAtField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { taskDueAt: isoFromDateTimeLocal(this.value) });
      });
      taskNotesField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { taskNotes: this.value });
      });

      const noteTypeField = noteForm.querySelector('select[name="note_type"]');
      const noteBodyField = noteForm.querySelector('textarea[name="body"]');
      noteTypeField.addEventListener("change", function () {
        rememberConversationDraft(conversationId, { noteType: this.value });
      });
      noteBodyField.addEventListener("input", function () {
        rememberConversationDraft(conversationId, { noteBody: this.value });
      });
      qualificationForm.querySelectorAll("textarea").forEach((field) => {
        field.addEventListener("input", function () {
          rememberConversationDraft(conversationId, { [`qualification_${this.name}`]: this.value });
        });
      });
      target.querySelectorAll("[data-manager-review-action]").forEach((button) => {
        button.addEventListener("click", async function () {
          const action = managerConversationReview.actions.find((item) => item.key === this.dataset.managerReviewAction);
          if (!action) {
            return;
          }
          await runPendingAction(`manager-review-${action.key}-${conversationId}`, this, "Saving...", async function () {
            try {
              if (action.kind === "note") {
                const resolvedLeadId =
                  leadNoteData.leadId || (await resolveLeadIdForConversation(snapshot, { createIfMissing: true }));
                if (!resolvedLeadId) {
                  throw new Error("This record could not be linked to a lead for manager review.");
                }
                const noteResponse = await apiFetch(`/api/leads/${resolvedLeadId}/notes`, {
                  method: "POST",
                  headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
                  body: JSON.stringify({
                    body: action.body,
                    contact_id: snapshot.contact_id,
                    conversation_id: conversationId,
                    note_type: action.noteType,
                  }),
                });
                rememberConversationRecordData(conversationId, {
                  notes: mergeWorkspaceCollection(
                    [],
                    [noteResponse].concat(((rememberedConversationRecordData(conversationId) || {}).notes || [])),
                    "created_at"
                  ),
                });
                rememberConversationMutation(conversationId, {
                  kind: action.noteType,
                  summary: action.noteType === "manager_review" ? "Manager review saved." : "Manager guidance returned.",
                  taskIdsCompleted: [],
                  taskIdCreated: null,
                  at: new Date().toISOString(),
                  tone: action.noteType === "manager_review" ? "ready" : "stale",
                });
                queueTelemetryEvent({
                  eventName: "manager_review_note_saved",
                  surface: context.surfaceKey,
                  route: buildSurfaceHash(context.surfaceKey),
                  metadata: {
                    conversation_id: conversationId,
                    lead_id: resolvedLeadId,
                    note_id: noteResponse.id,
                    note_type: action.noteType,
                  },
                });
                await loadBootstrap();
                await loadConversationDetail(conversationId, targetId);
                flash(action.noteType === "manager_review" ? "Manager review saved." : "Manager guidance returned.", "success");
                return;
              }

              if (action.kind === "task") {
                const taskResponse = await apiFetch("/api/tasks", {
                  method: "POST",
                  headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
                  body: JSON.stringify({
                    task_type: action.taskType,
                    title: action.title,
                    contact_id: snapshot.contact_id,
                    assigned_user_id: action.assignedUserId,
                    priority: action.priority || "normal",
                    notes: action.notes,
                    details_json: { conversation_id: conversationId, source: "manager_review_panel", action_key: action.key },
                  }),
                });
                rememberConversationRecordData(conversationId, {
                  tasks: mergeWorkspaceCollection(
                    [],
                    [taskResponse].concat(((rememberedConversationRecordData(conversationId) || {}).tasks || [])),
                    "due_at"
                  ),
                });
                rememberConversationMutation(conversationId, {
                  kind: "manager_review_task",
                  summary: "Manager follow-up assigned.",
                  taskIdsCompleted: [],
                  taskIdCreated: taskResponse.id || null,
                  at: new Date().toISOString(),
                  tone: "stale",
                });
                queueTelemetryEvent({
                  eventName: "manager_review_task_created",
                  surface: context.surfaceKey,
                  route: buildSurfaceHash(context.surfaceKey),
                  metadata: { conversation_id: conversationId, task_id: taskResponse.id },
                });
                await loadBootstrap();
                await loadConversationDetail(conversationId, targetId);
                flash("Manager follow-up assigned.", "success");
              }
            } catch (error) {
              queueTelemetryEvent({
                eventName: "manager_review_action_failed",
                surface: context.surfaceKey,
                route: buildSurfaceHash(context.surfaceKey),
                outcome: "error",
                detail: error.message,
                metadata: { conversation_id: conversationId, action_key: action.key },
              });
              flash(error.message, "error");
            }
          });
        });
      });

      taskForm.onsubmit = async function (event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const title = String(formData.get("title") || "").trim();
        const taskType = String(formData.get("task_type") || "").trim() || "follow_up";
        if (!title) {
          flash("Reminder title cannot be empty.", "error");
          return;
        }
        const submitButton = event.target.querySelector('button[type="submit"]');
        await runPendingAction(`task-create-${conversationId}`, submitButton, "Creating...", async function () {
          try {
            const taskResponse = await apiFetch("/api/tasks", {
              method: "POST",
              headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
              body: JSON.stringify({
                task_type: taskType,
                title,
                contact_id: snapshot.contact_id,
                assigned_user_id: String(formData.get("assigned_user_id") || "").trim() || null,
                priority: String(formData.get("priority") || "normal"),
                notes: String(formData.get("notes") || "").trim() || null,
                due_at: isoFromDateTimeLocal(formData.get("due_at")),
                details_json: { conversation_id: conversationId, source: "product_ui_detail_task" },
              }),
            });
            queueTelemetryEvent({
              eventName: "follow_up_task_created",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              metadata: { conversation_id: conversationId, task_id: taskResponse.id, task_type: taskType },
            });
            rememberConversationMutation(conversationId, {
              kind: "task",
              summary: "Follow-up reminder created.",
              taskIdsCompleted: [],
              taskIdCreated: taskResponse.id || null,
              at: new Date().toISOString(),
              tone: "ready",
            });
            rememberConversationRecordData(conversationId, {
              tasks: mergeWorkspaceCollection(
                [],
                [taskResponse].concat(((rememberedConversationRecordData(conversationId) || {}).tasks || [])),
                "due_at"
              ),
            });
            applyFollowUpWorkspace(
              targetId,
              ((rememberedConversationRecordData(conversationId) || {}).tasks || [])
            );
            clearConversationDraft(conversationId, [
              "taskTitle",
              "taskType",
              "taskAssignedUserId",
              "taskPriority",
              "taskDueAt",
              "taskNotes",
            ]);
            await loadBootstrap();
            finalizeConversationWorkspaceAction(targetId, conversationId, {
              label: "Latest action",
              value: "Follow-up reminder created.",
              meta: taskImpact({ taskIdsCompleted: [], taskIdCreated: taskResponse.id || null }),
              tone: "ready",
            });
            flash("Follow-up reminder created.", "success");
          } catch (error) {
            queueTelemetryEvent({
              eventName: "follow_up_task_create_failed",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              outcome: "error",
              detail: error.message,
              metadata: { conversation_id: conversationId, task_type: taskType },
            });
            flash(error.message, "error");
          }
        });
      };
      noteForm.onsubmit = async function (event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const body = String(formData.get("body") || "").trim();
        const noteType = String(formData.get("note_type") || "internal").trim() || "internal";
        if (!body) {
          flash("Internal note body cannot be empty.", "error");
          return;
        }
        const submitButton = event.target.querySelector('button[type="submit"]');
        await runPendingAction(`lead-note-${conversationId}`, submitButton, "Saving...", async function () {
          try {
            const resolvedLeadId =
              leadNoteData.leadId || (await resolveLeadIdForConversation(snapshot, { createIfMissing: true }));
            if (!resolvedLeadId) {
              throw new Error("This record could not be linked to a lead for internal notes.");
            }
            const noteResponse = await apiFetch(`/api/leads/${resolvedLeadId}/notes`, {
              method: "POST",
              headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
              body: JSON.stringify({
                body,
                contact_id: snapshot.contact_id,
                conversation_id: conversationId,
                note_type: noteType,
              }),
            });
            queueTelemetryEvent({
              eventName: "internal_note_saved",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              metadata: {
                conversation_id: conversationId,
                lead_id: resolvedLeadId,
                note_id: noteResponse.id,
                note_type: noteType,
              },
            });
            event.target.reset();
            rememberConversationMutation(conversationId, {
              kind: "note",
              summary: "Internal note saved.",
              taskIdsCompleted: [],
              taskIdCreated: null,
              at: new Date().toISOString(),
              tone: "ready",
            });
            rememberConversationRecordData(conversationId, {
              notes: mergeWorkspaceCollection(
                [],
                [noteResponse].concat(((rememberedConversationRecordData(conversationId) || {}).notes || [])),
                "created_at"
              ),
            });
            applyInternalNotesWorkspace(targetId, {
              notes: ((rememberedConversationRecordData(conversationId) || {}).notes || []),
              conversation: rememberedConversationSnapshot(conversationId) || snapshot,
              available: true,
            });
            const refreshedLeadRecord = await loadLeadRecordWorkspace(rememberedConversationSnapshot(conversationId) || snapshot);
            rememberConversationRecordData(conversationId, {
              lead: refreshedLeadRecord.lead || ((rememberedConversationRecordData(conversationId) || {}).lead || null),
              pipelineBoard: refreshedLeadRecord.pipelineBoard || ((rememberedConversationRecordData(conversationId) || {}).pipelineBoard || null),
            });
            applyRecordPipelineWorkspace(targetId, {
              conversation: rememberedConversationSnapshot(conversationId) || snapshot,
              lead: ((rememberedConversationRecordData(conversationId) || {}).lead || null),
              pipelineBoard: ((rememberedConversationRecordData(conversationId) || {}).pipelineBoard || null),
              relatedConversations: contactWorkspaceData.relatedConversations,
            });
            clearConversationDraft(conversationId, ["noteType", "noteBody"]);
            await loadBootstrap();
            finalizeConversationWorkspaceAction(targetId, conversationId, {
              label: "Latest action",
              value: "Internal note saved.",
              meta: "Record collaboration guidance is now stored on the CRM timeline.",
              tone: "ready",
            });
            flash("Internal note saved.", "success");
          } catch (error) {
            queueTelemetryEvent({
              eventName: "internal_note_save_failed",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              outcome: "error",
              detail: error.message,
              metadata: { conversation_id: conversationId, lead_id: snapshot.lead_id || null, note_type: noteType },
            });
            flash(error.message, "error");
          }
        });
      };
      qualificationForm.onsubmit = async function (event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const values = {};
        for (const field of qualificationWorkspace.fields) {
          values[field.key] = String(formData.get(field.key) || "").trim();
        }
        if (!Object.values(values).some(Boolean)) {
          flash("Capture at least one qualification field before saving.", "error");
          return;
        }
        const submitButton = event.target.querySelector('button[type="submit"]');
        await runPendingAction(`qualification-${conversationId}`, submitButton, "Saving...", async function () {
          try {
            const resolvedLeadId =
              leadNoteData.leadId || (await resolveLeadIdForConversation(snapshot, { createIfMissing: true }));
            if (!resolvedLeadId) {
              throw new Error("This record could not be linked to a lead for qualification.");
            }
            const noteResponse = await apiFetch(`/api/leads/${resolvedLeadId}/notes`, {
              method: "POST",
              headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
              body: JSON.stringify({
                body: buildQualificationNoteBody(values),
                contact_id: snapshot.contact_id,
                conversation_id: conversationId,
                note_type: "qualification",
              }),
            });
            queueTelemetryEvent({
              eventName: "lead_qualification_saved",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              metadata: {
                conversation_id: conversationId,
                lead_id: resolvedLeadId,
                note_id: noteResponse.id,
                captured_count: Object.values(values).filter(Boolean).length,
              },
            });
            rememberConversationMutation(conversationId, {
              kind: "qualification",
              summary: `Lead qualification saved: ${values.next_step || values.motivation || "manager-visible context captured"}.`,
              taskIdsCompleted: [],
              taskIdCreated: null,
              at: new Date().toISOString(),
              tone: "ready",
            });
            rememberConversationRecordData(conversationId, {
              notes: mergeWorkspaceCollection(
                [],
                [noteResponse].concat(((rememberedConversationRecordData(conversationId) || {}).notes || [])),
                "created_at"
              ),
            });
            clearConversationDraft(
              conversationId,
              qualificationWorkspace.fields.map((field) => `qualification_${field.key}`)
            );
            await loadBootstrap();
            await loadConversationDetail(conversationId, targetId);
            flash("Lead qualification saved for manager and owner visibility.", "success");
          } catch (error) {
            queueTelemetryEvent({
              eventName: "lead_qualification_save_failed",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              outcome: "error",
              detail: error.message,
              metadata: { conversation_id: conversationId, lead_id: snapshot.lead_id || null },
            });
            flash(error.message, "error");
          }
        });
      };
      replyForm.onsubmit = async function (event) {
        event.preventDefault();
        const body = String(new FormData(event.target).get("body") || "").trim();
        if (!body) {
          flash("Reply body cannot be empty.", "error");
          return;
        }
        const submitButton = event.target.querySelector('button[type="submit"]');
        await runPendingAction(`reply-${conversationId}`, submitButton, "Queueing...", async function () {
          try {
            const replyResponse = await apiFetch(`/api/conversations/${conversationId}/reply`, {
              method: "POST",
              headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
              body: JSON.stringify({ body }),
            });
            const completedCount = (replyResponse.task_ids_completed || []).length;
            const replySummary = completedCount
              ? `Reply queued. ${completedCount} follow-up task(s) completed.`
              : "Reply queued.";
            rememberConversationMutation(conversationId, {
              kind: "reply",
              summary: replySummary,
              taskIdsCompleted: replyResponse.task_ids_completed || [],
              taskIdCreated: null,
              at: new Date().toISOString(),
              tone: completedCount ? "ready" : "muted",
            });
            mutateRememberedConversation(conversationId, {
              status: "open",
              last_message_at: (replyResponse.message || {}).created_at || snapshot.last_message_at,
              latest_message: replyResponse.message ? { body: replyResponse.message.body } : snapshot.latest_message,
              optimistic_messages: [
                ...((((rememberedConversationSnapshot(conversationId) || {}).optimistic_messages) || []).filter(
                  (item) => item && item.id !== (replyResponse.message || {}).id
                )),
                ...(replyResponse.message ? [replyResponse.message] : []),
              ],
            });
            clearConversationDraft(conversationId, ["replyBody"]);
            if (replyResponse.message) {
              applyOptimisticReplyToWorkspace(
                targetId,
                replyResponse.message,
                rememberedConversationSnapshot(conversationId) || snapshot
              );
            }
            event.target.reset();
            queueTelemetryEvent({
              eventName: "reply_queued",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              metadata: { conversation_id: conversationId, completed_task_count: completedCount },
            });
            await loadBootstrap();
            finalizeConversationWorkspaceAction(targetId, conversationId, {
              label: "Latest action",
              value: replySummary,
              meta: taskImpact({
                taskIdsCompleted: replyResponse.task_ids_completed || [],
                taskIdCreated: null,
              }),
              tone: completedCount ? "ready" : "muted",
            });
            flash(replySummary, "success");
          } catch (error) {
            queueTelemetryEvent({
              eventName: "reply_queue_failed",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              outcome: "error",
              detail: error.message,
              metadata: { conversation_id: conversationId },
            });
            flash(error.message, "error");
          }
        });
      };
      dispositionForm.onsubmit = async function (event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const disposition = String(formData.get("disposition") || "").trim();
        const note = String(formData.get("note") || "").trim();
        if (!disposition) {
          flash("Select a disposition first.", "error");
          return;
        }
        const submitButton = event.target.querySelector('button[type="submit"]');
        await runPendingAction(`disposition-${conversationId}`, submitButton, "Saving...", async function () {
          try {
            const dispositionResponse = await apiFetch(`/api/conversations/${conversationId}/disposition`, {
              method: "POST",
              headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
              body: JSON.stringify({ disposition, note: note || null }),
            });
            const completedCount = (dispositionResponse.task_ids_completed || []).length;
            const createdCount = dispositionResponse.task_id_created ? 1 : 0;
            const impact = [];
            if (completedCount) {
              impact.push(`${completedCount} task(s) completed`);
            }
            if (createdCount) {
              impact.push("1 task queued");
            }
            const summary =
              impact.length > 0
                ? `Disposition saved as ${dispositionResponse.disposition}. ${impact.join(", ")}.`
                : `Disposition saved as ${dispositionResponse.disposition}.`;
            rememberConversationMutation(conversationId, {
              kind: "disposition",
              disposition: dispositionResponse.disposition,
              summary,
              taskIdsCompleted: dispositionResponse.task_ids_completed || [],
              taskIdCreated: dispositionResponse.task_id_created || null,
              at: new Date().toISOString(),
              tone: dispositionResponse.conversation_status === "closed" ? "stale" : "ready",
            });
            mutateRememberedConversation(conversationId, {
              status: dispositionResponse.conversation_status,
            });
            const existingRecordData = rememberedConversationRecordData(conversationId) || {};
            const existingLead = existingRecordData.lead || null;
            const updatedLeadBase = existingLead || {
              id: dispositionResponse.lead_id || snapshot.lead_id || null,
              contact_id: snapshot.contact_id || null,
              assigned_user_id: snapshot.assigned_user_id || null,
              source: "product_ui_disposition",
              priority: "normal",
              status: "active",
              stage_id: null,
              stage_status_id: null,
              stage_status_key: null,
              stage_status_label: null,
              dead_reason: null,
              last_contacted_at: null,
              last_response_at: snapshot.last_message_at || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            const updatedLead = updatedLeadBase.id
              ? Object.assign({}, updatedLeadBase, {
                  id: dispositionResponse.lead_id || updatedLeadBase.id,
                  status: dispositionResponse.lead_status || updatedLeadBase.status,
                  stage_id:
                    "lead_stage_id" in dispositionResponse
                      ? dispositionResponse.lead_stage_id
                      : updatedLeadBase.stage_id,
                  stage_status_id:
                    "lead_stage_status_id" in dispositionResponse
                      ? dispositionResponse.lead_stage_status_id
                      : updatedLeadBase.stage_status_id,
                  stage_status_key:
                    dispositionResponse.disposition === "interested"
                      ? "interested"
                      : dispositionResponse.lead_status === "dead"
                        ? null
                        : updatedLeadBase.stage_status_key,
                  stage_status_label:
                    dispositionResponse.disposition === "interested"
                      ? "Interested"
                      : dispositionResponse.lead_status === "dead"
                        ? null
                        : updatedLeadBase.stage_status_label,
                  dead_reason:
                    "dead_reason" in dispositionResponse
                      ? dispositionResponse.dead_reason
                      : updatedLeadBase.dead_reason,
                  updated_at: new Date().toISOString(),
                })
              : null;
            const updatedPipelineBoard = updatedLead
              ? mergeLeadIntoPipelineBoard(existingRecordData.pipelineBoard || null, updatedLead)
              : existingRecordData.pipelineBoard || null;
            rememberConversationRecordData(conversationId, {
              lead: updatedLead,
              pipelineBoard: updatedPipelineBoard,
            });
            applyRecordPipelineWorkspace(targetId, {
              conversation: rememberedConversationSnapshot(conversationId) || snapshot,
              lead: updatedLead,
              pipelineBoard: updatedPipelineBoard,
              relatedConversations: contactWorkspaceData.relatedConversations,
            });
            clearConversationDraft(conversationId, ["disposition", "dispositionNote"]);
            event.target.reset();
            queueTelemetryEvent({
              eventName: "disposition_saved",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              metadata: {
                conversation_id: conversationId,
                disposition: dispositionResponse.disposition,
                completed_task_count: completedCount,
              },
            });
            await loadBootstrap();
            await loadConversationDetail(conversationId, targetId);
            finalizeConversationWorkspaceAction(targetId, conversationId, {
              label: "Latest outcome",
              value: summary,
              meta: taskImpact({
                taskIdsCompleted: dispositionResponse.task_ids_completed || [],
                taskIdCreated: dispositionResponse.task_id_created || null,
              }),
              tone: dispositionResponse.conversation_status === "closed" ? "stale" : "ready",
            });
            flash(summary, "success");
          } catch (error) {
            queueTelemetryEvent({
              eventName: "disposition_save_failed",
              surface: context.surfaceKey,
              route: buildSurfaceHash(context.surfaceKey),
              outcome: "error",
              detail: error.message,
              metadata: { conversation_id: conversationId, disposition },
            });
            flash(error.message, "error");
          }
        });
      };
    } catch (error) {
      flash(error.message, "error");
    }
  }

  async function syncSelectedConversationDetail() {
    if (!state.selectedConversationId || !state.selectedConversationTargetId) {
      return;
    }
    await loadConversationDetail(state.selectedConversationId, state.selectedConversationTargetId);
  }

  async function mutateConversation(path, prefix, button) {
    await runPendingAction(`${prefix}-${path}`, button || null, "Working...", async function () {
      try {
        await apiFetch(path, {
          method: "POST",
          headers: authHeaders({ "Idempotency-Key": idempotencyKey(prefix) }),
        });
        queueTelemetryEvent({
          eventName: "queue_mutation_completed",
          surface: state.activeSurface || "operator",
          route: buildSurfaceHash(state.activeSurface || "operator"),
          metadata: { path, prefix },
        });
        await loadBootstrap();
      } catch (error) {
        queueTelemetryEvent({
          eventName: "queue_mutation_failed",
          surface: state.activeSurface || "operator",
          route: buildSurfaceHash(state.activeSurface || "operator"),
          outcome: "error",
          detail: error.message,
          metadata: { path, prefix },
        });
        throw error;
      }
    });
  }

  async function bulkAction(action, button) {
    const ids = selectedManagerConversationIds();
    if (!ids.length) {
      flash("Select at least one conversation first.", "error");
      return;
    }
    const userId = document.getElementById("bulk-user-select").value;
    const path = {
      assign: "/api/conversations/queue/bulk/assign",
      escalate: "/api/conversations/queue/bulk/escalate",
      release: "/api/conversations/queue/bulk/release",
    }[action];
    const body = { conversation_ids: ids, reason: "product_ui_bulk_action" };
    if (action !== "release") {
      body.assigned_user_id = userId;
    }
    await runPendingAction(`bulk-${action}`, button || null, "Working...", async function () {
      try {
        const result = await apiFetch(path, {
          method: "POST",
          headers: Object.assign({ "Content-Type": "application/json", "Idempotency-Key": idempotencyKey(`ui-${action}`) }, authHeaders()),
          body: JSON.stringify(body),
        });
        state.managerBulkResult = summarizeBulkResult(action, result);
        queueTelemetryEvent({
          eventName: "bulk_action_completed",
          surface: "manager",
          route: "#manager",
          metadata: { action, conversation_count: ids.length, success_count: result.success_count || 0 },
        });
        flash(`Bulk ${action} completed.`, "success");
        await loadBootstrap();
      } catch (error) {
        queueTelemetryEvent({
          eventName: "bulk_action_failed",
          surface: "manager",
          route: "#manager",
          outcome: "error",
          detail: error.message,
          metadata: { action, conversation_count: ids.length },
        });
        flash(error.message, "error");
      }
    });
  }

  document.getElementById("refresh-session").addEventListener("click", async function (event) {
    await runPendingAction("refresh-session", event.currentTarget, "Refreshing...", async function () {
      try {
        await resumeSession({ forceRefresh: true });
        flash("UI refreshed.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
    });
  });

  document.getElementById("logout-session").addEventListener("click", function () {
    clearSession();
    state.authChecked = true;
    renderSession();
    renderNav();
    window.location.hash = "";
    const operatorDetail = buildDetailPlaceholder("operator");
    document.getElementById(inboxDetailTarget("operator")).className = "";
    document.getElementById(inboxDetailTarget("operator")).innerHTML = renderGuidedEmptyState(
      operatorDetail.title,
      operatorDetail.detail,
      operatorDetail.tone,
      operatorDetail.actions
    );
    const managerDetail = buildDetailPlaceholder("manager");
    document.getElementById(inboxDetailTarget("manager")).className = "";
    document.getElementById(inboxDetailTarget("manager")).innerHTML = renderGuidedEmptyState(
      managerDetail.title,
      managerDetail.detail,
      managerDetail.tone,
      managerDetail.actions
    );
    flash("Session cleared.", "success");
  });

  document.getElementById("bulk-assign").addEventListener("click", (event) => bulkAction("assign", event.currentTarget));
  document.getElementById("bulk-escalate").addEventListener("click", (event) => bulkAction("escalate", event.currentTarget));
  document.getElementById("bulk-release").addEventListener("click", (event) => bulkAction("release", event.currentTarget));
  document.getElementById("bulk-user-select").addEventListener("change", function () {
    renderManagerBulkSummary();
  });

  document.body.addEventListener("submit", async function (event) {
    const inboxForm = event.target.closest("[data-inbox-filter-form]");
    if (inboxForm) {
      event.preventDefault();
      const surfaceKey = inboxForm.dataset.inboxFilterForm;
      updateInboxFilters(surfaceKey, inboxFilterValues(inboxForm));
      await rerenderInboxSurface(surfaceKey);
      flash("Inbox filters applied.", "success");
      return;
    }
    const ownerTeamRoleForm = event.target.closest("[data-owner-team-member-role-form]");
    if (ownerTeamRoleForm) {
      event.preventDefault();
      if ((state.context || {}).role !== "tenant_owner") {
        flash("Only tenant owners can manage workspace members.", "error");
        return;
      }
      const membershipId = ownerTeamRoleForm.dataset.membershipId;
      const memberLabel = ownerTeamRoleForm.dataset.memberLabel || "Member";
      const role = new FormData(ownerTeamRoleForm).get("role") || "operator";
      const submitButton = ownerTeamRoleForm.querySelector('button[type="submit"]');
      await runPendingAction(`owner-team-role-${membershipId}`, submitButton, "Saving...", async function () {
        try {
          const updated = await apiFetch(`/api/workspace/members/${membershipId}`, {
            method: "PATCH",
            headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
            body: JSON.stringify({ role }),
          });
          state.bootstrap.admin_control_center = await apiFetch("/api/admin/control-center", { headers: authHeaders() });
          renderAdminSurface(state.bootstrap.admin_control_center);
          await focusAdminTarget("admin-onboarding-roster");
          queueTelemetryEvent({
            eventName: "workspace_member_role_updated",
            surface: "admin",
            route: "#admin",
            metadata: { membership_id: membershipId, role: updated.role },
          });
          flash(`Role updated for ${memberLabel}.`, "success");
        } catch (error) {
          queueTelemetryEvent({
            eventName: "workspace_member_role_update_failed",
            surface: "admin",
            route: "#admin",
            outcome: "error",
            detail: error.message,
            metadata: { membership_id: membershipId },
          });
          flash(error.message, "error");
        }
      });
      return;
    }
    const ownerTeamInviteForm = event.target.closest("[data-owner-team-invite-form]");
    if (ownerTeamInviteForm) {
      event.preventDefault();
      if ((state.context || {}).role !== "tenant_owner") {
        flash("Only tenant owners can create workspace invites.", "error");
        return;
      }
      const data = new FormData(ownerTeamInviteForm);
      const body = {
        full_name: data.get("full_name") || "",
        email: data.get("email") || "",
        role: data.get("role") || "operator",
        expires_in_hours: data.get("expires_in_hours") ? Number(data.get("expires_in_hours")) : null,
      };
      const submitButton = ownerTeamInviteForm.querySelector('button[type="submit"]');
      await runPendingAction("owner-team-invite", submitButton, "Creating...", async function () {
        try {
          state.adminInviteResult = await apiFetch("/api/workspace/invites", {
            method: "POST",
            headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
            body: JSON.stringify(body),
          });
          state.bootstrap.admin_control_center = await apiFetch("/api/admin/control-center", { headers: authHeaders() });
          renderAdminSurface(state.bootstrap.admin_control_center);
          await focusAdminTarget("admin-onboarding-roster");
          queueTelemetryEvent({
            eventName: "workspace_invite_created",
            surface: "admin",
            route: "#admin",
            metadata: { role: state.adminInviteResult.role, email: state.adminInviteResult.email },
          });
          flash(`Invite ready for ${state.adminInviteResult.email}.`, "success");
        } catch (error) {
          queueTelemetryEvent({
            eventName: "workspace_invite_create_failed",
            surface: "admin",
            route: "#admin",
            outcome: "error",
            detail: error.message,
          });
          flash(error.message, "error");
        }
      });
      return;
    }
    if (event.target.id === "admin-reporting-form") {
      event.preventDefault();
      const data = new FormData(event.target);
      state.adminReporting.filters = {
        interval: data.get("interval") || "week",
        status: data.get("status") || "",
        assigned_user_id: data.get("assigned_user_id") || "",
        date_from: data.get("date_from") || "",
        date_to: data.get("date_to") || "",
      };
      persistAdminReportingFilters(window.localStorage, state.adminReporting.filters);
      try {
        await renderAdminReporting();
        flash("Operational report refreshed.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
    }
  });

  document.body.addEventListener("click", async function (event) {
    const ownerTeamAction = event.target.closest("[data-owner-team-member-action]");
    if (ownerTeamAction) {
      if ((state.context || {}).role !== "tenant_owner") {
        flash("Only tenant owners can manage workspace members.", "error");
        return;
      }
      const action = ownerTeamAction.dataset.ownerTeamMemberAction;
      const membershipId = ownerTeamAction.dataset.membershipId;
      const memberLabel = ownerTeamAction.dataset.memberLabel || "Member";
      await runPendingAction(`owner-team-${action}-${membershipId}`, ownerTeamAction, "Working...", async function () {
        try {
          const updated = await apiFetch(`/api/workspace/members/${membershipId}/${action}`, {
            method: "POST",
            headers: authHeaders(),
          });
          state.bootstrap.admin_control_center = await apiFetch("/api/admin/control-center", { headers: authHeaders() });
          renderAdminSurface(state.bootstrap.admin_control_center);
          await focusAdminTarget("admin-onboarding-roster");
          queueTelemetryEvent({
            eventName: `workspace_member_${action}`,
            surface: "admin",
            route: "#admin",
            metadata: { membership_id: membershipId, status: updated.status },
          });
          flash(`${memberLabel} ${action === "reactivate" ? "reactivated" : "deactivated"}.`, "success");
        } catch (error) {
          queueTelemetryEvent({
            eventName: `workspace_member_${action}_failed`,
            surface: "admin",
            route: "#admin",
            outcome: "error",
            detail: error.message,
            metadata: { membership_id: membershipId },
          });
          flash(error.message, "error");
        }
      });
      return;
    }
    const refresh = event.target.closest("[data-refresh-surface]");
    if (refresh) {
      const refreshLabel = refresh.textContent.trim();
      await runPendingAction(`refresh-${refresh.dataset.refreshSurface}`, refresh, "Refreshing...", async function () {
        try {
          const key = refresh.dataset.refreshSurface;
          if (key === "manager" && hasBootstrapSection("manager")) {
            await renderManagerSurface();
          } else if (key === "operator" && hasBootstrapSection("operator")) {
            await renderOperatorSurface();
          } else if (key === "platform" && hasBootstrapSection("platform")) {
            state.bootstrap.platform_control_center = await apiFetch("/api/platform/control-center", { headers: authHeaders() });
            state.platformData = state.bootstrap.platform_control_center;
            renderPlatformSurface(state.bootstrap.platform_control_center);
          } else if (key === "admin" && hasBootstrapSection("admin")) {
            state.bootstrap.admin_control_center = await apiFetch("/api/admin/control-center", { headers: authHeaders() });
            renderAdminSurface(state.bootstrap.admin_control_center);
            await renderAdminReporting();
          } else if (key === "support") {
            await renderSupportSurface();
          } else if (key === "governance" && hasBootstrapSection("governance")) {
            await renderGovernanceSurface();
          }
          flash(`${refreshLabel} complete.`, "success");
        } catch (error) {
          flash(error.message, "error");
        }
      });
      return;
    }
    const resetFilters = event.target.closest("[data-reset-inbox-filters]");
    if (resetFilters) {
      const surfaceKey = resetFilters.dataset.resetInboxFilters;
      updateInboxFilters(surfaceKey, defaultInboxFilters());
      await rerenderInboxSurface(surfaceKey);
      flash("Inbox filters reset.", "success");
      return;
    }
    const saveView = event.target.closest("[data-save-inbox-view]");
    if (saveView) {
      const surfaceKey = saveView.dataset.saveInboxView;
      const form = document.querySelector(`[data-inbox-filter-form="${surfaceKey}"]`);
      const name = form ? new FormData(form).get("preset_name") : "";
      state.inboxSavedViews[surfaceKey] = saveInboxViewPreset(window.localStorage, surfaceKey, name, currentInboxFilters(surfaceKey));
      renderInboxFilters(surfaceKey, activeInboxWorkspace(surfaceKey));
      flash("Saved inbox view updated.", "success");
      return;
    }
    const applySaved = event.target.closest("[data-apply-saved-view]");
    if (applySaved) {
      const surfaceKey = applySaved.dataset.applySavedView;
      const form = document.querySelector(`[data-inbox-filter-form="${surfaceKey}"]`);
      const presetId = form ? new FormData(form).get("saved_view_id") : "";
      const preset = (state.inboxSavedViews[surfaceKey] || []).find((item) => item.id === presetId);
      if (!preset) {
        flash("Select a saved view first.", "error");
        return;
      }
      updateInboxFilters(surfaceKey, preset.filters);
      await rerenderInboxSurface(surfaceKey);
      flash(`Applied saved view: ${preset.name}.`, "success");
      return;
    }
    const deleteSaved = event.target.closest("[data-delete-saved-view]");
    if (deleteSaved) {
      const surfaceKey = deleteSaved.dataset.deleteSavedView;
      const form = document.querySelector(`[data-inbox-filter-form="${surfaceKey}"]`);
      const presetId = form ? new FormData(form).get("saved_view_id") : "";
      state.inboxSavedViews[surfaceKey] = deleteInboxViewPreset(window.localStorage, surfaceKey, presetId);
      renderInboxFilters(surfaceKey, activeInboxWorkspace(surfaceKey));
      flash("Saved inbox view deleted.", "success");
      return;
    }
    if (event.target.closest("[data-reset-admin-reporting]")) {
      state.adminReporting.filters = defaultAdminReportingFilters();
      persistAdminReportingFilters(window.localStorage, state.adminReporting.filters);
      try {
        await renderAdminReporting();
        flash("Operational report reset.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
      return;
    }
  });

  document.body.addEventListener("click", async function (event) {
    const openSurface = event.target.closest("[data-open-surface]");
    if (openSurface) {
      showSurface(openSurface.dataset.openSurface, { updateHash: true, persist: true });
      return;
    }
    const workspaceFocus = event.target.closest("[data-workspace-focus-target]");
    if (workspaceFocus) {
      if (applyAdminFocusMarker(workspaceFocus.dataset.workspaceFocusTarget)) {
        flash("Workspace focus moved.", "success");
      }
      return;
    }
    const copyAccessInvite = event.target.closest("[data-copy-access-invite]");
    if (copyAccessInvite) {
      try {
        await navigator.clipboard.writeText(copyAccessInvite.dataset.copyAccessInvite || "");
        flash("Invite link copied.", "success");
      } catch (error) {
        flash("Clipboard write failed. Copy the invite link manually.", "error");
      }
      return;
    }
    const accessRequestAction = event.target.closest("[data-access-request-action]");
    if (accessRequestAction) {
      const surfaceKey = accessRequestAction.dataset.accessRequestSurface || "admin";
      const role = ((state.context || {}).role || "").toLowerCase();
      if (surfaceKey === "platform") {
        if (!["platform_owner", "platform_ops"].includes(role)) {
          flash("Only platform reviewers can review public requests.", "error");
          return;
        }
        if (accessRequestAction.dataset.accessRequestAction === "approve" && role !== "platform_owner") {
          flash("Only the platform owner can approve and provision new accounts.", "error");
          return;
        }
      } else if (role !== "tenant_owner") {
        flash("Only tenant owners can review access requests.", "error");
        return;
      }
      const form = accessRequestAction.closest("[data-access-request-form]");
      await runAccessRequestAction(
        accessRequestAction.dataset.accessRequestAction,
        accessRequestAction.dataset.accessRequestId,
        form,
        accessRequestAction,
        surfaceKey
      );
      return;
    }
    const onboardingFocus = event.target.closest("[data-onboarding-focus-target]");
    if (onboardingFocus) {
      if (await focusAdminTarget(onboardingFocus.dataset.onboardingFocusTarget)) {
        flash("Onboarding focus moved.", "success");
      }
      return;
    }
    const onboardingPreset = event.target.closest("[data-onboarding-apply-default]");
    if (onboardingPreset) {
      if ((state.context || {}).role !== "tenant_owner") {
        flash("Only tenant owners can apply onboarding defaults.", "error");
        return;
      }
      const controlCenter = (state.bootstrap || {}).admin_control_center;
      const recommendation = (((controlCenter || {}).onboarding || {}).recommended_defaults || []).find(
        (item) => item.key === onboardingPreset.dataset.onboardingApplyDefault
      );
      const form = document.getElementById("admin-form");
      if (!recommendation || !form) {
        flash("Onboarding defaults were unavailable.", "error");
        return;
      }
      if (applyOnboardingRecommendation(form, recommendation, controlCenter)) {
        state.adminFormDirty = true;
        await focusAdminTarget("admin-form");
        flash(`${recommendation.title} defaults loaded. Review and save to persist.`, "success");
      }
      return;
    }
    const onboardingCopy = event.target.closest("[data-onboarding-copy-command]");
    if (onboardingCopy) {
      const commandIndex = Number(onboardingCopy.dataset.onboardingCopyCommand);
      const command = ((((state.bootstrap || {}).admin_control_center || {}).onboarding || {}).helpers || {}).commands?.[commandIndex]?.command;
      if (!command) {
        flash("Onboarding command unavailable.", "error");
        return;
      }
      try {
        await navigator.clipboard.writeText(command);
        flash("Onboarding helper command copied.", "success");
      } catch (error) {
        flash("Clipboard write failed. Copy the command manually from the helper card.", "error");
      }
      return;
    }
    const managerCommandView = event.target.closest("[data-manager-command-view]");
    if (managerCommandView) {
      await applyManagerCommand({ viewKey: managerCommandView.dataset.managerCommandView });
      flash(`Manager focus moved to ${managerCommandView.dataset.managerCommandView}.`, "success");
      return;
    }
    const managerCommandUser = event.target.closest("[data-manager-command-user]");
    if (managerCommandUser) {
      await applyManagerCommand({ viewKey: "open", assignedUserId: managerCommandUser.dataset.managerCommandUser });
      flash("Manager workload filter applied.", "success");
      return;
    }
    const managerCommandConversation = event.target.closest("[data-manager-command-conversation]");
    if (managerCommandConversation) {
      await applyManagerCommand({ conversationId: managerCommandConversation.dataset.managerCommandConversation });
      flash("Manager conversation focus loaded.", "success");
      return;
    }
    if (event.target.closest("[data-manager-clear-focus]")) {
      updateInboxFilters("manager", defaultInboxFilters());
      await renderManagerSurface();
      setActiveInboxView("manager", "open");
      flash("Manager focus reset.", "success");
      return;
    }
    const supportSelectDestination = event.target.closest("[data-support-select-destination]");
    if (supportSelectDestination) {
      state.supportWorkflow.selectedDestinationId = supportSelectDestination.dataset.supportSelectDestination;
      await renderSupportSurface();
      flash("Support destination focus updated.", "success");
      return;
    }
    const supportRunTest = event.target.closest("[data-support-run-test]");
    if (supportRunTest) {
      await runPendingAction(
        `support-test-${supportRunTest.dataset.supportRunTest}`,
        supportRunTest,
        "Testing...",
        async function () {
          try {
            const selectedKind = document.getElementById("support-delivery-kind")?.value;
            await runSupportDestinationTest(
              supportRunTest.dataset.supportRunTest,
              selectedKind || supportRunTest.dataset.supportDeliveryKind
            );
            flash("Notification destination test completed.", "success");
          } catch (error) {
            flash(error.message, "error");
          }
        }
      );
      return;
    }
    const supportRunRecovery = event.target.closest("[data-support-run-recovery]");
    if (supportRunRecovery) {
      await runPendingAction(
        `support-recovery-${supportRunRecovery.dataset.supportRunRecovery}`,
        supportRunRecovery,
        "Recovering...",
        async function () {
          try {
            const selectedKind = document.getElementById("support-delivery-kind")?.value;
            const reason =
              document.getElementById("support-recovery-reason")?.value || "Recovered from CRM support console.";
            await runSupportDestinationRecovery(
              supportRunRecovery.dataset.supportRunRecovery,
              selectedKind || supportRunRecovery.dataset.supportDeliveryKind,
              reason
            );
            flash("Notification destination recovery workflow completed.", "success");
          } catch (error) {
            flash(error.message, "error");
          }
        }
      );
      return;
    }
    const outcomeConversation = event.target.closest("[data-outcome-open-conversation]");
    if (outcomeConversation) {
      await openOutcomeConversation(
        outcomeConversation.dataset.outcomeOpenConversation,
        outcomeConversation.dataset.outcomeSurface || preferredOutcomeSurface()
      );
      return;
    }
    if (event.target.closest("[data-refresh-sync-notice]")) {
      if (state.selectedConversationId && state.selectedConversationTargetId) {
        await loadConversationDetail(state.selectedConversationId, state.selectedConversationTargetId);
        flash("Latest record state loaded.", "success");
      }
      return;
    }
    const focusInbox = event.target.closest("[data-focus-inbox]");
    if (focusInbox) {
      const surfaceKey = focusInbox.dataset.focusInbox || "operator";
      showSurface(surfaceKey, { updateHash: true, persist: true });
      const summary = document.getElementById(`${surfaceKey}-inbox-summary`);
      if (summary) {
        summary.scrollIntoView({ block: "start", behavior: "smooth" });
      }
      return;
    }
    const detail = event.target.closest("[data-detail-conversation]");
    if (detail) {
      await loadConversationDetail(detail.dataset.detailConversation, detail.dataset.detailTarget || inboxDetailTarget("operator"));
      showSurface(detail.dataset.detailTarget?.startsWith("manager") ? "manager" : "operator", { updateHash: false, persist: false });
      return;
    }
    const refreshDetail = event.target.closest("[data-refresh-detail]");
    if (refreshDetail) {
      if (state.selectedConversationId && state.selectedConversationTargetId) {
        await loadConversationDetail(state.selectedConversationId, state.selectedConversationTargetId);
        flash("Conversation detail refreshed.", "success");
      }
      return;
    }
    const detailClaim = event.target.closest("[data-detail-claim]");
    if (detailClaim) {
      try {
        await mutateConversation(`/api/conversations/${detailClaim.dataset.detailClaim}/claim`, "ui-detail-claim", detailClaim);
        flash("Conversation claimed.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
      return;
    }
    const detailUnassign = event.target.closest("[data-detail-unassign]");
    if (detailUnassign) {
      try {
        await mutateConversation(`/api/conversations/${detailUnassign.dataset.detailUnassign}/unassign`, "ui-detail-unassign", detailUnassign);
        flash("Conversation unassigned.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
      return;
    }
    const completeTask = event.target.closest("[data-complete-task]");
    if (completeTask) {
      const taskId = completeTask.dataset.completeTask;
      await runPendingAction(`task-complete-${taskId}`, completeTask, "Completing...", async function () {
        try {
          const completedTask = await apiFetch(`/api/tasks/${taskId}/complete`, {
            method: "POST",
            headers: Object.assign({ "Content-Type": "application/json" }, authHeaders()),
            body: JSON.stringify({ resolution_notes: "Completed from CRM conversation workspace." }),
          });
          queueTelemetryEvent({
            eventName: "follow_up_task_completed",
            surface: state.activeSurface || "operator",
            route: buildSurfaceHash(state.activeSurface || "operator"),
            metadata: { task_id: taskId, conversation_id: state.selectedConversationId },
          });
          if (state.selectedConversationId) {
            rememberConversationMutation(state.selectedConversationId, {
              kind: "task",
              summary: "Follow-up task completed.",
              taskIdsCompleted: [taskId],
              taskIdCreated: null,
              at: new Date().toISOString(),
              tone: "ready",
            });
            rememberConversationRecordData(state.selectedConversationId, {
              tasks: mergeWorkspaceCollection(
                [],
                [completedTask].concat(
                  (((rememberedConversationRecordData(state.selectedConversationId) || {}).tasks || []).filter((item) => item.id !== taskId))
                ),
                "due_at"
              ),
            });
            applyFollowUpWorkspace(
              state.selectedConversationTargetId,
              ((rememberedConversationRecordData(state.selectedConversationId) || {}).tasks || [])
            );
          }
          await loadBootstrap();
          if (state.selectedConversationId && state.selectedConversationTargetId) {
            finalizeConversationWorkspaceAction(state.selectedConversationTargetId, state.selectedConversationId, {
              label: "Latest action",
              value: "Follow-up task completed.",
              meta: taskImpact({ taskIdsCompleted: [taskId], taskIdCreated: null }),
              tone: "ready",
            });
          }
          flash("Follow-up task completed.", "success");
        } catch (error) {
          queueTelemetryEvent({
            eventName: "follow_up_task_complete_failed",
            surface: state.activeSurface || "operator",
            route: buildSurfaceHash(state.activeSurface || "operator"),
            outcome: "error",
            detail: error.message,
            metadata: { task_id: taskId, conversation_id: state.selectedConversationId },
          });
          flash(error.message, "error");
        }
      });
      return;
    }
    const claim = event.target.closest("[data-claim-conversation]");
    if (claim) {
      try {
        await mutateConversation(`/api/conversations/${claim.dataset.claimConversation}/claim`, "ui-claim", claim);
        flash("Conversation claimed.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
      return;
    }
    const unassign = event.target.closest("[data-unassign-conversation]");
    if (unassign) {
      try {
        await mutateConversation(`/api/conversations/${unassign.dataset.unassignConversation}/unassign`, "ui-unassign", unassign);
        flash("Conversation unassigned.", "success");
      } catch (error) {
        flash(error.message, "error");
      }
    }
  });

  document.body.addEventListener("click", function (event) {
    const inboxToggle = event.target.closest("[data-inbox-surface][data-inbox-view]");
    if (!inboxToggle) {
      return;
    }
    setActiveInboxView(inboxToggle.dataset.inboxSurface, inboxToggle.dataset.inboxView);
  });

  document.body.addEventListener("change", function (event) {
    if (event.target.closest("#admin-form")) {
      state.adminFormDirty = true;
    }
    if (event.target.matches(".manager-conversation-check")) {
      setManagerSelection(Array.from(document.querySelectorAll(".manager-conversation-check:checked")).map((item) => item.value));
      renderManagerBulkSummary();
      return;
    }
    if (event.target.id === "support-destination-selector") {
      state.supportWorkflow.selectedDestinationId = event.target.value || null;
      renderSupportSurface().catch((error) => flash(error.message, "error"));
    }
  });

  document.body.addEventListener("input", function (event) {
    if (event.target.closest("#admin-form")) {
      state.adminFormDirty = true;
    }
  });

  async function resumeSession(options) {
    const settings = Object.assign({ forceRefresh: false }, options || {});
    if (!state.token || !state.context) {
      state.authChecked = true;
      renderSession();
      renderNav();
      return;
    }
    if (!settings.forceRefresh && isSessionExpired({ accessToken: state.token, context: state.context, expiresAt: state.expiresAt }, new Date())) {
      clearSession();
      state.authChecked = true;
      renderSession();
      renderNav();
      flash("Saved CRM session expired. Sign in again.", "error");
      return;
    }
    try {
      const me = await apiFetch("/api/me", { headers: authHeaders() });
      state.context = Object.assign({}, state.context || {}, me || {});
      state.authChecked = true;
      saveSession();
      renderSession();
      await loadBootstrap();
    } catch (error) {
      if (String(error.message || "").includes("returned 401")) {
        clearSession();
        state.authChecked = true;
        renderSession();
        renderNav();
        flash("Saved CRM session is no longer valid. Sign in again.", "error");
        return;
      }
      throw error;
    }
  }

  window.addEventListener("hashchange", function () {
    if (!state.bootstrap) {
      return;
    }
    const preferredSurface = resolvePreferredSurface({
      hash: window.location.hash,
      storedSurface: loadStoredSurface(window.localStorage),
      allowedSections: state.bootstrap.sections,
    });
    if (preferredSurface && preferredSurface !== state.activeSurface) {
      showSurface(preferredSurface, { updateHash: false, persist: true });
    }
  });

  if (skipLink && workspaceMain) {
    skipLink.addEventListener("click", function () {
      window.requestAnimationFrame(() => {
        workspaceMain.focus({ preventScroll: true });
      });
    });
  }

  document.addEventListener("visibilitychange", function () {
    renderSyncStatus();
    if (!state.token || !state.context) {
      return;
    }
    if (!document.hidden) {
      loadBootstrap({ syncDetail: false, telemetryEventName: "bootstrap_visibility_refresh" }).catch(() => {});
    } else {
      scheduleBackgroundRefresh();
    }
  });

  loginForm.addEventListener("submit", login);
  renderSession();
  renderNav();
  renderSyncStatus();
  resumeSession().catch((error) => flash(error.message, "error"));
})();
